/**
 * Learn 教材コンテンツ
 *
 * アンセル・アダムスのゾーンシステムを撮影現場〜暗室まで体系的に学ぶための
 * 8章構成の教材。本文は HTML 文字列で記述しており、リスト・強調・テーブル等を
 * 構造化表示できる。すべてのコンテンツはこのファイル内にハードコードされており
 * オフラインで動作する。
 */

export const ARTICLES = [
  // ============================================================
  // 01. ゾーンシステムとは
  // ============================================================
  {
    id: 'overview',
    chapter: '01',
    title: 'ゾーンシステムとは',
    summary: 'アンセル・アダムスとフレッド・アーチャーが体系化した、撮影〜現像〜プリントを一貫した予測可能なプロセスとして統合する手法。',
    sections: [
      {
        heading: '体系の起源',
        body: `
<p>1939〜40 年、アンセル・アダムスとフレッド・アーチャーは、ロサンゼルスの <em>Art Center School</em> でモノクロ写真の教鞭を執る中で、それまで職人の経験則に依存していた露出・現像・プリントの判断を、誰でも再現可能なシステムへと体系化した。これがゾーンシステム (Zone System) である。</p>
<p>アダムスはこれを後年「3部作」として書籍化した:</p>
<ul>
  <li><strong>The Camera</strong> (1980) — 撮影機材と運用</li>
  <li><strong>The Negative</strong> (1981) — ゾーンシステムの本体</li>
  <li><strong>The Print</strong> (1983) — プリント技法</li>
</ul>
<p>この三部作は今でもモノクロ写真の標準教科書として読み継がれている。</p>
`,
      },
      {
        heading: '中核思想 — Visualization',
        body: `
<p>ゾーンシステムの中核にあるのは <strong>Visualization (プレビジュアライゼーション)</strong> という考え方である。シャッターを切る前に、最終プリントの全ての調子を頭の中で完成させ、撮影・現像・プリントの全工程をその完成像から逆算的に設計する。</p>
<p>つまり「写真を撮る」のではなく、「自分が選んだ調子を、ネガとプリントに彫り込む」という能動的な行為に転換する。</p>
`,
      },
      {
        heading: 'もっとも重要な原則',
        body: `
<p class="article-quote">"Expose for the shadows, develop for the highlights."<br/>— Ansel Adams</p>
<p>シャドウは <strong>露出</strong> で決まり、ハイライトは <strong>現像</strong> で決まる。これがゾーンシステムを貫く操作原則である。</p>
<ul>
  <li>露出を多めに与える → シャドウに調子が乗る (低密度部に光が届く)</li>
  <li>現像時間を伸ばす → ハイライト密度が上がる (高密度部のコントラストが増す)</li>
  <li>シャドウは時間に対して鈍感、ハイライトは敏感に反応する</li>
</ul>
<p>この非対称性こそがゾーンシステムが成立する物理的根拠である。</p>
`,
      },
      {
        heading: '4 つの工程の循環',
        body: `
<p>ゾーンシステムは以下のサイクルで完結する:</p>
<svg class="article-illust" viewBox="0 0 460 240" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
  <g font-family="Inter Tight, sans-serif" font-size="11" letter-spacing="0.08em" text-anchor="middle">
    <rect x="20" y="60" width="90" height="56" />
    <text x="65" y="84" stroke="none" fill="currentColor">01</text>
    <text x="65" y="102" stroke="none" fill="currentColor">VISUALIZE</text>
    <rect x="130" y="60" width="90" height="56" />
    <text x="175" y="84" stroke="none" fill="currentColor">02</text>
    <text x="175" y="102" stroke="none" fill="currentColor">EXPOSE</text>
    <rect x="240" y="60" width="90" height="56" />
    <text x="285" y="84" stroke="none" fill="currentColor">03</text>
    <text x="285" y="102" stroke="none" fill="currentColor">DEVELOP</text>
    <rect x="350" y="60" width="90" height="56" />
    <text x="395" y="84" stroke="none" fill="currentColor">04</text>
    <text x="395" y="102" stroke="none" fill="currentColor">PRINT</text>
    <path d="M110 88 L130 88 M220 88 L240 88 M330 88 L350 88" />
    <path d="M122 84 l8 4 -8 4 M232 84 l8 4 -8 4 M342 84 l8 4 -8 4" />
    <path d="M395 116 Q395 180 230 180 Q65 180 65 116" stroke-dasharray="3 4" />
    <text x="230" y="200" stroke="none" fill="currentColor" font-size="9">前工程の判断が次工程を縛り、結果が次の視覚化を更新する</text>
  </g>
</svg>
<ol>
  <li><strong>視覚化</strong> — 完成プリントを頭の中で構築</li>
  <li><strong>露出</strong> — シャドウを目標ゾーンに「配置」する</li>
  <li><strong>現像</strong> — ハイライトを目標ゾーンに到達させる時間を選ぶ</li>
  <li><strong>プリント</strong> — ネガを「楽譜」として、表現を「演奏」する</li>
</ol>
<p>このいずれの工程でも判断と操作が連動しており、一つを変えれば他にも影響が及ぶ。だからこそ、現場の判断を後で参照できる記録が重要になる。</p>
`,
      },
    ],
  },

  // ============================================================
  // 02. 11ゾーンの理解
  // ============================================================
  {
    id: 'zones-detail',
    chapter: '02',
    title: '11 ゾーンの理解',
    summary: 'ゾーン 0 から X までの 11 段階。各ゾーンは 1 絞り (1 stop) の差を持つ、最終プリント上の調子の物差し。',
    showZoneChart: true,  // 専用ゾーンチャート UI を埋め込む
    sections: [
      {
        heading: 'ゾーンの定義',
        body: `
<svg class="article-illust" viewBox="0 0 460 180" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">18% GRAY CARD REFERENCE</text>
    <g>
      <rect x="10" y="30" width="90" height="90" fill="#808080" stroke="#0a0a0a"/>
      <text x="55" y="64" text-anchor="middle" font-size="11" font-weight="500" fill="#ffffff">18%</text>
      <text x="55" y="78" text-anchor="middle" font-size="9" fill="#cfcfcf">reflectance</text>
      <text x="55" y="95" text-anchor="middle" font-size="12" font-weight="500" fill="#ffffff">Zone V</text>
      <text x="55" y="108" text-anchor="middle" font-size="9" fill="#cfcfcf">middle gray</text>
      <text x="55" y="138" text-anchor="middle" font-size="9" fill="#5a5a58">Kodak Gray Card</text>
    </g>
    <g transform="translate(120,30)">
      <text x="0" y="14" font-size="10" font-weight="500">露出計の基準</text>
      <text x="0" y="32" font-size="10" fill="#5a5a58">あらゆる被写体は</text>
      <text x="0" y="46" font-size="10" fill="#5a5a58">「ゾーン V (18%) として写すための」</text>
      <text x="0" y="60" font-size="10" fill="#5a5a58">露出を露出計が指示する</text>
      <text x="0" y="86" font-size="10" font-weight="500">配置 (Placement)</text>
      <text x="0" y="104" font-size="10" fill="#5a5a58">→ シャドウを III に置く = −2 段補正</text>
      <text x="0" y="118" font-size="10" fill="#5a5a58">→ ハイライトを VIII に置く = +3 段補正</text>
    </g>
    <text x="10" y="170" font-size="11" fill="#5a5a58">グレーカードは反射率 18% の物理的基準。撮影の出発点となる。</text>
  </g>
</svg>
<p>ゾーンとは、<strong>プリント紙上に再現される明度の段階</strong>である。シーンの輝度ではなく、最終プリントの調子に対する尺度であることに注意。</p>
<ul>
  <li><strong>各ゾーンは 1 絞り (1 stop) の差</strong> = 光量で 2 倍 / 1/2</li>
  <li>ゾーン V (中央) = <strong>18% 反射率の中間グレー</strong> (Kodak グレーカード基準)</li>
  <li>露出計はあらゆる被写体を「ゾーン V として写すための値」を返す</li>
</ul>
`,
      },
      {
        heading: '3 つの重要ゾーン',
        body: `
<p>11 段階あるが、操作上特に重要なのは以下の 3 つ:</p>
<table class="article-table">
  <tr><th>ゾーン</th><th>役割</th><th>例</th></tr>
  <tr><td>III</td><td>主要シャドウ (ディテールある暗部)</td><td>影の中の岩肌、暗い肌の質感、暗い樹皮</td></tr>
  <tr><td>V</td><td>中間グレー (露出計基準)</td><td>北の青空、平均的な肌 (日陰側)、コンクリート</td></tr>
  <tr><td>VIII</td><td>主要ハイライト (ディテールある明部)</td><td>雪の表面、白い陶器、明るい衣服の折り目</td></tr>
</table>
`,
      },
      {
        heading: '配置 (Placement) と 落ち位置 (Fall on)',
        body: `
<p>ゾーンシステムの核心となる 2 つの動詞を区別する:</p>
<svg class="article-illust" viewBox="0 0 460 200" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" letter-spacing="0.08em">
    <text x="20" y="20" font-size="9" letter-spacing="0.18em" fill="#5a5a58">PLACEMENT (意図)</text>
    <text x="240" y="20" font-size="9" letter-spacing="0.18em" fill="#5a5a58">FALL ON (結果)</text>
    <g>
      <rect x="20"  y="40" width="32" height="40" fill="#000000"/>
      <rect x="52"  y="40" width="32" height="40" fill="#1a1a1a"/>
      <rect x="84"  y="40" width="32" height="40" fill="#2d2d2d"/>
      <rect x="116" y="40" width="32" height="40" fill="#444444"/>
      <rect x="148" y="40" width="32" height="40" fill="#5e5e5e"/>
      <rect x="180" y="40" width="32" height="40" fill="#808080"/>
      <rect x="20"  y="40" width="192" height="40" fill="none" stroke="#0a0a0a"/>
      <rect x="84" y="36" width="32" height="48" fill="none" stroke="#0a0a0a" stroke-width="2.5"/>
      <text x="100" y="100" text-anchor="middle" fill="#0a0a0a" font-size="11">III</text>
      <text x="100" y="115" text-anchor="middle" fill="#5a5a58" font-size="9">主要シャドウ</text>
      <text x="100" y="127" text-anchor="middle" fill="#5a5a58" font-size="9">を「置く」</text>
    </g>
    <path d="M220 60 L260 60" stroke="#0a0a0a" fill="none"/>
    <path d="M252 56 l8 4 -8 4" stroke="#0a0a0a" fill="none"/>
    <text x="240" y="50" text-anchor="middle" fill="#5a5a58" font-size="9">露出決定</text>
    <g>
      <rect x="240" y="40" width="22" height="40" fill="#000000"/>
      <rect x="262" y="40" width="22" height="40" fill="#1a1a1a"/>
      <rect x="284" y="40" width="22" height="40" fill="#2d2d2d"/>
      <rect x="306" y="40" width="22" height="40" fill="#444444"/>
      <rect x="328" y="40" width="22" height="40" fill="#5e5e5e"/>
      <rect x="350" y="40" width="22" height="40" fill="#808080"/>
      <rect x="372" y="40" width="22" height="40" fill="#9a9a9a"/>
      <rect x="394" y="40" width="22" height="40" fill="#b5b5b5"/>
      <rect x="416" y="40" width="22" height="40" fill="#cfcfcf"/>
      <rect x="240" y="40" width="198" height="40" fill="none" stroke="#0a0a0a"/>
      <rect x="416" y="36" width="22" height="48" fill="none" stroke="#0a0a0a" stroke-width="2.5"/>
      <text x="427" y="100" text-anchor="middle" fill="#0a0a0a" font-size="11">VIII</text>
      <text x="427" y="115" text-anchor="middle" fill="#5a5a58" font-size="9">ハイライトが</text>
      <text x="427" y="127" text-anchor="middle" fill="#5a5a58" font-size="9">「落ちる」</text>
    </g>
    <text x="20" y="160" font-size="11" fill="#0a0a0a"><tspan font-weight="500">配置</tspan> = あなたが選ぶ露出操作</text>
    <text x="240" y="160" font-size="11" fill="#0a0a0a"><tspan font-weight="500">落ち位置</tspan> = 結果として現像方針を示す</text>
    <text x="20" y="180" font-size="9" fill="#5a5a58">現像方針 = 「VIII に落ちなければ N± で調整」</text>
  </g>
</svg>
<ul>
  <li><strong>配置 (Place on)</strong> — シーン内のある部分を、自分の意思で特定のゾーンに置く判断。通常は主要シャドウをゾーン III に「配置」する。</li>
  <li><strong>落ち位置 (Fall on)</strong> — 配置を決めた結果、他の部分が <em>どこに落ちるか</em> を観察する。主要ハイライトの落ち位置で現像方針が決まる。</li>
</ul>
<p>「配置 = 露出操作」「落ち位置 = 現像操作の指標」と理解すると整理しやすい。</p>
`,
      },
    ],
  },

  // ============================================================
  // 03. 被写体の観察と視覚化
  // ============================================================
  {
    id: 'visualization',
    chapter: '03',
    title: '被写体の観察と視覚化',
    summary: 'シーンを 11 段階のゾーンに分解して読み取る。スポットメーターを使い、配置と落ち位置を見極める。',
    sections: [
      {
        heading: '主要シャドウを決める',
        body: `
<p>シーンの中で <strong>「最終プリントで質感を残したい一番暗い部分」</strong> を特定する。これは多くの場合ゾーン III に配置される。</p>
<p>配置の選択肢:</p>
<ul>
  <li><strong>ゾーン II</strong> — 質感ぎりぎりの限界、深い暗部</li>
  <li><strong>ゾーン III</strong> — 標準的な「ディテールあるシャドウ」</li>
  <li><strong>ゾーン IV</strong> — やや明るめのシャドウ、人物の暗部肌など</li>
</ul>
<p>初心者はまず <strong>ゾーン III 配置</strong> を標準にする。</p>
`,
      },
      {
        heading: '主要ハイライトを観察する',
        body: `
<p>続いて「ディテールを残したい一番明るい部分」を特定し、<strong>同じ撮影設定でどのゾーンに落ちるか</strong> を予測する。</p>
<table class="article-table">
  <tr><th>ハイライトの落ち位置</th><th>必要な現像</th></tr>
  <tr><td>ゾーン VIII</td><td>N (ノーマル)</td></tr>
  <tr><td>ゾーン IX</td><td>N − 1 (1 段圧縮)</td></tr>
  <tr><td>ゾーン X</td><td>N − 2 (2 段圧縮)</td></tr>
  <tr><td>ゾーン VII</td><td>N + 1 (1 段拡張)</td></tr>
  <tr><td>ゾーン VI</td><td>N + 2 (2 段拡張)</td></tr>
</table>
`,
      },
      {
        heading: 'スポットメーターの使い方',
        body: `
<figure class="article-figure">
  <img src="/learn-images/03-spot-metering.png" alt="スポット測光中の写真家" loading="lazy" />
  <figcaption>三脚に据えた中判カメラと、別個に手持ちのスポットメーター</figcaption>
</figure>
<p>1° スポット式の反射光式露出計 (Pentax Spotmeter V 等) で、各部位を個別に測光する。</p>
<ol>
  <li>主要シャドウを測光 → 仮 EV 値を得る</li>
  <li>主要ハイライトを測光 → 仮 EV 値を得る</li>
  <li>差分 (絞り段数) を計算 → ダイナミックレンジ把握</li>
  <li>必要なら中間部、エッジ部分も測光して全体の分布を確認</li>
</ol>
<p><strong>測光は「ゾーン V に置こうとする値」</strong> を返すため、シャドウやハイライトをそのまま採用しないこと。配置によって露出を「ずらす」のが操作。</p>
`,
      },
      {
        heading: 'シーンの 3 つの典型',
        body: `
<svg class="article-illust" viewBox="0 0 460 220" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">LOW CONTRAST</text>
    <g transform="translate(10,22)">
      <rect x="0"   y="0" width="13" height="34" fill="#000000"/>
      <rect x="13"  y="0" width="13" height="34" fill="#1a1a1a"/>
      <rect x="26"  y="0" width="13" height="34" fill="#2d2d2d"/>
      <rect x="39"  y="0" width="13" height="34" fill="#444444"/>
      <rect x="52"  y="0" width="13" height="34" fill="#5e5e5e"/>
      <rect x="65"  y="0" width="13" height="34" fill="#808080"/>
      <rect x="78"  y="0" width="13" height="34" fill="#9a9a9a"/>
      <rect x="91"  y="0" width="13" height="34" fill="#b5b5b5"/>
      <rect x="104" y="0" width="13" height="34" fill="#cfcfcf"/>
      <rect x="117" y="0" width="13" height="34" fill="#e6e6e6"/>
      <rect x="130" y="0" width="13" height="34" fill="#ffffff" stroke="#0a0a0a"/>
      <rect x="0" y="0" width="143" height="34" fill="none" stroke="#0a0a0a"/>
      <rect x="52" y="-4" width="52" height="42" fill="none" stroke="#0a0a0a" stroke-width="2"/>
    </g>
    <text x="10" y="74" font-size="9" fill="#5a5a58">DR ≈ 3-4 stops · 霧、曇天</text>
    <text x="10" y="86" font-size="11" fill="#0a0a0a"><tspan font-weight="500">→ N+1 〜 N+2 拡張</tspan></text>

    <text x="160" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">STANDARD</text>
    <g transform="translate(160,22)">
      <rect x="0"   y="0" width="13" height="34" fill="#000000"/>
      <rect x="13"  y="0" width="13" height="34" fill="#1a1a1a"/>
      <rect x="26"  y="0" width="13" height="34" fill="#2d2d2d"/>
      <rect x="39"  y="0" width="13" height="34" fill="#444444"/>
      <rect x="52"  y="0" width="13" height="34" fill="#5e5e5e"/>
      <rect x="65"  y="0" width="13" height="34" fill="#808080"/>
      <rect x="78"  y="0" width="13" height="34" fill="#9a9a9a"/>
      <rect x="91"  y="0" width="13" height="34" fill="#b5b5b5"/>
      <rect x="104" y="0" width="13" height="34" fill="#cfcfcf"/>
      <rect x="117" y="0" width="13" height="34" fill="#e6e6e6"/>
      <rect x="130" y="0" width="13" height="34" fill="#ffffff" stroke="#0a0a0a"/>
      <rect x="0" y="0" width="143" height="34" fill="none" stroke="#0a0a0a"/>
      <rect x="26" y="-4" width="91" height="42" fill="none" stroke="#0a0a0a" stroke-width="2"/>
    </g>
    <text x="160" y="74" font-size="9" fill="#5a5a58">DR ≈ 5-6 stops · 晴天順光</text>
    <text x="160" y="86" font-size="11" fill="#0a0a0a"><tspan font-weight="500">→ N (ノーマル)</tspan></text>

    <text x="310" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">HIGH CONTRAST</text>
    <g transform="translate(310,22)">
      <rect x="0"   y="0" width="13" height="34" fill="#000000"/>
      <rect x="13"  y="0" width="13" height="34" fill="#1a1a1a"/>
      <rect x="26"  y="0" width="13" height="34" fill="#2d2d2d"/>
      <rect x="39"  y="0" width="13" height="34" fill="#444444"/>
      <rect x="52"  y="0" width="13" height="34" fill="#5e5e5e"/>
      <rect x="65"  y="0" width="13" height="34" fill="#808080"/>
      <rect x="78"  y="0" width="13" height="34" fill="#9a9a9a"/>
      <rect x="91"  y="0" width="13" height="34" fill="#b5b5b5"/>
      <rect x="104" y="0" width="13" height="34" fill="#cfcfcf"/>
      <rect x="117" y="0" width="13" height="34" fill="#e6e6e6"/>
      <rect x="130" y="0" width="13" height="34" fill="#ffffff" stroke="#0a0a0a"/>
      <rect x="0" y="0" width="143" height="34" fill="none" stroke="#0a0a0a"/>
      <rect x="13" y="-4" width="130" height="42" fill="none" stroke="#0a0a0a" stroke-width="2"/>
    </g>
    <text x="310" y="74" font-size="9" fill="#5a5a58">DR ≈ 7-9 stops · 雪+影</text>
    <text x="310" y="86" font-size="11" fill="#0a0a0a"><tspan font-weight="500">→ N−1 〜 N−2 圧縮</tspan></text>

    <text x="10" y="118" font-size="11" fill="#0a0a0a" font-weight="500">対処の基本原則</text>
    <text x="10" y="138" font-size="11" fill="#5a5a58">DR &lt; 5 段 → 拡張現像で「持ち上げ」、DR &gt; 7 段 → 圧縮現像で「抑える」。</text>
    <text x="10" y="156" font-size="11" fill="#5a5a58">9 段超は撮影前に「シャドウかハイライトのどちらを犠牲にするか」決断が必要。</text>
    <text x="10" y="184" font-size="9" fill="#5a5a58">※ 太枠の範囲がシーンのダイナミックレンジ (主要シャドウ ↔ 主要ハイライト)</text>
  </g>
</svg>
<table class="article-table">
  <tr><th>シーン</th><th>DR (絞り)</th><th>典型的方針</th></tr>
  <tr><td>低コントラスト (霧、曇天)</td><td>3-4 段</td><td>N+1 〜 N+2 拡張</td></tr>
  <tr><td>標準 (晴天順光)</td><td>5-6 段</td><td>N (ノーマル)</td></tr>
  <tr><td>高コントラスト (晴天日陰混在、雪+影)</td><td>7-9 段</td><td>N−1 〜 N−2 圧縮</td></tr>
</table>
<p>ダイナミックレンジが 7 段を超える場合は、シャドウかハイライトのどちらかを犠牲にせざるを得ないこともある。判断の基準を「自分が何を見せたいか」に置く。</p>
`,
      },
    ],
  },

  // ============================================================
  // 04. 露出設定
  // ============================================================
  {
    id: 'exposure',
    chapter: '04',
    title: '露出設定',
    summary: 'シャドウの配置から撮影 EV を計算し、絞り・シャッター・フィルター補正を統合する具体手順。',
    sections: [
      {
        heading: '露出決定の 5 ステップ',
        body: `
<svg class="article-illust" viewBox="0 0 460 280" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="11" fill="#0a0a0a">
    <g>
      <circle cx="30" cy="30" r="14" fill="#0a0a0a"/>
      <text x="30" y="34" text-anchor="middle" fill="#ffffff" font-size="11">1</text>
      <text x="55" y="28" font-weight="500">主要シャドウを測光</text>
      <text x="55" y="44" font-size="10" fill="#5a5a58">スポットメーターで EV<tspan font-size="8">shadow</tspan> を得る</text>
    </g>
    <path d="M30 50 L30 70" stroke="#0a0a0a" stroke-width="1.4"/>

    <g transform="translate(0,76)">
      <circle cx="30" cy="14" r="14" fill="#0a0a0a"/>
      <text x="30" y="18" text-anchor="middle" fill="#ffffff" font-size="11">2</text>
      <text x="55" y="12" font-weight="500">配置ゾーンを決定 (通常 III)</text>
      <text x="55" y="28" font-size="10" fill="#5a5a58">EV<tspan font-size="8">shoot</tspan> = EV<tspan font-size="8">shadow</tspan> + (5 − placedZone)</text>
    </g>
    <path d="M30 110 L30 130" stroke="#0a0a0a" stroke-width="1.4"/>

    <g transform="translate(0,136)">
      <circle cx="30" cy="14" r="14" fill="#0a0a0a"/>
      <text x="30" y="18" text-anchor="middle" fill="#ffffff" font-size="11">3</text>
      <text x="55" y="12" font-weight="500">主要ハイライトを測光</text>
      <text x="55" y="28" font-size="10" fill="#5a5a58">fallenZone = 5 + (EV<tspan font-size="8">hi</tspan> − EV<tspan font-size="8">shoot</tspan>)</text>
    </g>
    <path d="M30 170 L30 190" stroke="#0a0a0a" stroke-width="1.4"/>

    <g transform="translate(0,196)">
      <circle cx="30" cy="14" r="14" fill="#0a0a0a"/>
      <text x="30" y="18" text-anchor="middle" fill="#ffffff" font-size="11">4</text>
      <text x="55" y="12" font-weight="500">N± 現像方針を決定</text>
      <text x="55" y="28" font-size="10" fill="#5a5a58">fallenZone − VIII の差で N+1, N, N−1 ...</text>
    </g>
    <path d="M30 230 L30 250" stroke="#0a0a0a" stroke-width="1.4"/>

    <g transform="translate(0,256)">
      <circle cx="30" cy="14" r="14" fill="#0a0a0a"/>
      <text x="30" y="18" text-anchor="middle" fill="#ffffff" font-size="11">5</text>
      <text x="55" y="12" font-weight="500">EI/フィルター補正 → 最終絞り・SS</text>
    </g>
  </g>
</svg>
<ol>
  <li>主要シャドウをスポット測光 → <code>EV<sub>shadow</sub></code> を得る</li>
  <li>配置ゾーン (通常 III) を決める → <code>EV<sub>shoot</sub> = EV<sub>shadow</sub> + (5 − placedZone)</code></li>
  <li>主要ハイライトを測光 → 落ち位置 <code>fallenZone = 5 + (EV<sub>highlight</sub> − EV<sub>shoot</sub>)</code></li>
  <li>fallenZone と目標 (通常 VIII) の差で N±現像を決定</li>
  <li>EI 補正・フィルター補正を適用して最終的な絞り・SS を決める</li>
</ol>
<p>例: シャドウ EV 9 をゾーン III に配置 → EV<sub>shoot</sub> = 9 + (5−3) = <strong>11</strong>。ハイライト EV 14 → fallen = 5 + (14−11) = ゾーン VIII → <strong>N 現像</strong>。</p>
`,
      },
      {
        heading: '実効感度 (EI) の意味',
        body: `
<p>フィルムの箱書き ISO は、メーカー基準 (ANSI 標準) で測定された値である。実際の現場ではこれが <strong>そのまま正解になることは少ない</strong>。</p>
<p>シャドウに十分な質感を残すには、多くの場合 <strong>箱書きより 1/3〜1 段下の EI</strong> を採用する:</p>
<ul>
  <li>Tri-X 400 → 実用 EI 200〜320</li>
  <li>HP5 Plus 400 → 実用 EI 250〜400</li>
  <li>T-Max 400 → 箱書き付近</li>
  <li>Acros 100 II → 箱書き付近</li>
</ul>
<p>パーソナル EI は <strong>ゾーン I テスト</strong> (後章) で測定する。Zone Notes は ANALYTICS で過去の露出評価を集計し、実測ベースの EI 提案を出す。</p>
`,
      },
      {
        heading: 'フィルター補正',
        body: `
<svg class="article-illust" viewBox="0 0 460 200" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">SKY ZONE CHANGE BY FILTER</text>
    <g transform="translate(10,28)">
      <rect x="0"  y="0" width="40" height="80" fill="#9a9a9a"/>
      <rect x="0"  y="0" width="40" height="80" fill="none" stroke="#0a0a0a"/>
      <text x="20" y="100" text-anchor="middle" font-size="10">なし</text>
      <text x="20" y="116" text-anchor="middle" font-size="11" font-weight="500">VI</text>
      <text x="20" y="130" text-anchor="middle" font-size="9" fill="#5a5a58">標準</text>
    </g>
    <g transform="translate(120,28)">
      <rect x="0"  y="0" width="40" height="80" fill="#5e5e5e"/>
      <rect x="0"  y="0" width="40" height="80" fill="none" stroke="#0a0a0a"/>
      <text x="20" y="100" text-anchor="middle" font-size="10">Y8</text>
      <text x="20" y="116" text-anchor="middle" font-size="11" font-weight="500">IV</text>
      <text x="20" y="130" text-anchor="middle" font-size="9" fill="#5a5a58">+0.7段</text>
    </g>
    <g transform="translate(230,28)">
      <rect x="0"  y="0" width="40" height="80" fill="#444444"/>
      <rect x="0"  y="0" width="40" height="80" fill="none" stroke="#0a0a0a"/>
      <text x="20" y="100" text-anchor="middle" font-size="10">O16</text>
      <text x="20" y="116" text-anchor="middle" font-size="11" font-weight="500">III</text>
      <text x="20" y="130" text-anchor="middle" font-size="9" fill="#5a5a58">+1.5段</text>
    </g>
    <g transform="translate(340,28)">
      <rect x="0"  y="0" width="40" height="80" fill="#1a1a1a"/>
      <rect x="0"  y="0" width="40" height="80" fill="none" stroke="#0a0a0a"/>
      <text x="20" y="100" text-anchor="middle" font-size="10">R25</text>
      <text x="20" y="116" text-anchor="middle" font-size="11" font-weight="500">I-II</text>
      <text x="20" y="130" text-anchor="middle" font-size="9" fill="#5a5a58">+3.0段</text>
    </g>
    <path d="M50 68 L120 68 M170 68 L230 68 M280 68 L340 68" stroke="#0a0a0a" stroke-dasharray="2 3"/>
    <text x="10" y="160" font-size="11" fill="#5a5a58">青い空がフィルターで段階的に暗くなる効果のシミュレーション</text>
    <text x="10" y="178" font-size="11" fill="#5a5a58">露出補正は撮影 EV から減算 (= シャッター速度を遅くする)</text>
  </g>
</svg>
<table class="article-table">
  <tr><th>フィルター</th><th>補正</th><th>典型効果</th></tr>
  <tr><td>Yellow #8 (Y2)</td><td>+0.7 段</td><td>空をやや暗く、肌を健康的に</td></tr>
  <tr><td>Orange #16 (O56)</td><td>+1.5 段</td><td>空を中程度に、コントラスト増</td></tr>
  <tr><td>Red #25 (R60)</td><td>+3.0 段</td><td>空を真っ黒に、葉も暗く</td></tr>
  <tr><td>Green #11</td><td>+1.5 段</td><td>葉を明るく、肌は暗く</td></tr>
  <tr><td>偏光 (PL)</td><td>+1.5 段</td><td>反射除去、空の暗化</td></tr>
</table>
<p>フィルターを使う場合は、<strong>測光時には外し、撮影時にだけ装着</strong>するのが基本 (内蔵の TTL 測光なら付けたままで可)。露出計算後に補正分を加算する。</p>
`,
      },
      {
        heading: '絞り・シャッター速度の組合せ',
        body: `
<p>同じ撮影 EV に対して、複数の絞り・シャッター組合せが存在する。<code>EV = log₂(N²/t)</code> の関係から、N²/t が一定であれば同じ露光量。</p>
<p>選択基準:</p>
<ul>
  <li>被写界深度 (絞り選択)</li>
  <li>被写体ブレ・手ブレ (シャッター速度選択)</li>
  <li>レンズの最良画質域 (多くは F8〜F11)</li>
  <li>長時間露光時の <strong>相反則不軌</strong> 補正 (1 秒以上で補正必要なフィルム多)</li>
</ul>
<p>中判 6×7 では深い被写界深度が得やすく、F11〜F22 + 1/15〜1 秒の組合せが頻用される。</p>
`,
      },
    ],
  },

  // ============================================================
  // 05. 撮影
  // ============================================================
  {
    id: 'camera-work',
    chapter: '05',
    title: '撮影',
    summary: '中判フィルムでの実践、フォーマット別の特性、フィルター効果の物理、構図と精度。',
    sections: [
      {
        heading: 'フォーマット別の特性',
        body: `
<div style="display:flex; gap:8px; margin-bottom:14px;">
  <figure class="article-figure" style="flex:1; margin:0;">
    <img src="/learn-images/05-pentax-67.png" alt="Pentax 67" loading="lazy" />
    <figcaption>Pentax 67 (6×7)</figcaption>
  </figure>
  <figure class="article-figure" style="flex:1; margin:0;">
    <img src="/learn-images/05-hasselblad-500cm.png" alt="Hasselblad 500CM" loading="lazy" />
    <figcaption>Hasselblad 500CM (6×6)</figcaption>
  </figure>
</div>
<svg class="article-illust" viewBox="0 0 460 220" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="9" fill="#0a0a0a" letter-spacing="0.06em">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">FILM SIZES (実寸 mm, scale 1.5x)</text>
    <g transform="translate(10,30)">
      <rect x="0" y="42" width="36" height="24" fill="#f4f4f2" stroke="#0a0a0a"/>
      <text x="18" y="80" text-anchor="middle">35mm</text>
      <text x="18" y="92" text-anchor="middle" font-size="8" fill="#5a5a58">36×24</text>
    </g>
    <g transform="translate(60,30)">
      <rect x="0" y="22" width="56" height="44" fill="#ededea" stroke="#0a0a0a"/>
      <text x="28" y="80" text-anchor="middle">645</text>
      <text x="28" y="92" text-anchor="middle" font-size="8" fill="#5a5a58">56×42</text>
    </g>
    <g transform="translate(135,30)">
      <rect x="0" y="10" width="56" height="56" fill="#ededea" stroke="#0a0a0a"/>
      <text x="28" y="80" text-anchor="middle">6×6</text>
      <text x="28" y="92" text-anchor="middle" font-size="8" fill="#5a5a58">56×56</text>
    </g>
    <g transform="translate(210,30)">
      <rect x="0" y="0" width="56" height="70" fill="#ededea" stroke="#0a0a0a"/>
      <text x="28" y="80" text-anchor="middle">6×7</text>
      <text x="28" y="92" text-anchor="middle" font-size="8" fill="#5a5a58">70×56</text>
    </g>
    <g transform="translate(285,30)">
      <rect x="0" y="-14" width="56" height="84" fill="#ededea" stroke="#0a0a0a"/>
      <text x="28" y="80" text-anchor="middle">6×9</text>
      <text x="28" y="92" text-anchor="middle" font-size="8" fill="#5a5a58">84×56</text>
    </g>
    <g transform="translate(360,30)">
      <rect x="0" y="-22" width="76" height="92" fill="#ededea" stroke="#0a0a0a"/>
      <text x="38" y="80" text-anchor="middle">4×5</text>
      <text x="38" y="92" text-anchor="middle" font-size="8" fill="#5a5a58">102×127</text>
    </g>

    <text x="10" y="135" font-size="11" font-weight="500">ゾーン制御の自由度</text>
    <line x1="10" y1="155" x2="450" y2="155" stroke="#0a0a0a" stroke-width="1.4"/>
    <text x="10" y="172" font-size="10">35mm</text>
    <text x="10" y="184" font-size="9" fill="#5a5a58">1本一括現像、N選択肢のみ</text>
    <text x="200" y="172" font-size="10">中判 (645/66/67/69)</text>
    <text x="200" y="184" font-size="9" fill="#5a5a58">1本でシーン揃えて N±対応</text>
    <text x="370" y="172" font-size="10">大判シート</text>
    <text x="370" y="184" font-size="9" fill="#5a5a58">1枚毎に個別現像</text>
    <path d="M10 195 L450 195" stroke="#0a0a0a" stroke-dasharray="2 3"/>
    <text x="10" y="210" font-size="9" fill="#5a5a58">→ ZS の本来の領域は大判だが、中判でも実用十分</text>
  </g>
</svg>
<table class="article-table">
  <tr><th>フォーマット</th><th>実寸 (mm)</th><th>特徴</th></tr>
  <tr><td>645</td><td>56 × 41.5</td><td>16 枚撮、機動的、ゾーンシステムの導入に良い</td></tr>
  <tr><td>6×6</td><td>56 × 56</td><td>正方形構図、Hasselblad、Rolleiflex 系</td></tr>
  <tr><td>6×7</td><td>70 × 56</td><td>「理想形」(Pentax 67、Mamiya RB67/RZ67)</td></tr>
  <tr><td>6×9</td><td>84 × 56</td><td>横長、35mm に近い縦横比</td></tr>
  <tr><td>4×5 シート</td><td>102 × 127</td><td>ゾーンシステムの本来の領域、シートごと個別現像</td></tr>
</table>
<p>ロールフィルムでは 1 本の中で同じ現像を共有するため、<strong>同じシーン傾向 (DR が近い被写体) で撮り切る</strong>のが鉄則。シート派は1枚ごとに N±現像できる自由度が魅力。</p>
`,
      },
      {
        heading: 'フィルターの分光的役割',
        body: `
<p>白黒フィルムにとってフィルターは「色の差を明度の差に翻訳する装置」である。フィルターは自分の色を通し補色を遮るため:</p>
<ul>
  <li><strong>赤フィルター</strong> → 赤を明るく / 青 (空) を暗く</li>
  <li><strong>緑フィルター</strong> → 緑 (葉) を明るく / 赤 (肌) を暗く</li>
  <li><strong>黄フィルター</strong> → 弱めの赤フィルター効果、ニュートラル寄り</li>
</ul>
<p>同じ風景を撮っても、フィルター 1 枚で空の調子が ゾーン III から ゾーン VII まで変動しうる。<strong>「フィルター = 第二の露出操作」</strong>と捉えると活用しやすい。</p>
`,
      },
      {
        heading: '三脚と精密性',
        body: `
<p>ゾーンシステムを実践するなら、三脚は事実上必須。理由は以下:</p>
<ul>
  <li>スポット測光に時間がかかる (慣れても 2-3 分)</li>
  <li>F11〜F22 + 低 EI で SS が遅くなる</li>
  <li>構図の微調整と測光部位の関係を保つ必要がある</li>
  <li>長時間露光・偏光フィルター使用時の確実な像定着</li>
</ul>
<p>測光した部位と最終構図がずれないよう、三脚に据えてから測光する習慣をつける。</p>
`,
      },
      {
        heading: '撮影時に必ず記録する 7 項目',
        body: `
<ol>
  <li>主要シャドウの位置と測光 EV、配置ゾーン</li>
  <li>主要ハイライトの位置と測光 EV、落ち位置</li>
  <li>その他の重要部分の測光 EV (3〜5 点)</li>
  <li>採用した絞り・シャッター・EI</li>
  <li>使用フィルター</li>
  <li>計画した N±現像方針</li>
  <li>シーン情報 (場所・天候・時刻)</li>
</ol>
<p>Zone Notes はこれらを撮影フローの中で自動記録する。後日の現像・プリントで完全に再現できる状態が理想。</p>
`,
      },
    ],
  },

  // ============================================================
  // 06. 現像
  // ============================================================
  {
    id: 'development',
    chapter: '06',
    title: '現像',
    summary: 'N、N+、N− の物理的意味と、再現性のある現像プロセス。パーソナルフィルムテストで自分の基準を作る。',
    sections: [
      {
        heading: 'N、N+、N− の物理',
        body: `
<p>現像時間を伸ばすほど、ハイライトの密度が上がっていく。シャドウは時間に対して鈍感なので、現像は <strong>「ハイライトを目標に到達させる時間」</strong> を選ぶ操作になる。</p>
<svg class="article-illust" viewBox="0 0 460 240" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">DEVELOPMENT TIME MULTIPLIER</text>
    <line x1="40" y1="200" x2="440" y2="200" stroke="#0a0a0a" stroke-width="1.4"/>
    <line x1="40" y1="40" x2="40" y2="200" stroke="#0a0a0a" stroke-width="1.4"/>
    <text x="40" y="216" text-anchor="middle" font-size="9" fill="#5a5a58">N−2</text>
    <text x="120" y="216" text-anchor="middle" font-size="9" fill="#5a5a58">N−1</text>
    <text x="200" y="216" text-anchor="middle" font-size="9" fill="#5a5a58">N</text>
    <text x="280" y="216" text-anchor="middle" font-size="9" fill="#5a5a58">N+1</text>
    <text x="360" y="216" text-anchor="middle" font-size="9" fill="#5a5a58">N+2</text>
    <text x="440" y="216" text-anchor="middle" font-size="9" fill="#5a5a58">N+3</text>
    <text x="20" y="44" font-size="9" fill="#5a5a58">×2.3</text>
    <text x="20" y="84" font-size="9" fill="#5a5a58">×1.85</text>
    <text x="20" y="124" font-size="9" fill="#5a5a58">×1.4</text>
    <text x="22" y="164" font-size="9" fill="#5a5a58">×1.0</text>
    <text x="22" y="184" font-size="9" fill="#5a5a58">×0.7</text>
    <line x1="40" y1="180" x2="440" y2="180" stroke="#ededea" stroke-dasharray="2 3"/>
    <line x1="40" y1="160" x2="440" y2="160" stroke="#ededea" stroke-dasharray="2 3"/>
    <line x1="40" y1="120" x2="440" y2="120" stroke="#ededea" stroke-dasharray="2 3"/>
    <line x1="40" y1="80"  x2="440" y2="80"  stroke="#ededea" stroke-dasharray="2 3"/>
    <line x1="40" y1="40"  x2="440" y2="40"  stroke="#ededea" stroke-dasharray="2 3"/>
    <path d="M40 188 L120 180 L200 160 L280 120 L360 80 L440 44" stroke="#0a0a0a" stroke-width="2" fill="none"/>
    <circle cx="40"  cy="188" r="3" fill="#0a0a0a"/>
    <circle cx="120" cy="180" r="3" fill="#0a0a0a"/>
    <circle cx="200" cy="160" r="3" fill="#0a0a0a"/>
    <circle cx="280" cy="120" r="3" fill="#0a0a0a"/>
    <circle cx="360" cy="80"  r="3" fill="#0a0a0a"/>
    <circle cx="440" cy="44"  r="3" fill="#0a0a0a"/>
    <text x="200" y="148" text-anchor="middle" font-size="11" font-weight="500">基準 N</text>
    <line x1="180" y1="200" x2="220" y2="200" stroke="#0a0a0a" stroke-width="3"/>
    <text x="280" y="20" font-size="9" fill="#5a5a58">圧縮 (Compression) ←   → 拡張 (Expansion)</text>
  </g>
</svg>
<ul>
  <li><strong>N (Normal)</strong> — シャドウ III、ハイライト VIII になる時間</li>
  <li><strong>N+1</strong> — ハイライト VII を VIII まで持ち上げる (拡張現像、約 ×1.4)</li>
  <li><strong>N−1</strong> — ハイライト IX を VIII まで抑える (圧縮現像、約 ×0.7)</li>
</ul>
<p>これらの倍率は経験則。フィルム・現像液により ±10% 程度のばらつきがある。</p>
`,
      },
      {
        heading: 'パーソナルフィルムテスト',
        body: `
<p>箱書きの ISO・現像時間は出発点に過ぎない。真の精度を得るには、自分の機材・現像液・攪拌・温度の組合せでテストする必要がある。</p>
<p><strong>① 実効感度 (EI) テスト</strong> — ゾーン I 配置で撮影し、ベース+カブリ+0.10 の濃度を出す EI を測定。</p>
<p><strong>② N 時間テスト</strong> — ゾーン VIII 配置で撮影し、目標濃度 (1.20〜1.30) を出す現像時間を測定。</p>
<ol>
  <li>同一被写体を異なる EI で 5 段階撮影</li>
  <li>同一被写体を異なる現像時間で 4-5 段階現像</li>
  <li>濃度計で測定 → 適正値を特定</li>
  <li>結果を機材プリセットに「パーソナル EI」「N 時間」として記録</li>
</ol>
<p>濃度計がない場合は、目視で濃度の段差を確認するだけでも十分実用的。</p>
`,
      },
      {
        heading: '現像液の特性',
        body: `
<table class="article-table">
  <tr><th>現像液</th><th>特性</th><th>適した用途</th></tr>
  <tr><td>D-76 (1:1)</td><td>標準的、バランス型</td><td>初心者の出発点、汎用</td></tr>
  <tr><td>HC-110 (Dilution B)</td><td>長期保存可、シャープ</td><td>使用頻度の少ない人</td></tr>
  <tr><td>XTOL (1:1)</td><td>微粒子、高解像</td><td>引き伸ばし倍率高い時</td></tr>
  <tr><td>Pyrocat-HD</td><td>ステイン、エッジシャープ</td><td>大判・微粒子重視</td></tr>
  <tr><td>Rodinal (1:50)</td><td>強いシャープネス、粒荒い</td><td>古典的なルック</td></tr>
</table>
<p>現像液を変えると、N 時間、シャドウ感度、ハイライト形状すべてが変わる。<strong>1 種類の組合せに習熟してから他を試す</strong>のが上達の道筋。</p>
`,
      },
      {
        heading: '再現性の 4 つの柱',
        body: `
<svg class="article-illust" viewBox="0 0 460 200" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">DEVELOPING TANK &amp; REEL</text>
    <g transform="translate(20,30)">
      <ellipse cx="50" cy="20" rx="40" ry="8" fill="#cfcfcf" stroke="#0a0a0a"/>
      <path d="M10 20 L10 130 Q10 145 50 145 Q90 145 90 130 L90 20" fill="#9a9a9a" stroke="#0a0a0a"/>
      <ellipse cx="50" cy="20" rx="40" ry="8" fill="none" stroke="#0a0a0a"/>
      <ellipse cx="50" cy="80" rx="30" ry="3" fill="none" stroke="#5a5a58"/>
      <line x1="20" y1="80" x2="20" y2="120" stroke="#0a0a0a" stroke-width="1"/>
      <line x1="80" y1="80" x2="80" y2="120" stroke="#0a0a0a" stroke-width="1"/>
      <text x="50" y="80" text-anchor="middle" font-size="9" fill="#0a0a0a">reel</text>
      <text x="50" y="170" text-anchor="middle" font-size="10" font-weight="500">Developing Tank</text>
    </g>
    <g transform="translate(140,30)">
      <text x="0" y="14" font-size="10" font-weight="500">再現性の 4 つの柱</text>
      <text x="0" y="36" font-size="10" fill="#5a5a58">① 温度: 20.0°C ±0.3 (氷水ジャケット推奨)</text>
      <text x="0" y="54" font-size="10" fill="#5a5a58">② 攪拌: 「最初30秒連続 → 30秒毎に5秒」</text>
      <text x="0" y="72" font-size="10" fill="#5a5a58">③ 希釈と新鮮さ: ロット記録</text>
      <text x="0" y="90" font-size="10" fill="#5a5a58">④ 水洗・定着の質: 不十分は変質</text>
      <text x="0" y="118" font-size="10" font-weight="500">攪拌の動作 (タンク反転)</text>
      <g transform="translate(0,128)">
        <rect x="0"  y="0"  width="14" height="20" fill="#9a9a9a" stroke="#0a0a0a"/>
        <text x="20" y="14" font-size="9">→</text>
        <rect x="32" y="0"  width="14" height="20" fill="#9a9a9a" stroke="#0a0a0a" transform="rotate(45 39 10)"/>
        <text x="60" y="14" font-size="9">→</text>
        <rect x="72" y="-2" width="14" height="20" fill="#9a9a9a" stroke="#0a0a0a" transform="rotate(180 79 8)"/>
        <text x="100" y="14" font-size="9">→</text>
        <rect x="115" y="0" width="14" height="20" fill="#9a9a9a" stroke="#0a0a0a" transform="rotate(315 122 10)"/>
        <text x="143" y="14" font-size="9">→</text>
        <rect x="157" y="0" width="14" height="20" fill="#9a9a9a" stroke="#0a0a0a"/>
      </g>
      <text x="0" y="178" font-size="9" fill="#5a5a58">1 回転 ≈ 4 秒、5 秒で 1-1.5 回転</text>
    </g>
  </g>
</svg>
<p>現像で同じ結果を出すには、以下を必ず一定に保つ:</p>
<ol>
  <li><strong>温度</strong> — 20.0°C ± 0.3°C。夏場は氷水ジャケット推奨</li>
  <li><strong>攪拌</strong> — 「最初の 30 秒連続、以降 30 秒ごとに 5 秒」など、自分のプロトコルを固定</li>
  <li><strong>現像液の希釈と新鮮さ</strong> — 古い現像液は活性が落ちる、ロット記録</li>
  <li><strong>水洗・定着の質</strong> — 不十分だと長期で像が変質</li>
</ol>
<p>Zone Notes の DEVELOPMENT 画面のタイマーは、ここで確実な時間管理を支援する。暗室ナイトモードを併用するとなお良い。</p>
`,
      },
    ],
  },

  // ============================================================
  // 07. プリント
  // ============================================================
  {
    id: 'printing',
    chapter: '07',
    title: 'プリント',
    summary: 'ネガを「楽譜」と捉え、引伸機・印画紙・露光技法でゾーンを最終的な調子に焼き上げる工程。',
    sections: [
      {
        heading: 'ネガ = 楽譜、プリント = 演奏',
        body: `
<p>同じネガからでも、印画紙・号数・覆い焼き・焼き込みの組合せで全く違うプリントが生まれる。ゾーンシステムにおいてプリントは <strong>「予測したゾーンを意図通りに紙の上に翻訳する」</strong> 工程である。</p>
<p>《Moonrise, Hernandez, New Mexico》(1941) は、アダムス自身が同一ネガから 1940 年代の柔らかい解釈と 1970 年代の劇的な空の解釈を作り続けた代表例。</p>
`,
      },
      {
        heading: '引伸機と印画紙の選択',
        body: `
<table class="article-table">
  <tr><th>装置</th><th>特性</th></tr>
  <tr><td>コンデンサー式</td><td>シャープ、コントラスト高、傷が目立つ</td></tr>
  <tr><td>ディフューザー式 (コールドライト)</td><td>滑らか、ハイライト伸び良し、長時間露光</td></tr>
</table>
<table class="article-table">
  <tr><th>印画紙</th><th>特性</th></tr>
  <tr><td>FB (Fiber Based)</td><td>最高画質・長期保存、扱い難</td></tr>
  <tr><td>RC (Resin Coated)</td><td>即乾、扱い易、保存性は FB に劣る</td></tr>
  <tr><td>VC (Variable Contrast)</td><td>フィルター 0〜5 で号数を可変</td></tr>
  <tr><td>Graded (号数固定)</td><td>クラシック、特定号数で安定</td></tr>
</table>
`,
      },
      {
        heading: 'プリントの 5 ステップ',
        body: `
<ol>
  <li><strong>テストストリップ</strong> — 5 / 10 / 15 / 20 / 25 秒の段階露光で基本時間を決定</li>
  <li><strong>ストレートプリント</strong> — 全面均一露光で基準プリントを焼く</li>
  <li><strong>コントラスト調整</strong> — VC ならフィルター、号数紙なら号数選択</li>
  <li><strong>覆い焼き (Dodge)</strong> — 暗くしたい部分を遮光してトーンを残す</li>
  <li><strong>焼き込み (Burn)</strong> — 明るすぎる部分に追加露光して引き締める</li>
</ol>
<svg class="article-illust" viewBox="0 0 460 200" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">DODGE &amp; BURN</text>
    <g transform="translate(10,28)">
      <rect x="0" y="0" width="200" height="120" fill="#5e5e5e" stroke="#0a0a0a"/>
      <text x="100" y="20" text-anchor="middle" font-size="9" fill="#ffffff" letter-spacing="0.18em">PRINT</text>
      <circle cx="50"  cy="60" r="22" fill="#9a9a9a" stroke="#0a0a0a" stroke-dasharray="3 3"/>
      <text x="50" y="63" text-anchor="middle" font-size="10" fill="#0a0a0a">DODGE</text>
      <text x="50" y="100" text-anchor="middle" font-size="8" fill="#ffffff">−30%</text>
      <rect x="120" y="40" width="60" height="50" fill="#2d2d2d" stroke="#ffffff" stroke-dasharray="3 3"/>
      <text x="150" y="68" text-anchor="middle" font-size="10" fill="#ffffff">BURN</text>
      <text x="150" y="105" text-anchor="middle" font-size="8" fill="#ffffff">+50%</text>
    </g>
    <g transform="translate(230,28)">
      <text x="0" y="10" font-size="10" font-weight="500">DODGE (覆い焼き)</text>
      <text x="0" y="26" font-size="10" fill="#5a5a58">露光中に手・カード等で遮光</text>
      <text x="0" y="40" font-size="10" fill="#5a5a58">→ その部分は明るく残る</text>
      <text x="0" y="60" font-size="10" font-weight="500">BURN (焼き込み)</text>
      <text x="0" y="76" font-size="10" fill="#5a5a58">基本露光後に追加露光</text>
      <text x="0" y="90" font-size="10" fill="#5a5a58">→ その部分は暗くなる</text>
      <text x="0" y="112" font-size="9" fill="#5a5a58">いずれも常に手を動かして</text>
      <text x="0" y="124" font-size="9" fill="#5a5a58">境界をぼかすのが鉄則</text>
    </g>
    <text x="10" y="170" font-size="11" fill="#5a5a58">基本露光 12s に対して 中央 +50% (= 6s 追加), 左 -30% (= 3.6s 遮光)</text>
    <text x="10" y="186" font-size="11" fill="#5a5a58">アダムスは《Moonrise》を 1000 枚以上焼いて Dodge/Burn を改善し続けた</text>
  </g>
</svg>
`,
      },
      {
        heading: 'スプリットグレード露光',
        body: `
<p>VC 印画紙の現代的定石。号数を 2 段階に分けて露光する:</p>
<svg class="article-illust" viewBox="0 0 460 180" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">SPLIT GRADE EXPOSURE</text>
    <g>
      <rect x="10" y="30" width="90" height="50" fill="#ffffff" stroke="#0a0a0a"/>
      <text x="55" y="50" text-anchor="middle" font-size="11" font-weight="500">Grade 0</text>
      <text x="55" y="68" text-anchor="middle" font-size="9" fill="#5a5a58">soft filter</text>
      <text x="55" y="100" text-anchor="middle" font-size="9">ハイライトを焼く</text>
      <text x="55" y="114" text-anchor="middle" font-size="9" fill="#5a5a58">例: 12 秒</text>
    </g>
    <text x="120" y="60" font-size="20" fill="#5a5a58">+</text>
    <g>
      <rect x="150" y="30" width="90" height="50" fill="#0a0a0a" stroke="#0a0a0a"/>
      <text x="195" y="50" text-anchor="middle" font-size="11" font-weight="500" fill="#ffffff">Grade 5</text>
      <text x="195" y="68" text-anchor="middle" font-size="9" fill="#cfcfcf">hard filter</text>
      <text x="195" y="100" text-anchor="middle" font-size="9">シャドウを焼く</text>
      <text x="195" y="114" text-anchor="middle" font-size="9" fill="#5a5a58">例: 6 秒</text>
    </g>
    <text x="260" y="60" font-size="20" fill="#5a5a58">=</text>
    <g>
      <rect x="290" y="30" width="160" height="50" fill="#444444" stroke="#0a0a0a"/>
      <text x="370" y="48" text-anchor="middle" font-size="10" fill="#ffffff" letter-spacing="0.18em">FINAL PRINT</text>
      <text x="370" y="65" text-anchor="middle" font-size="9" fill="#ffffff">精密な階調制御</text>
      <text x="370" y="76" text-anchor="middle" font-size="9" fill="#ffffff">部分 Dodge/Burn 可</text>
    </g>
    <text x="10" y="135" font-size="11" fill="#5a5a58">アダムスの号数紙時代は不可能、現代 VC 印画紙の最大の強み。</text>
    <text x="10" y="152" font-size="11" fill="#5a5a58">各ステップで個別の Dodge/Burn が可能 → 階調の柔軟性が極めて高い。</text>
  </g>
</svg>
<ol>
  <li><strong>Grade 0 フィルター</strong> でハイライトに必要な露光を与える</li>
  <li><strong>Grade 5 フィルター</strong> でシャドウに必要な露光を与える</li>
  <li>各ステップで個別に Dodge / Burn が可能</li>
</ol>
<p>これにより階調の柔軟性が極めて高くなり、アダムス時代の号数紙では難しかった精密な調子の制御が可能になる。</p>
`,
      },
      {
        heading: '仕上げ',
        body: `
<ul>
  <li><strong>セレン調色 (KRST)</strong> — 黒の深みを増し、保存性を向上。アダムスは標準的に使用</li>
  <li><strong>スポッティング</strong> — Spotone やインクで傷・ホコリ穴を補修</li>
  <li><strong>水洗</strong> — FB は 1 時間以上、ハイポクリア併用で 30 分</li>
  <li><strong>乾燥</strong> — 網棚または平面プレス、温湿度管理</li>
  <li><strong>マウント</strong> — 4ply 博物館品質マットボード、ヒンジマウント</li>
</ul>
<p>ここまでを「シーン 1 件」としてプリント評価し、Zone Notes の REFLECTION フェーズ 3 に記録すると、撮影〜プリントの全工程の学習データになる。</p>
`,
      },
    ],
  },

  // ============================================================
  // 08. 実践のコツ
  // ============================================================
  {
    id: 'practice',
    chapter: '08',
    title: '実践のコツ',
    summary: 'よくある失敗、上達の道筋、最初の練習被写体。挫折せず継続するための現実的アドバイス。',
    sections: [
      {
        heading: '最初の 100 カットで身につけること',
        body: `
<p>新しいフィルム・新しいシステムに慣れる時期は、変数を増やしすぎない:</p>
<ol>
  <li>機材・フィルム・現像液は 1 種類に固定</li>
  <li>すべて N 現像で揃える</li>
  <li>ゾーン III 配置を標準にして、ハイライトの落ち位置を観察</li>
  <li>100 カット撮影〜現像〜プリントを通して自分の EI を確定</li>
  <li>その後ようやく N+ / N− を試し始める</li>
</ol>
`,
      },
      {
        heading: 'よくある失敗パターン',
        body: `
<table class="article-table">
  <tr><th>症状</th><th>原因</th><th>対処</th></tr>
  <tr><td>シャドウが薄く調子なし</td><td>露出不足 (EI 過大)</td><td>EI を 1/3〜1 段下げる</td></tr>
  <tr><td>ハイライトが飛ぶ・抜ける</td><td>現像過剰</td><td>N 時間を 5〜10% 短縮、N−1 を試す</td></tr>
  <tr><td>全体が眠い・コントラスト不足</td><td>現像不足、低コントラストシーン</td><td>N+1 を試す、印画紙号数上げる</td></tr>
  <tr><td>コントラスト過多・硬い</td><td>現像過剰、高コントラストシーン</td><td>N−1 / N−2、印画紙号数下げる</td></tr>
  <tr><td>プリントが暗い</td><td>ネガの密度不足、露光時間短い</td><td>絞り開く / 露光時間延長</td></tr>
</table>
`,
      },
      {
        heading: '練習に向く被写体',
        body: `
<ol>
  <li><strong>晴天順光の建物</strong> — DR 5-6 段、N 現像基準。ゾーン感覚を掴む基本</li>
  <li><strong>日陰の人物</strong> — DR 4 段以下、N+1 拡張の練習</li>
  <li><strong>雪景色 + 日陰</strong> — DR 8-9 段、N−2 圧縮の練習</li>
  <li><strong>ステンドグラスや窓辺</strong> — 極端なハイライト、限界の判断</li>
  <li><strong>樹木・葉</strong> — 中間調の連続変化、フィルター効果の比較</li>
</ol>
<p>自分の住む環境で「いつでも撮れる」シーンを 5 つ決め、機材を変えながら同じ被写体を反復するのが最も学習効率が高い。</p>
`,
      },
      {
        heading: '上達の道筋',
        body: `
<p>ゾーンシステムは数ヶ月で全てを身につけられる体系ではない。以下は典型的な学習曲線:</p>
<ul>
  <li><strong>1〜3 ヶ月</strong>: スポット測光と配置の感覚、N 現像のフィルム特性</li>
  <li><strong>3〜6 ヶ月</strong>: パーソナル EI / N 時間の確定、N+ / N− の使い分け</li>
  <li><strong>6〜12 ヶ月</strong>: フィルター活用、複数フィルムの比較、独自スタイル形成</li>
  <li><strong>12 ヶ月〜</strong>: プリントのスプリットグレード、調色、独自の表現</li>
</ul>
<p>Zone Notes の ANALYTICS は、自分がどの段階にいるかを統計で示す。露出評価の偏り (under/over) や N±使用頻度の傾向を見ながら、次に強化すべき部分を判断する。</p>
`,
      },
      {
        heading: '最後に',
        body: `
<p class="article-quote">"The negative is the equivalent of the composer's score, and the print the performance."<br/>— Ansel Adams</p>
<p>ゾーンシステムは技術であると同時に、自分の意図を視覚化する訓練でもある。完璧な計算より、<strong>自分が何を見せたいかを言語化する習慣</strong>のほうが上達を速める。</p>
<p>このアプリは、その習慣化を支える電子フィールドノートとして設計されている。撮影 → 現像 → プリントの全工程の判断を記録し、後で見返せる状態を作ることで、感覚を「データ」に変えていく。</p>
`,
      },
    ],
  },
];

// ============================================================
// 09. フィルム特性比較
// ============================================================
ARTICLES.push({
  id: 'film-comparison',
  chapter: '09',
  title: 'フィルム特性比較',
  summary: '主要モノクロフィルムの分光感度・粒状性・コントラスト傾向を比較し、シーンに合わせた選択基準を整理する。',
  sections: [
    {
      heading: '伝統系 vs 新世代',
      body: `
<div style="display:grid; grid-template-columns: repeat(2, 1fr); gap:8px; margin-bottom:14px;">
  <figure class="article-figure" style="margin:0;">
    <img src="/learn-images/09-film-trix.png" alt="Kodak Tri-X 400" loading="lazy" />
    <figcaption>Kodak Tri-X 400</figcaption>
  </figure>
  <figure class="article-figure" style="margin:0;">
    <img src="/learn-images/09-film-hp5.png" alt="Ilford HP5 Plus" loading="lazy" />
    <figcaption>Ilford HP5 Plus 400</figcaption>
  </figure>
  <figure class="article-figure" style="margin:0;">
    <img src="/learn-images/09-film-tmax.png" alt="Kodak T-Max" loading="lazy" />
    <figcaption>Kodak T-Max 400</figcaption>
  </figure>
  <figure class="article-figure" style="margin:0;">
    <img src="/learn-images/09-film-acros.png" alt="Fujifilm Acros 100 II" loading="lazy" />
    <figcaption>Fujifilm Acros 100 II</figcaption>
  </figure>
</div>
<p>モノクロフィルムは大きく <strong>伝統系</strong> (T-grain でない、立方晶結晶) と <strong>新世代 T-grain 系</strong> (T-Max / Delta) に分かれる。</p>
<table class="article-table">
  <tr><th>系統</th><th>代表</th><th>特徴</th></tr>
  <tr><td>伝統系</td><td>Tri-X / HP5+ / FP4+</td><td>粒は粗いが柔らかい階調、ハイライトの破綻が緩やか</td></tr>
  <tr><td>T-grain 系</td><td>T-Max 100/400 / Delta 100/400</td><td>微粒子、シャープ、ハイライト硬め、現像液選択にシビア</td></tr>
  <tr><td>カーブ系</td><td>Acros 100 II</td><td>滑らかなトーン、長時間露光に強い (相反則不軌少)</td></tr>
  <tr><td>古典系</td><td>Pan F+ 50 / Adox CMS 20</td><td>極微粒子、シャドウ感度低、低速</td></tr>
</table>
`,
    },
    {
      heading: '感度別の使い分け',
      body: `
<table class="article-table">
  <tr><th>ISO</th><th>適した用途</th><th>例</th></tr>
  <tr><td>25-100</td><td>大判・微粒子重視・長時間露光</td><td>Acros 100 II, FP4+, Pan F+</td></tr>
  <tr><td>400</td><td>汎用、最も使いやすい</td><td>Tri-X, HP5+, T-Max 400, Delta 400</td></tr>
  <tr><td>3200</td><td>暗所・ステージ・ストリート</td><td>T-Max P3200, Delta 3200</td></tr>
</table>
<p>初心者は ISO 400 の <strong>Tri-X 400 か HP5+</strong> から始め、現像液は <strong>D-76 1:1</strong> で標準化する。これが最も再現性の高い出発点。</p>
`,
    },
    {
      heading: 'コントラスト特性',
      body: `
<p>同じ被写体でもフィルムによって階調曲線が異なる。Zone Notes のモノクロプレビューはこの差を概念的に再現するが、実機では微差ある:</p>
<ul>
  <li><strong>Tri-X</strong> — シャドウからミッドトーンに豊かな階調、ハイライトはやや圧縮</li>
  <li><strong>HP5+</strong> — Tri-X より柔らかい、ハイライトの伸びが良い</li>
  <li><strong>T-Max 400</strong> — リニアに近い、ハイライトが硬く飛びやすい (DD-X 推奨)</li>
  <li><strong>Acros 100 II</strong> — シャドウからハイライトまで滑らか、Zone System との相性◎</li>
</ul>
`,
    },
    {
      heading: '相反則不軌',
      body: `
<p>長時間露光 (1秒以上) では、フィルムの感度が公称値より低下する <strong>相反則不軌 (Reciprocity Failure)</strong> が発生する。</p>
<table class="article-table">
  <tr><th>計算上の露光時間</th><th>Tri-X 補正後</th><th>Acros II 補正後</th></tr>
  <tr><td>1 秒</td><td>1.5 秒</td><td>1 秒 (補正不要)</td></tr>
  <tr><td>10 秒</td><td>50 秒</td><td>10 秒</td></tr>
  <tr><td>100 秒</td><td>1200 秒 (20分)</td><td>100 秒</td></tr>
</table>
<p><strong>Acros 100 II は 120 秒まで補正不要</strong>という特性のため、夜景・長時間露光ではほぼ唯一の選択肢。</p>
`,
    },
  ],
});

// ============================================================
// 10. 構図とゾーン配置
// ============================================================
ARTICLES.push({
  id: 'composition',
  chapter: '10',
  title: '構図とゾーン配置',
  summary: '構図の中で「光と影をどこに置くか」を意識する。古典構図とゾーン配置を統合する視点。',
  sections: [
    {
      heading: '光の流れを読む',
      body: `
<p>優れた風景写真は、被写体だけでなく <strong>光の方向と質感</strong> によって構築される。アダムスは「太陽は被写体ではなく、彫刻刀である」と表現した。</p>
<svg class="article-illust" viewBox="0 0 460 220" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">LIGHT DIRECTIONS</text>

    <g transform="translate(20,40)">
      <circle cx="35" cy="50" r="14" fill="#0a0a0a"/>
      <circle cx="0"  cy="0"  r="7" fill="#0a0a0a"/>
      <path d="M5 5 L25 35" stroke="#0a0a0a" stroke-width="1.4"/>
      <path d="M20 32 l5 4 -1 -6" stroke="#0a0a0a" stroke-width="1.4" fill="#0a0a0a"/>
      <text x="35" y="90" text-anchor="middle" font-size="10" font-weight="500">FRONT (順光)</text>
      <text x="35" y="106" text-anchor="middle" font-size="9" fill="#5a5a58">フラット、DR 4-5</text>
    </g>

    <g transform="translate(140,40)">
      <circle cx="35" cy="50" r="14" fill="#0a0a0a"/>
      <ellipse cx="49" cy="50" rx="6" ry="14" fill="#5e5e5e" opacity="0.5"/>
      <circle cx="-5" cy="50" r="7" fill="#0a0a0a"/>
      <path d="M5 50 L25 50" stroke="#0a0a0a" stroke-width="1.4"/>
      <path d="M20 47 l5 3 -5 3" stroke="#0a0a0a" stroke-width="1.4" fill="#0a0a0a"/>
      <text x="35" y="90" text-anchor="middle" font-size="10" font-weight="500">SIDE (サイド光)</text>
      <text x="35" y="106" text-anchor="middle" font-size="9" fill="#5a5a58">立体感最大、DR 5-7</text>
    </g>

    <g transform="translate(260,40)">
      <circle cx="35" cy="50" r="14" fill="#0a0a0a"/>
      <circle cx="80" cy="50" r="7" fill="#0a0a0a"/>
      <path d="M70 50 L55 50" stroke="#0a0a0a" stroke-width="1.4"/>
      <path d="M60 47 l-5 3 5 3" stroke="#0a0a0a" stroke-width="1.4" fill="#0a0a0a"/>
      <text x="35" y="90" text-anchor="middle" font-size="10" font-weight="500">BACK (逆光)</text>
      <text x="35" y="106" text-anchor="middle" font-size="9" fill="#5a5a58">シルエット、DR 7-10</text>
    </g>

    <g transform="translate(380,40)">
      <circle cx="35" cy="50" r="14" fill="#0a0a0a"/>
      <path d="M10 15 q10 -5 20 0 q10 -5 20 0 q10 -5 20 0" stroke="#0a0a0a" stroke-width="1.4" fill="none"/>
      <path d="M15 22 q10 -5 20 0 q10 -5 20 0" stroke="#0a0a0a" stroke-width="1.4" fill="none"/>
      <path d="M22 32 l3 4 M48 32 l3 4 M35 32 l3 4" stroke="#0a0a0a" stroke-width="1.4" opacity="0.4"/>
      <text x="35" y="90" text-anchor="middle" font-size="10" font-weight="500">CLOUDY (曇天)</text>
      <text x="35" y="106" text-anchor="middle" font-size="9" fill="#5a5a58">ソフト、DR 3-4</text>
    </g>

    <text x="10" y="175" font-size="11" fill="#5a5a58">光線方向が DR と陰影の形状を決定する。</text>
    <text x="10" y="192" font-size="11" fill="#5a5a58">サイド光が立体感を最大化、逆光は危険だが詩情を生む。</text>
    <text x="10" y="208" font-size="11" fill="#5a5a58">曇天はシャドウのディテールが豊富、N+ で表現力アップ。</text>
  </g>
</svg>
<ul>
  <li><strong>順光</strong> — フラットでコントラスト低め (DR 4-5 段)</li>
  <li><strong>サイド光</strong> — 立体感最大、テクスチャが生きる (DR 5-7 段)</li>
  <li><strong>逆光</strong> — シルエット表現、ハイライト周辺が重要 (DR 7-10 段)</li>
  <li><strong>曇天</strong> — ソフト、シャドウディテール豊富 (DR 3-4 段)</li>
</ul>
`,
    },
    {
      heading: 'ゾーン配置による画面分割',
      body: `
<p>構図を「画面のうち何 % が各ゾーンを占めるか」で考える視点は、ゾーンシステム的な構図訓練として有効。</p>
<table class="article-table">
  <tr><th>典型構図</th><th>ゾーン分布の例</th></tr>
  <tr><td>劇的な空 (アダムス的)</td><td>空: II-III / 雲: VII-VIII / 地表: V / 影: III</td></tr>
  <tr><td>ハイキー (ソフト)</td><td>主要部: VI-VIII / 影: V / アクセント: III</td></tr>
  <tr><td>ローキー (重厚)</td><td>背景: I-II / 主要部: III-IV / アクセント: VI</td></tr>
  <tr><td>標準 (バランス)</td><td>シャドウ: III / 中間: V / ハイライト: VII-VIII の三分割</td></tr>
</table>
<p>撮影前に「この画面はどう塗り分けたいか」を頭の中で素描する習慣をつけると、配置の判断が一貫する。</p>
`,
    },
    {
      heading: '主役と脇役のゾーン差',
      body: `
<p>主題となる被写体は、画面内の他の要素と <strong>2-3 段以上のゾーン差</strong> を持たせると視線が誘導される。</p>
<ul>
  <li>主役を <strong>ゾーン VI-VII</strong>、背景を <strong>ゾーン IV-V</strong> → 主役が明るく際立つ</li>
  <li>主役を <strong>ゾーン III-IV</strong>、背景を <strong>ゾーン VII-VIII</strong> → シルエット強調</li>
  <li>主役と背景が同じゾーン → 視線が定まらない、構図的に弱い</li>
</ul>
<p>フィルター・現像方針はこのゾーン差を作るための道具。例えば赤フィルターで空を III まで落とし、白い建物を VII に置けば、強烈な対比が生まれる。</p>
`,
    },
    {
      heading: '画面端とコーナーの注意',
      body: `
<p>初心者がよくやる失敗:</p>
<ul>
  <li>画面端に明るすぎる部分 (ゾーン IX) があり、視線が外に逃げる</li>
  <li>コーナーがゾーン I で重く、暗黒の塊として浮く</li>
  <li>主要シャドウが画面端に切り取られていて落ち着かない</li>
</ul>
<p>撮影前にスポット測光で <strong>四隅と画面端</strong>も確認し、必要なら焼き込み (Burn) で抑える計画を立てる。プリント時の Burn 計画を撮影時に予測しておくこと自体がプレビジュアライゼーション。</p>
`,
    },
  ],
});

// ============================================================
// 11. 暗室の整え方とプリント保存
// ============================================================
ARTICLES.push({
  id: 'darkroom-setup',
  chapter: '11',
  title: '暗室の整え方とプリント保存',
  summary: '自宅暗室の最低限の機材、安全光、給排水の工夫。プリントの長期保存に必要な工程。',
  sections: [
    {
      heading: '最低限の機材リスト',
      body: `
<table class="article-table">
  <tr><th>カテゴリ</th><th>必須</th><th>推奨</th></tr>
  <tr><td>引伸機</td><td>Beseler 23C / Durst M605 / LPL 7700</td><td>4×5 対応 / VC ヘッド搭載</td></tr>
  <tr><td>レンズ</td><td>50mm (35mm用) / 80mm (中判用)</td><td>Rodagon / Componon-S / EL-Nikkor</td></tr>
  <tr><td>イーゼル</td><td>2 ブレード式</td><td>4 ブレード Saunders</td></tr>
  <tr><td>タイマー</td><td>1/10 秒精度</td><td>RH デジタル</td></tr>
  <tr><td>セーフライト</td><td>OC (琥珀色) ナトリウム灯</td><td>Thomas Duplex (大空間用)</td></tr>
  <tr><td>トレイ</td><td>4 枚 (現像/停止/定着/水洗)</td><td>サイズに合わせて (8×10 / 11×14 等)</td></tr>
  <tr><td>その他</td><td>温度計、トング、メスシリンダー、粒子鏡</td><td>暗室時計、ハイポクリア槽</td></tr>
</table>
<p>初期投資は中古機材を活用すれば 10 万円程度で揃う。新品の引伸レンズだけは妥協しないこと (画質の支配項)。</p>
`,
    },
    {
      heading: '空間の作り方',
      body: `
<figure class="article-figure">
  <img src="/learn-images/11-darkroom-layout.png" alt="暗室レイアウト" loading="lazy" />
  <figcaption>引伸機・現像トレイ 4 列・セーフライト・乾燥棚の典型レイアウト</figcaption>
</figure>
<ul>
  <li><strong>遮光</strong> — 完全な暗室は不要、目を慣らせばわずかな光漏れは許容できる。窓は遮光カーテン + 黒テープ</li>
  <li><strong>給排水</strong> — 浴室を改造するのが定番。シンクに直接トレイ並べ</li>
  <li><strong>換気</strong> — 定着液 (酢酸、亜硫酸ナトリウム) は揮発性。換気扇 + 作業中は短時間集中</li>
  <li><strong>湿度</strong> — 50% 以下が望ましい。雨季は除湿機</li>
  <li><strong>温度</strong> — 20°C 維持が困難な場合、現像液をジャケット保温</li>
</ul>
`,
    },
    {
      heading: 'ウェット側とドライ側の分離',
      body: `
<p>プロの暗室は <strong>ウェット側</strong> (現像トレイ) と <strong>ドライ側</strong> (引伸機・印画紙保管) を物理的に分ける。家庭暗室でも、最低限以下を心がける:</p>
<ul>
  <li>引伸機の側で印画紙を切り出す → ウェット側へ運ぶ</li>
  <li>濡れた手でドライ側に触らない</li>
  <li>定着液が引伸機やレンズに飛ばないよう作業順序を固定</li>
  <li>使い終わった現像液は密閉容器で別保管 (酸化防止)</li>
</ul>
`,
    },
    {
      heading: 'プリントの長期保存',
      body: `
<p>適切な処理をしたファイバー印画紙は <strong>100 年以上</strong>もつ。逆に処理が雑だと数年で変色する。</p>
<ol>
  <li><strong>2 浴定着</strong> — 1 浴目で大半の銀ハロゲン化物を除去、2 浴目で完全溶解</li>
  <li><strong>ハイポクリアリングエージェント</strong> — チオ硫酸塩を除去、水洗時間短縮</li>
  <li><strong>セレン調色</strong> — 銀像をセレン錯体に変換、長期保存性が劇的に向上</li>
  <li><strong>本水洗</strong> — FB 紙で 60 分以上、流水で完全に薬品を除去</li>
  <li><strong>マウント</strong> — 4ply 博物館品質マットボード、ヒンジマウントで紙への接着剤直接接触を避ける</li>
  <li><strong>保管</strong> — 18-22°C、相対湿度 30-50%、暗所、酸性物質から隔離</li>
</ol>
<p>プリント裏面に <strong>撮影日・現像時間・印画紙号数・処理日</strong>を 鉛筆で記入する習慣をつけると、後年の再プリント時に貴重な情報になる。</p>
`,
    },
    {
      heading: 'デジタル時代との接続',
      body: `
<p>ネガをスキャンしてデジタル管理するのも有効:</p>
<ul>
  <li><strong>Epson V850 Pro</strong> — 中判ネガを 4800dpi でスキャン可</li>
  <li><strong>専用フィルムスキャナー</strong> — Plustek 8200i (35mm) / Hasselblad Flextight (中判以上)</li>
  <li><strong>Camera Scanning</strong> — デジタルカメラ + マクロレンズ + ネガホルダー、コスパ◎</li>
</ul>
<p>スキャンデータと Zone Notes の撮影記録を結びつけると、後年「なぜこの露出にしたか」を完全に追跡できる写真資産になる。</p>
`,
    },
  ],
});

// ============================================================
// 12. 相反則不軌と長時間露光
// ============================================================
ARTICLES.push({
  id: 'reciprocity',
  chapter: '12',
  title: '相反則不軌と長時間露光',
  summary: '1秒以上の露光ではフィルム感度が低下する物理現象。夜景・微速度・大判での実用的な補正計算。',
  sections: [
    {
      heading: '相反則と相反則不軌',
      body: `
<p>通常の露光時間では <strong>「絞り値の組合せが同じなら同じ露光量」</strong>という相反則 (Reciprocity Law) が成立する。例: F8 1/60 = F11 1/30 = F16 1/15。</p>
<p>ところが <strong>1秒以上の長時間露光</strong>になると、フィルム乳剤に届く光子が断続的になり、潜像形成が非効率になる。これが <strong>相反則不軌 (Reciprocity Failure)</strong>。計算上の露光時間より、実際にはより長く露光する必要がある。</p>
<p>シャドウ部 (低密度部) ほど影響が顕著で、露光不足のような結果になる。コントラストも増す傾向。</p>
`,
    },
    {
      heading: '主要フィルム別の補正値',
      body: `
<svg class="article-illust" viewBox="0 0 460 240" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">RECIPROCITY FAILURE (補正後露光時間)</text>
    <line x1="50" y1="200" x2="450" y2="200" stroke="#0a0a0a" stroke-width="1.4"/>
    <line x1="50" y1="40"  x2="50" y2="200"  stroke="#0a0a0a" stroke-width="1.4"/>
    <text x="50"  y="216" text-anchor="middle" font-size="9" fill="#5a5a58">1秒</text>
    <text x="150" y="216" text-anchor="middle" font-size="9" fill="#5a5a58">10秒</text>
    <text x="250" y="216" text-anchor="middle" font-size="9" fill="#5a5a58">100秒</text>
    <text x="350" y="216" text-anchor="middle" font-size="9" fill="#5a5a58">1000秒</text>
    <text x="450" y="216" text-anchor="middle" font-size="9" fill="#5a5a58">→ 計算露光</text>
    <text x="20" y="44" font-size="9" fill="#5a5a58">×10</text>
    <text x="20" y="104" font-size="9" fill="#5a5a58">×5</text>
    <text x="20" y="164" font-size="9" fill="#5a5a58">×2</text>
    <text x="20" y="200" font-size="9" fill="#5a5a58">×1</text>
    <line x1="50" y1="160" x2="450" y2="160" stroke="#ededea" stroke-dasharray="2 3"/>
    <line x1="50" y1="100" x2="450" y2="100" stroke="#ededea" stroke-dasharray="2 3"/>

    <path d="M50 200 L150 180 L250 100 L350 50" stroke="#0a0a0a" stroke-width="2" fill="none"/>
    <text x="370" y="50" font-size="10" font-weight="500">Tri-X</text>

    <path d="M50 200 L150 200 L250 195 L350 175" stroke="#5a5a58" stroke-width="2" fill="none" stroke-dasharray="6 3"/>
    <text x="370" y="175" font-size="10" fill="#5a5a58">Acros II</text>

    <line x1="50" y1="200" x2="450" y2="200" stroke="#cfcfcf" stroke-width="1" stroke-dasharray="2 2"/>
    <text x="270" y="232" font-size="9" fill="#5a5a58">点線: 補正不要の理想線</text>
  </g>
</svg>
<table class="article-table">
  <tr><th>計算露光</th><th>Tri-X 400</th><th>HP5+</th><th>T-Max 400</th><th>Acros 100 II</th><th>FP4+</th></tr>
  <tr><td>1秒</td><td>2秒</td><td>2秒</td><td>1.3秒</td><td>1秒</td><td>1.5秒</td></tr>
  <tr><td>10秒</td><td>50秒</td><td>40秒</td><td>15秒</td><td>10秒</td><td>30秒</td></tr>
  <tr><td>100秒</td><td>20分</td><td>15分</td><td>3分</td><td>100秒</td><td>10分</td></tr>
  <tr><td>1000秒</td><td>—</td><td>—</td><td>—</td><td>30分</td><td>—</td></tr>
</table>
<p><strong>Acros 100 II は 120秒まで補正不要</strong>という驚異的な特性を持ち、夜景・星景・大判長時間露光ではほぼ唯一の選択肢になる。</p>
`,
    },
    {
      heading: 'コントラスト補正',
      body: `
<p>長時間露光では <strong>ハイライトが伸び、シャドウが沈む</strong>傾向で、コントラストが上昇する。Tri-X で 30秒露光なら、現像時間を <strong>10〜15% 短縮</strong>するのが目安 (実質 N−1 寄りに)。</p>
<p>Acros II はこの問題も小さく、シーン本来の階調がそのまま記録される。</p>
`,
    },
    {
      heading: '実用シーン例',
      body: `
<table class="article-table">
  <tr><th>シーン</th><th>計算 EV</th><th>計算露光</th><th>Tri-X 補正後</th><th>Acros II 補正後</th></tr>
  <tr><td>満月の夜景 (F11)</td><td>-3</td><td>2分</td><td>30分前後</td><td>2分</td></tr>
  <tr><td>月のない夜空 (F2.8)</td><td>-7</td><td>30分</td><td>事実上不可</td><td>30-45分</td></tr>
  <tr><td>暗い室内 (F2.8)</td><td>2</td><td>4秒</td><td>20秒</td><td>4秒</td></tr>
  <tr><td>雪景色の夕暮れ (F11)</td><td>5</td><td>1秒</td><td>2秒</td><td>1秒</td></tr>
</table>
<p><strong>長時間露光でフィルムを選ぶなら Acros 100 II 一択</strong>。これ以外を使う場合は、撮影現場で電卓または事前に作った補正表を用意する。</p>
`,
    },
    {
      heading: '実践チェックリスト',
      body: `
<ol>
  <li>三脚 + ケーブルレリーズ + ミラーアップ (中判一眼の場合)</li>
  <li>レンズフードと内面反射対策 (長時間露光は微弱光のフレアが顕在化)</li>
  <li>露光中の照明・人通りに注意 (写り込み)</li>
  <li>気温による現像液温度変化 (15℃ なら現像時間 +30%)</li>
  <li>連続撮影しない場合、フィルムは 1 本撮り切らず半分残す覚悟</li>
</ol>
`,
    },
  ],
});

// ============================================================
// 13. スポット測光器の選び方と使いこなし
// ============================================================
ARTICLES.push({
  id: 'spotmeter',
  chapter: '13',
  title: 'スポット測光器の選び方と使いこなし',
  summary: 'ゾーンシステムの中核機材。1°視野で正確に被写体を読む、Pentax/Sekonic/Minolta の比較と運用法。',
  sections: [
    {
      heading: 'なぜスポット測光が必要か',
      body: `
<p>ゾーンシステムは <strong>「シーンの各部分を独立して測り、配置を決める」</strong>手法。広視野の TTL 測光や入射光式では、シーン内の特定の岩肌・特定の雪・特定の影を切り出して測ることができない。</p>
<p>スポット測光は通常 <strong>1° (一部 5°)</strong> の極狭視野で、ファインダーの中央スポットだけの輝度を読む。これにより、ゾーン III に置きたい暗部 / ゾーン VIII に置きたい明部を別々に測定可能になる。</p>
`,
    },
    {
      heading: '主要モデル比較',
      body: `
<figure class="article-figure">
  <img src="/learn-images/13-pentax-spotmeter.png" alt="Pentax Spotmeter V" loading="lazy" />
  <figcaption>Pentax Spotmeter V — ZS の代名詞、ピストルグリップ型</figcaption>
</figure>
<table class="article-table">
  <tr><th>機種</th><th>視野</th><th>EV 表示</th><th>特徴</th></tr>
  <tr><td>Pentax Spotmeter V</td><td>1°</td><td>1/3 段</td><td>シンプル、ZS の代名詞、中古 2-4 万円</td></tr>
  <tr><td>Pentax Digital Spotmeter</td><td>1°</td><td>0.1 EV</td><td>デジタル表示、現行モデル無し、中古希少</td></tr>
  <tr><td>Sekonic L-758D / L-858D</td><td>1°</td><td>0.1 EV</td><td>入射 + 反射 + フラッシュ、ZS モード搭載</td></tr>
  <tr><td>Minolta Spotmeter F</td><td>1°</td><td>1/10 EV</td><td>軽量、フラッシュ対応、廃番だが名機</td></tr>
  <tr><td>Gossen Starlite 2</td><td>1°/5°</td><td>0.1 EV</td><td>高機能、ヨーロッパで人気</td></tr>
</table>
<p>ゾーンシステム入門なら <strong>Pentax Spotmeter V</strong> が定番。電子部品が少なく長持ちし、Zone Dial シールを貼るカスタムが伝統的。</p>
`,
    },
    {
      heading: 'Zone Dial の活用',
      body: `
<svg class="article-illust" viewBox="0 0 460 200" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">ZONE DIAL STICKER (Pentax Spotmeter V)</text>
    <g transform="translate(230,100)">
      <circle r="70" fill="#cfcfcf" stroke="#0a0a0a" stroke-width="1.4"/>
      <circle r="50" fill="#f4f4f2" stroke="#0a0a0a"/>
      <circle r="30" fill="#ffffff" stroke="#0a0a0a"/>
      <text x="0" y="-3" text-anchor="middle" font-size="11" font-weight="500">EV</text>
      <text x="0" y="11" text-anchor="middle" font-size="9" fill="#5a5a58">scale</text>
      <g font-size="8" fill="#0a0a0a">
        <text x="0"   y="-58" text-anchor="middle">V</text>
        <text x="29"  y="-50" text-anchor="middle" transform="rotate(30 29 -50)">VI</text>
        <text x="50"  y="-29" text-anchor="middle" transform="rotate(60 50 -29)">VII</text>
        <text x="58"  y="0"   text-anchor="middle" transform="rotate(90 58 0)">VIII</text>
        <text x="50"  y="29"  text-anchor="middle" transform="rotate(120 50 29)">IX</text>
        <text x="29"  y="50"  text-anchor="middle" transform="rotate(150 29 50)">X</text>
        <text x="0"   y="58"  text-anchor="middle" transform="rotate(180 0 58)" font-size="6">0</text>
        <text x="-29" y="50"  text-anchor="middle" transform="rotate(210 -29 50)">I</text>
        <text x="-50" y="29"  text-anchor="middle" transform="rotate(240 -50 29)">II</text>
        <text x="-58" y="0"   text-anchor="middle" transform="rotate(270 -58 0)">III</text>
        <text x="-50" y="-29" text-anchor="middle" transform="rotate(300 -50 -29)">IV</text>
      </g>
      <path d="M0 -70 L0 -65" stroke="#0a0a0a" stroke-width="2"/>
      <text x="0" y="-78" text-anchor="middle" font-size="7" fill="#5a5a58">▼ ここに測光値を合わせる</text>
    </g>
    <g transform="translate(10,30)">
      <text x="0" y="0" font-size="10" font-weight="500">使い方</text>
      <text x="0" y="20" font-size="10" fill="#5a5a58">1. シャドウを測光</text>
      <text x="0" y="36" font-size="10" fill="#5a5a58">2. EV 値をダイヤルに合わせる</text>
      <text x="0" y="52" font-size="10" fill="#5a5a58">3. ゾーン III マークの位置で</text>
      <text x="0" y="68" font-size="10" fill="#5a5a58">   絞り/SS を読み取る</text>
      <text x="0" y="100" font-size="10" font-weight="500">利点</text>
      <text x="0" y="120" font-size="10" fill="#5a5a58">数値計算なしで配置可</text>
      <text x="0" y="136" font-size="10" fill="#5a5a58">物理ダイヤル = 確実性</text>
    </g>
  </g>
</svg>
<p>Pentax Spotmeter V には EV 値を読み取る回転ダイヤルがある。<strong>Zone Dial シール</strong>を貼ると、測光した部分を直接「ゾーン III」「ゾーン VIII」などのマークに合わせられる:</p>
<ol>
  <li>シャドウを測光 → 数値ではなく Zone III マークに合わせる</li>
  <li>ダイヤル右側の絞り・SS を読む → そのまま撮影設定</li>
  <li>ハイライトを測光 → 何ゾーンに落ちるか即座に確認</li>
</ol>
<p>Zone Notes アプリは同じ機能をデジタルで実現しているが、<strong>Zone Dial シールは物理的な操作の確実性</strong>として現場で重宝される。</p>
`,
    },
    {
      heading: '正確な測光のコツ',
      body: `
<ul>
  <li><strong>同じ距離・同じ角度で測る</strong> — 自分が立つ位置と被写体への入射角が、撮影位置と一致するよう移動して測光</li>
  <li><strong>1° の視野は意外と狭い</strong> — 遠くの小さな対象 (例: 50m 先の岩の影) は確実にスポット内に収まるが、近接被写体は周囲が混じることに注意</li>
  <li><strong>偏光板や偏光が強い空</strong> — フィルターを使うなら、フィルターを通して測光する (or 補正値で計算)</li>
  <li><strong>ハイライトは必ず鏡面反射でないか確認</strong> — 鏡面反射 (太陽の反射等) は数 EV 過剰、テクスチャ部分を測る</li>
  <li><strong>暗順応に時間をかける</strong> — 長時間明所にいた後、暗部を測ると目が騙されやすい</li>
</ul>
`,
    },
    {
      heading: 'スマートフォン代替アプリ',
      body: `
<p>Sekonic, Lumu Light Meter, myLightMeter Pro 等のアプリも存在するが、ゾーンシステム実践には <strong>物理スポットメーターが推奨</strong>。理由:</p>
<ul>
  <li>iPhone カメラの実視野は広く、スポット測光精度が低い</li>
  <li>露出計校正が機種依存、ロット間ばらつき</li>
  <li>明るい屋外では画面が見づらく操作困難</li>
  <li>バッテリー消費</li>
</ul>
<p>Zone Notes アプリは <strong>物理メーターで測った値を入力する電子ノート</strong>として設計されており、測光自体はメーターに任せる。これが最も再現性が高い。</p>
`,
    },
  ],
});

// ============================================================
// 14. 入射光式 vs 反射光式測光
// ============================================================
ARTICLES.push({
  id: 'incident-reflected',
  chapter: '14',
  title: '入射光式 vs 反射光式測光',
  summary: '2 つの測光方式の物理的違いと、ゾーンシステムでの使い分け。なぜ ZS は反射光式か。',
  sections: [
    {
      heading: '2 つの方式',
      body: `
<svg class="article-illust" viewBox="0 0 460 200" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">INCIDENT vs REFLECTED METERING</text>

    <text x="60" y="38" text-anchor="middle" font-size="10" font-weight="500">INCIDENT (入射光式)</text>
    <circle cx="20"  cy="80" r="10" fill="#e6e6e6" stroke="#0a0a0a"/>
    <text x="20" y="100" text-anchor="middle" font-size="8">SUN</text>
    <path d="M30 80 L70 80" stroke="#0a0a0a" stroke-width="1.4"/>
    <path d="M62 76 l8 4 -8 4" stroke="#0a0a0a" stroke-width="1.4" fill="#0a0a0a"/>
    <circle cx="80"  cy="80" r="6" fill="#0a0a0a"/>
    <text x="80" y="100" text-anchor="middle" font-size="8">METER</text>
    <text x="80" y="110" text-anchor="middle" font-size="8" fill="#5a5a58">@ subject</text>
    <rect x="105" y="68" width="30" height="24" fill="#5e5e5e" stroke="#0a0a0a"/>
    <text x="120" y="100" text-anchor="middle" font-size="8">SUBJECT</text>
    <text x="60" y="140" font-size="9" fill="#5a5a58">光源 → メーター (被写体位置)</text>
    <text x="60" y="155" font-size="9" fill="#5a5a58">→ 18% グレー想定で平均露出を提案</text>
    <text x="60" y="170" font-size="9" fill="#5a5a58">スタジオ・人物に適</text>

    <line x1="225" y1="30" x2="225" y2="180" stroke="#ededea" stroke-dasharray="3 3"/>

    <text x="340" y="38" text-anchor="middle" font-size="10" font-weight="500">REFLECTED (反射光式)</text>
    <circle cx="260" cy="60" r="10" fill="#e6e6e6" stroke="#0a0a0a"/>
    <text x="260" y="50" text-anchor="middle" font-size="8">SUN</text>
    <path d="M270 70 L370 90" stroke="#0a0a0a" stroke-width="1.4"/>
    <rect x="370" y="80" width="30" height="24" fill="#5e5e5e" stroke="#0a0a0a"/>
    <text x="385" y="120" text-anchor="middle" font-size="8">SUBJECT</text>
    <path d="M365 95 L320 100" stroke="#0a0a0a" stroke-width="1.4"/>
    <path d="M325 96 l-5 3 5 3" stroke="#0a0a0a" stroke-width="1.4" fill="#0a0a0a"/>
    <circle cx="310" cy="100" r="6" fill="#0a0a0a"/>
    <text x="310" y="120" text-anchor="middle" font-size="8">METER</text>
    <text x="310" y="130" text-anchor="middle" font-size="8" fill="#5a5a58">@ camera</text>
    <text x="240" y="155" font-size="9" fill="#5a5a58">被写体反射光 → メーター (撮影位置)</text>
    <text x="240" y="170" font-size="9" fill="#5a5a58">1° スポットで個別測光 → ZS で必須</text>
  </g>
</svg>
<table class="article-table">
  <tr><th></th><th>入射光式 (Incident)</th><th>反射光式 (Reflected)</th></tr>
  <tr><td>測るもの</td><td>被写体に降り注ぐ光の強さ</td><td>被写体から反射する光の強さ</td></tr>
  <tr><td>機材</td><td>白色半球ドーム付きメーター</td><td>1°〜30°視野メーター</td></tr>
  <tr><td>立つ位置</td><td>被写体の位置 (光源を向ける)</td><td>カメラ位置 (被写体を向ける)</td></tr>
  <tr><td>校正基準</td><td>18% グレー反射想定の入射光量</td><td>18% グレーへの変換</td></tr>
  <tr><td>得意</td><td>スタジオ、人物、安定したライティング</td><td>風景、複雑なシーン、近寄れない被写体</td></tr>
</table>
`,
    },
    {
      heading: 'なぜゾーンシステムは反射光式か',
      body: `
<p>入射光式は <strong>「被写体の反射率を 18% と仮定して、平均的な露出を提案する」</strong>方式。シーン全体の DR や、特定部位の輝度差を読み取れない。</p>
<p>ゾーンシステムは <strong>「シーンの各部分が反射する光の輝度を個別に測定し、配置と落ちる位置を決める」</strong>手法。これは反射光式 (特にスポット式) でしか実現できない。</p>
<p class="article-quote">"You cannot place anything if you don't know what it reflects."<br/>— ZS 講師 Fred Picker (近代ゾーンシステム再普及者)</p>
`,
    },
    {
      heading: '入射光式が有効なシーン',
      body: `
<ul>
  <li><strong>スタジオ撮影</strong> — モデリングライトの強度測定</li>
  <li><strong>結婚式・イベント</strong> — 急変するシーンで素早く露出決定</li>
  <li><strong>カラーフィルム</strong> — リバーサルでハイライトを飛ばさないため、入射光ベースが安全</li>
  <li><strong>同一光源下の連続撮影</strong> — 一度測れば繰り返し使える</li>
</ul>
<p>これらのシーンでも、ZS 的な精密な配置は得られないが、簡便さと速度が優先される場面では有効。</p>
`,
    },
    {
      heading: 'ハイブリッド運用',
      body: `
<p>多くの ZS 実践者は両方を持ち歩く:</p>
<ol>
  <li>入射光メーター → シーン全体の「ベース露出」確認</li>
  <li>スポットメーター → 主要シャドウ・ハイライトの個別測定</li>
  <li>2 つの結果を比較 → シーン全体の理解を深める</li>
</ol>
<p>Sekonic L-858D 等の二刀流モデルは、入射光と反射光を 1 台で切り替えられる。ZS だけなら Pentax Spotmeter V で十分。</p>
`,
    },
    {
      heading: '撮影前の習慣',
      body: `
<p>レベルアップのコツ: <strong>測光する前に「目で見て予測」する習慣</strong>をつける。例えば「あの白壁は EV 14 くらい」と当てておき、メーターで答え合わせする。これを 1000 シーン繰り返すと、メーターなしで露出を予測できるようになる (アダムスはこの域に達していた)。</p>
<p>Zone Notes の ANALYTICS で過去の測光値統計を見ると、自分の予測精度が向上していくのを実感できる。</p>
`,
    },
  ],
});

// ============================================================
// 15. レンズ選択と画角設計
// ============================================================
ARTICLES.push({
  id: 'lens-choice',
  chapter: '15',
  title: 'レンズ選択と画角設計',
  summary: '中判フォーマット別の標準・広角・望遠焦点距離、35mm 換算、選択基準。被写界深度との関係。',
  sections: [
    {
      heading: 'フォーマット別の「標準レンズ」',
      body: `
<p>「標準レンズ」とはフィルム対角線とほぼ同じ焦点距離のレンズ。視野角約 47° で、人間の中心視野に近い自然な遠近感が得られる。</p>
<svg class="article-illust" viewBox="0 0 460 220" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">ANGLE OF VIEW (35mm equivalent)</text>
    <circle cx="230" cy="180" r="4" fill="#0a0a0a"/>
    <text x="230" y="200" text-anchor="middle" font-size="9" fill="#5a5a58">camera</text>

    <path d="M230 180 L60 30 A 200 200 0 0 1 400 30 Z" fill="#ededea" opacity="0.4" stroke="#5a5a58" stroke-width="1" stroke-dasharray="3 3"/>
    <text x="100" y="40" font-size="10" fill="#5a5a58">超広角 21mm</text>

    <path d="M230 180 L130 50 A 130 130 0 0 1 330 50 Z" fill="#cfcfcf" opacity="0.5" stroke="#5a5a58" stroke-width="1" stroke-dasharray="3 3"/>
    <text x="135" y="65" font-size="10" fill="#5a5a58">広角 35mm</text>

    <path d="M230 180 L160 80 A 100 100 0 0 1 300 80 Z" fill="#9a9a9a" opacity="0.6" stroke="#0a0a0a" stroke-width="1.4"/>
    <text x="190" y="90" font-size="10" fill="#0a0a0a">標準 50mm</text>

    <path d="M230 180 L195 100 A 50 50 0 0 1 265 100 Z" fill="#444444" opacity="0.7" stroke="#0a0a0a" stroke-width="1.4"/>
    <text x="245" y="118" font-size="10" fill="#ffffff">中望遠 105mm</text>

    <path d="M230 180 L215 130 A 20 20 0 0 1 245 130 Z" fill="#0a0a0a" stroke="#0a0a0a"/>
    <text x="270" y="155" font-size="10" fill="#0a0a0a">望遠 200mm</text>

    <text x="10" y="216" font-size="11" fill="#5a5a58">各焦点距離の画角を扇形で重ねた図 (人の視野は約 50° = 標準レンズ相当)</text>
  </g>
</svg>
<table class="article-table">
  <tr><th>フォーマット</th><th>対角線 (mm)</th><th>標準焦点距離</th><th>35mm 換算</th></tr>
  <tr><td>645</td><td>69.5</td><td>75-80mm</td><td>≈50mm</td></tr>
  <tr><td>6×6</td><td>79.2</td><td>80mm</td><td>≈44mm</td></tr>
  <tr><td>6×7</td><td>89.6</td><td>90-105mm</td><td>≈49-57mm</td></tr>
  <tr><td>6×9</td><td>101.0</td><td>105-110mm</td><td>≈51-53mm</td></tr>
  <tr><td>4×5</td><td>153</td><td>150mm</td><td>≈47mm</td></tr>
  <tr><td>8×10</td><td>305</td><td>300mm</td><td>≈47mm</td></tr>
</table>
`,
    },
    {
      heading: '広角・望遠の選び方',
      body: `
<table class="article-table">
  <tr><th>用途</th><th>645</th><th>6×7</th><th>4×5</th></tr>
  <tr><td>超広角</td><td>35-45mm</td><td>50-55mm</td><td>75-90mm</td></tr>
  <tr><td>広角</td><td>55-65mm</td><td>65-75mm</td><td>110-135mm</td></tr>
  <tr><td>標準</td><td>75-80mm</td><td>90-105mm</td><td>150mm</td></tr>
  <tr><td>中望遠</td><td>110-150mm</td><td>135-165mm</td><td>210-240mm</td></tr>
  <tr><td>望遠</td><td>200-300mm</td><td>250-400mm</td><td>300-450mm</td></tr>
</table>
<p>中判フォーマットでは <strong>標準 + 広角 1 本</strong>から始め、必要に応じて望遠を追加するのが現実的。風景撮影なら広角 + 中望遠の組合せが頻用される。</p>
`,
    },
    {
      heading: '被写界深度の特性',
      body: `
<p>同じ画角 (35mm 換算) で比較した場合、<strong>大きいフォーマットほど被写界深度が浅い</strong>。これは中判で美しいボケが得られる理由でもあり、深い被写界深度を確保するには絞り込みが必要な理由でもある。</p>
<table class="article-table">
  <tr><th>フォーマット</th><th>同等被写界深度を得る絞り (vs 35mm)</th></tr>
  <tr><td>645</td><td>+1 段絞る</td></tr>
  <tr><td>6×6</td><td>+1.3 段絞る</td></tr>
  <tr><td>6×7</td><td>+1.5 段絞る</td></tr>
  <tr><td>4×5</td><td>+3.5 段絞る</td></tr>
</table>
<p>例: 35mm で F8 と同じ被写界深度を 4×5 で得るには F32 必要 → SS が大幅に長くなり、相反則不軌補正も必要。これが大判が三脚必須たる物理的理由。</p>
`,
    },
    {
      heading: 'レンズの特性とゾーン',
      body: `
<p>古典的な ZS 教則では「レンズはコントラスト・解像度に影響するだけで、ZS とは直接関係ない」とされてきた。実際には:</p>
<ul>
  <li><strong>コーティング</strong> — マルチコート (現代レンズ) は逆光時のフレアが少なく、シャドウが沈みにくい</li>
  <li><strong>レンズフード</strong> — フレア防止で実質的にコントラストを上げる効果</li>
  <li><strong>絞りの形</strong> — 絞り羽根の数で点光源の表現が変わる</li>
  <li><strong>フィルター枠</strong> — フィルター装着のしやすさが現場での運用に直結</li>
</ul>
<p>古いレンズ (Tessar、Sonnar 等) はコーティングが弱く、シャドウが浮きやすい傾向。ZS で意図したシャドウ III が IV に浮く場合、レンズフード追加 or 撮影時に手で遮光することで補正可能。</p>
`,
    },
  ],
});

// ============================================================
// 16. シートフィルムでのゾーンシステム
// ============================================================
ARTICLES.push({
  id: 'sheet-film',
  chapter: '16',
  title: 'シートフィルムでのゾーンシステム',
  summary: '4×5 / 5×7 / 8×10 大判の運用。シートごとに個別現像できる ZS の本来の領域。',
  sections: [
    {
      heading: 'シート vs ロールの本質的な違い',
      body: `
<figure class="article-figure">
  <img src="/learn-images/16-large-format-camera.png" alt="4×5 大判ビューカメラ" loading="lazy" />
  <figcaption>4×5 大判ビューカメラ — ZS の本来の領域</figcaption>
</figure>
<p>ロールフィルムは 1 本に 8〜36 コマあり、<strong>必ず一括現像</strong>される。N+1 のシーンと N−1 のシーンが混在しても、最終的には妥協した時間で現像せざるを得ない。</p>
<p>シートフィルムなら <strong>1 枚ずつ別の現像処理</strong>が可能。これがゾーンシステムが大判で生まれ、大判で完成された理由。</p>
`,
    },
    {
      heading: 'シートホルダーへの記録',
      body: `
<figure class="article-figure">
  <img src="/learn-images/16-sheet-holder.png" alt="シートフィルムホルダー" loading="lazy" />
  <figcaption>4×5 シートホルダー — 遮光板の縁に N / N+1 / N−1 と鉛筆で書き込む</figcaption>
</figure>
<p>大判シートホルダーには 2 枚のフィルムが入る (表/裏)。各シートに対する現像方針を記録する慣習:</p>
<ul>
  <li><strong>遮光紙の縁にメモ</strong> — 「N」「N+1」「N−2」など鉛筆で記入</li>
  <li><strong>シートホルダーの ID 番号</strong> — 撮影順を記録 (Holder #5 Side B = N−1 等)</li>
  <li><strong>撮影ノート</strong> — Zone Notes に GPS・日時・シーン名と紐付けて保存</li>
</ul>
<p>暗室でフィルムをタンクに装填する際、メモを見て同じ現像方針のシートをまとめて処理する。これにより 1 シーンずつ最適化された結果が得られる。</p>
`,
    },
    {
      heading: 'シートフィルム現像の方法',
      body: `
<table class="article-table">
  <tr><th>方法</th><th>容量</th><th>特徴</th></tr>
  <tr><td>BTZS チューブ</td><td>1 枚/チューブ</td><td>個別現像最適、少量で経済的、攪拌均一</td></tr>
  <tr><td>Jobo CPP/CPA</td><td>4-12 枚</td><td>ローテーション処理、温度管理優秀</td></tr>
  <tr><td>SP-445 タンク</td><td>4 枚</td><td>シンプル、初心者向け</td></tr>
  <tr><td>トレイ現像</td><td>同時 4-6 枚</td><td>暗室作業必須、視覚的に進行確認可能</td></tr>
  <tr><td>Combi Plan</td><td>6 枚</td><td>定番、攪拌に注意 (筋ムラ)</td></tr>
</table>
<p>個別現像を最も効率的に行うなら <strong>BTZS チューブ</strong>。1 枚 100ml 程度の現像液で済み、N / N+1 / N−1 を並行処理できる。</p>
`,
    },
    {
      heading: '4×5 撮影フロー',
      body: `
<ol>
  <li>三脚に 4×5 カメラを据える</li>
  <li>ピントグラスで構図確認、ピント調整</li>
  <li><strong>ピントグラスを外す前にスポット測光</strong> (重要部位 5-10 点)</li>
  <li>露出 + 現像方針を Zone Notes に記録</li>
  <li>シートホルダーをセット、遮光板を抜く</li>
  <li>シャッターチャージ → ケーブルレリーズ撮影</li>
  <li>遮光板を戻す → ホルダーを取り外し</li>
  <li>遮光板の縁に「N+1」等を鉛筆で記入</li>
  <li>2 枚目を撮影 (バックアップ or ブラケット)</li>
</ol>
`,
    },
    {
      heading: 'ムーブメントと ZS の融合',
      body: `
<p>大判カメラは <strong>ライズ・フォール・ティルト・スイング・シフト</strong>といった独自のレンズ・フィルムムーブメントを持つ。これらは ZS と直接関係ないが、相乗効果がある:</p>
<svg class="article-illust" viewBox="0 0 460 220" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">VIEW CAMERA MOVEMENTS</text>

    <g transform="translate(20,30)">
      <rect x="0" y="20" width="60" height="40" fill="#ededea" stroke="#0a0a0a"/>
      <line x1="30" y1="20" x2="30" y2="60" stroke="#5a5a58" stroke-dasharray="2 2"/>
      <path d="M30 5 L30 18" stroke="#0a0a0a" stroke-width="1.4"/>
      <path d="M27 10 l3 -5 3 5" stroke="#0a0a0a" stroke-width="1.4" fill="#0a0a0a"/>
      <text x="30" y="80" text-anchor="middle" font-size="10" font-weight="500">RISE</text>
      <text x="30" y="95" text-anchor="middle" font-size="9" fill="#5a5a58">建築の歪み補正</text>
    </g>

    <g transform="translate(120,30)">
      <rect x="0" y="20" width="60" height="40" fill="#ededea" stroke="#0a0a0a"/>
      <line x1="0" y1="40" x2="60" y2="40" stroke="#5a5a58" stroke-dasharray="2 2"/>
      <path d="M-3 25 L-3 55" stroke="#0a0a0a" stroke-width="1.4"/>
      <path d="M-7 30 l4 -5 4 5" stroke="#0a0a0a" stroke-width="1.4" fill="#0a0a0a"/>
      <path d="M63 25 L63 55" stroke="#0a0a0a" stroke-width="1.4"/>
      <path d="M59 50 l4 5 4 -5" stroke="#0a0a0a" stroke-width="1.4" fill="#0a0a0a"/>
      <text x="30" y="80" text-anchor="middle" font-size="10" font-weight="500">SHIFT</text>
      <text x="30" y="95" text-anchor="middle" font-size="9" fill="#5a5a58">構図の平行移動</text>
    </g>

    <g transform="translate(220,30)">
      <path d="M0 60 L60 30" stroke="#0a0a0a" stroke-width="1.4" fill="#ededea"/>
      <path d="M0 60 L60 30 L60 35 L0 65 Z" fill="#ededea" stroke="#0a0a0a"/>
      <line x1="30" y1="20" x2="30" y2="70" stroke="#5a5a58" stroke-dasharray="2 2"/>
      <path d="M55 15 a 15 15 0 0 0 -10 15" stroke="#0a0a0a" stroke-width="1.4" fill="none"/>
      <text x="30" y="80" text-anchor="middle" font-size="10" font-weight="500">TILT</text>
      <text x="30" y="95" text-anchor="middle" font-size="9" fill="#5a5a58">前後ピント拡張</text>
    </g>

    <g transform="translate(320,30)">
      <path d="M5 25 L55 35 L55 55 L5 65 Z" fill="#ededea" stroke="#0a0a0a"/>
      <line x1="30" y1="20" x2="30" y2="70" stroke="#5a5a58" stroke-dasharray="2 2"/>
      <path d="M20 18 a 12 12 0 0 1 15 0" stroke="#0a0a0a" stroke-width="1.4" fill="none"/>
      <text x="30" y="80" text-anchor="middle" font-size="10" font-weight="500">SWING</text>
      <text x="30" y="95" text-anchor="middle" font-size="9" fill="#5a5a58">左右ピント拡張</text>
    </g>

    <text x="10" y="140" font-size="11" font-weight="500">ZS との相乗効果</text>
    <text x="10" y="158" font-size="11" fill="#5a5a58">ティルト → シャインプルーフで深い被写界深度 → F22 まで絞らず短 SS → 相反則回避</text>
    <text x="10" y="176" font-size="11" fill="#5a5a58">シフト → ハイライトを画面外に追い出して高 DR シーンを N 現像で扱える</text>
    <text x="10" y="194" font-size="11" fill="#5a5a58">ライズ → 建築のコーナー暗部 (ゾーン I 化) を回避</text>
  </g>
</svg>
<ul>
  <li><strong>ティルト</strong> — シャインプルーフの法則で被写界深度を実質的に拡張、F22 まで絞らずとも前後ピントが得られる → SS 短縮 → 相反則回避</li>
  <li><strong>シフト</strong> — 構図の微調整でハイライトを画面外に追い出せる → 高 DR シーンを N 現像で扱える</li>
  <li><strong>ライズ</strong> — 建築撮影で歪み補正、コーナーのゾーン I 化を回避</li>
</ul>
`,
    },
  ],
});

// ============================================================
// 17. ハイライト・シャドウのディテール救済
// ============================================================
ARTICLES.push({
  id: 'detail-recovery',
  chapter: '17',
  title: 'ハイライト・シャドウのディテール救済',
  summary: '撮影で守りきれなかった階調を、現像・プリント工程で取り戻す技術。',
  sections: [
    {
      heading: 'ハイライト救済',
      body: `
<svg class="article-illust" viewBox="0 0 460 180" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">WATER BATH DEVELOPMENT</text>
    <g>
      <rect x="40"  y="40" width="120" height="60" fill="#444444" stroke="#0a0a0a"/>
      <text x="100" y="70" text-anchor="middle" font-size="10" fill="#ffffff">DEVELOPER</text>
      <text x="100" y="86" text-anchor="middle" font-size="9" fill="#cfcfcf">D-76 1:1</text>
      <text x="100" y="116" text-anchor="middle" font-size="9" fill="#5a5a58">活性 (現像)</text>
    </g>
    <g>
      <path d="M170 70 L230 70" stroke="#0a0a0a" stroke-width="1.4"/>
      <path d="M222 66 l8 4 -8 4" stroke="#0a0a0a" stroke-width="1.4" fill="#0a0a0a"/>
      <text x="200" y="55" text-anchor="middle" font-size="9" fill="#5a5a58">30秒</text>
      <path d="M230 90 L170 90" stroke="#0a0a0a" stroke-width="1.4"/>
      <path d="M178 86 l-8 4 8 4" stroke="#0a0a0a" stroke-width="1.4" fill="#0a0a0a"/>
      <text x="200" y="105" text-anchor="middle" font-size="9" fill="#5a5a58">30秒</text>
    </g>
    <g>
      <rect x="240" y="40" width="120" height="60" fill="#e6e6e6" stroke="#0a0a0a"/>
      <text x="300" y="70" text-anchor="middle" font-size="10">WATER BATH</text>
      <text x="300" y="86" text-anchor="middle" font-size="9" fill="#5a5a58">純水 20°C</text>
      <text x="300" y="116" text-anchor="middle" font-size="9" fill="#5a5a58">休止 (拡散)</text>
    </g>
    <text x="10" y="148" font-size="11" fill="#5a5a58">交互浸漬で、活性層が薄いシャドウは現像続行、ハイライトは現像停止</text>
    <text x="10" y="164" font-size="11" fill="#5a5a58">高 DR シーンで N−2 圧縮よりさらに強力にハイライトを抑える伝統技法</text>
  </g>
</svg>
<table class="article-table">
  <tr><th>失敗</th><th>救済工程</th><th>方法</th></tr>
  <tr><td>ハイライトが完全に飛ぶ (>ゾーン X)</td><td>不可能</td><td>撮影やり直し</td></tr>
  <tr><td>ハイライトがゾーン IX で硬い</td><td>現像</td><td>N−1 圧縮 (時間 ×0.7)</td></tr>
  <tr><td>ネガではあるが、プリントで飛ぶ</td><td>プリント</td><td>焼き込み (Burn) で +50〜100% 露光</td></tr>
  <tr><td>VC 印画紙でハイライトのみ硬い</td><td>プリント</td><td>スプリットグレード、Grade 0 主体で焼く</td></tr>
</table>
<p>最強の救済は <strong>水浴現像 (Water Bath)</strong>: ハイライトが飽和しそうな現像液から、純水浴に 30秒移して反応を中断、ハイライトを抑える伝統技法。</p>
`,
    },
    {
      heading: 'シャドウ救済',
      body: `
<table class="article-table">
  <tr><th>失敗</th><th>救済工程</th><th>方法</th></tr>
  <tr><td>シャドウが完全に黒 (=ゾーン 0)</td><td>不可能</td><td>露光時に不足、再撮影</td></tr>
  <tr><td>シャドウがゾーン II で薄い</td><td>現像</td><td>N+1 拡張 (時間 ×1.4)、ただしハイライト膨れに注意</td></tr>
  <tr><td>ネガに像はあるが暗い</td><td>プリント</td><td>覆い焼き (Dodge) で −30〜50% 露光</td></tr>
  <tr><td>シャドウのコントラスト不足</td><td>プリント</td><td>Grade 4-5 で部分的に焼く (スプリット)</td></tr>
</table>
<p>シャドウ救済の根本: <strong>シャドウは露出で決まる</strong>。後工程での救済は限定的なので、撮影時に EI を 1/3 段下げる (露出を増やす) 予防策が最も有効。</p>
`,
    },
    {
      heading: 'プリント時の Dodge & Burn 設計',
      body: `
<svg class="article-illust" viewBox="0 0 460 240" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">DODGE/BURN MAP (annotation on straight print)</text>
    <g transform="translate(10,28)">
      <rect x="0" y="0" width="280" height="180" fill="#5e5e5e" stroke="#0a0a0a"/>
      <rect x="20" y="20" width="80" height="60" fill="#9a9a9a"/>
      <text x="60" y="55" text-anchor="middle" font-size="9" fill="#0a0a0a">SKY</text>
      <text x="60" y="92" text-anchor="middle" font-size="8" fill="#ffffff">+50% burn</text>
      <path d="M60 76 L60 100" stroke="#ffffff" stroke-width="1"/>
      <path d="M55 95 l5 5 5 -5" stroke="#ffffff" stroke-width="1" fill="none"/>

      <ellipse cx="180" cy="50" rx="40" ry="20" fill="#cfcfcf"/>
      <text x="180" y="55" text-anchor="middle" font-size="9" fill="#0a0a0a">CLOUDS</text>
      <text x="180" y="86" text-anchor="middle" font-size="8" fill="#ffffff">+30% burn G0</text>

      <rect x="20" y="110" width="120" height="50" fill="#2d2d2d"/>
      <text x="80" y="140" text-anchor="middle" font-size="9" fill="#ffffff">SHADOW</text>
      <text x="80" y="168" text-anchor="middle" font-size="8" fill="#ffffff">−30% dodge</text>

      <rect x="160" y="120" width="100" height="40" fill="#444444"/>
      <text x="210" y="145" text-anchor="middle" font-size="9" fill="#ffffff">building wall</text>
      <text x="210" y="172" text-anchor="middle" font-size="8" fill="#ffffff">straight</text>
    </g>
    <g transform="translate(310,28)">
      <text x="0" y="14" font-size="10" font-weight="500">マッピング手順</text>
      <text x="0" y="32" font-size="10" fill="#5a5a58">1. ストレート焼き</text>
      <text x="0" y="46" font-size="10" fill="#5a5a58">2. ライトテーブルで観察</text>
      <text x="0" y="60" font-size="10" fill="#5a5a58">3. マーカーで Dodge/Burn を記録</text>
      <text x="0" y="74" font-size="10" fill="#5a5a58">4. 本番焼きで実行</text>
      <text x="0" y="100" font-size="10" font-weight="500">マップ表記の規則</text>
      <text x="0" y="118" font-size="10" fill="#5a5a58">+N% = N% 追加焼き込み</text>
      <text x="0" y="132" font-size="10" fill="#5a5a58">−N% = N% 遮光 (覆い焼き)</text>
      <text x="0" y="146" font-size="10" fill="#5a5a58">G0 / G5 = グレード指定</text>
    </g>
  </g>
</svg>
<p>救済プリントを系統的に行うため、ストレートプリントを焼いた後に「どこを何 % 増減させるか」をマップ化する:</p>
<ol>
  <li>ストレートプリントをライトテーブルに置く</li>
  <li>マーカーで "−30%" "+50% Grade 0" などを書き込む</li>
  <li>各エリアごとに Dodge / Burn の動きを練習</li>
  <li>本番プリントで実行</li>
  <li>結果を Zone Notes の REFLECTION (printing) に記録</li>
</ol>
<p>同じネガから何枚も焼いて Dodge/Burn を改善していく。アダムスは《Moonrise》を 1000 枚以上焼いたとされる。</p>
`,
    },
    {
      heading: '化学的救済 (現像後)',
      body: `
<p>すでに現像してしまったネガでも、化学処理で部分的に修正可能:</p>
<ul>
  <li><strong>強化液 (Intensifier)</strong> — クロム・銅・水銀ベースの薬品でネガ密度を上げる、薄いネガの救済</li>
  <li><strong>減力液 (Reducer)</strong> — Farmer's Reducer (赤血塩 + チオ硫酸) で密度を下げる、過剰ネガ救済</li>
  <li><strong>セレン調色</strong> — シャドウ部の密度を上げる効果がある (1:9 程度)</li>
</ul>
<p>これらは取り扱いに毒性あり。現代では <strong>RAW 現像での救済</strong>が一般化しているため、ネガをスキャンしてデジタルで補正する方が安全で精密。</p>
`,
    },
  ],
});

// ============================================================
// 18. ローキー / ハイキー表現
// ============================================================
ARTICLES.push({
  id: 'low-key-high-key',
  chapter: '18',
  title: 'ローキー / ハイキー表現',
  summary: 'ゾーン分布の偏りで作る情緒的な表現。重厚なローキー、繊細なハイキー、それぞれの設計法。',
  sections: [
    {
      heading: 'キー (Key) とは',
      body: `
<div style="display:flex; gap:6px; margin-bottom:14px;">
  <figure class="article-figure" style="flex:1; margin:0;">
    <img src="/learn-images/18-low-key-example.png" alt="ローキー例" loading="lazy" />
    <figcaption>Low Key</figcaption>
  </figure>
  <figure class="article-figure" style="flex:1; margin:0;">
    <img src="/learn-images/18-middle-key-example.png" alt="ミドルキー例" loading="lazy" />
    <figcaption>Middle Key</figcaption>
  </figure>
  <figure class="article-figure" style="flex:1; margin:0;">
    <img src="/learn-images/18-high-key-example.png" alt="ハイキー例" loading="lazy" />
    <figcaption>High Key</figcaption>
  </figure>
</div>
<p>「キー」は写真全体のトーンの重心を指す:</p>
<svg class="article-illust" viewBox="0 0 460 250" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">LOW KEY</text>
    <g transform="translate(10,22)">
      <rect x="0"   y="36" width="13" height="20" fill="#000000"/>
      <rect x="13"  y="20" width="13" height="36" fill="#1a1a1a"/>
      <rect x="26"  y="6"  width="13" height="50" fill="#2d2d2d"/>
      <rect x="39"  y="0"  width="13" height="56" fill="#444444"/>
      <rect x="52"  y="14" width="13" height="42" fill="#5e5e5e"/>
      <rect x="65"  y="32" width="13" height="24" fill="#808080"/>
      <rect x="78"  y="44" width="13" height="12" fill="#9a9a9a"/>
      <rect x="91"  y="50" width="13" height="6"  fill="#b5b5b5"/>
      <rect x="104" y="52" width="13" height="4"  fill="#cfcfcf"/>
      <rect x="117" y="55" width="13" height="1"  fill="#e6e6e6"/>
      <rect x="130" y="55" width="13" height="1"  fill="#ffffff" stroke="#0a0a0a" stroke-width="0.5"/>
      <line x1="0" y1="56" x2="143" y2="56" stroke="#0a0a0a"/>
    </g>
    <text x="10" y="100" font-size="11">重心: <tspan font-weight="500">Zone II-IV</tspan></text>
    <text x="10" y="116" font-size="9" fill="#5a5a58">夜景・男性肖像・重厚・森山的</text>

    <text x="160" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">MIDDLE KEY</text>
    <g transform="translate(160,22)">
      <rect x="0"   y="48" width="13" height="8"  fill="#000000"/>
      <rect x="13"  y="40" width="13" height="16" fill="#1a1a1a"/>
      <rect x="26"  y="28" width="13" height="28" fill="#2d2d2d"/>
      <rect x="39"  y="14" width="13" height="42" fill="#444444"/>
      <rect x="52"  y="0"  width="13" height="56" fill="#5e5e5e"/>
      <rect x="65"  y="0"  width="13" height="56" fill="#808080"/>
      <rect x="78"  y="6"  width="13" height="50" fill="#9a9a9a"/>
      <rect x="91"  y="14" width="13" height="42" fill="#b5b5b5"/>
      <rect x="104" y="28" width="13" height="28" fill="#cfcfcf"/>
      <rect x="117" y="40" width="13" height="16" fill="#e6e6e6"/>
      <rect x="130" y="48" width="13" height="8"  fill="#ffffff" stroke="#0a0a0a" stroke-width="0.5"/>
      <line x1="0" y1="56" x2="143" y2="56" stroke="#0a0a0a"/>
    </g>
    <text x="160" y="100" font-size="11">重心: <tspan font-weight="500">Zone IV-VI</tspan></text>
    <text x="160" y="116" font-size="9" fill="#5a5a58">標準・報道・バランス</text>

    <text x="310" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">HIGH KEY</text>
    <g transform="translate(310,22)">
      <rect x="0"   y="55" width="13" height="1"  fill="#000000"/>
      <rect x="13"  y="55" width="13" height="1"  fill="#1a1a1a"/>
      <rect x="26"  y="52" width="13" height="4"  fill="#2d2d2d"/>
      <rect x="39"  y="50" width="13" height="6"  fill="#444444"/>
      <rect x="52"  y="44" width="13" height="12" fill="#5e5e5e"/>
      <rect x="65"  y="32" width="13" height="24" fill="#808080"/>
      <rect x="78"  y="14" width="13" height="42" fill="#9a9a9a"/>
      <rect x="91"  y="0"  width="13" height="56" fill="#b5b5b5"/>
      <rect x="104" y="6"  width="13" height="50" fill="#cfcfcf"/>
      <rect x="117" y="20" width="13" height="36" fill="#e6e6e6"/>
      <rect x="130" y="36" width="13" height="20" fill="#ffffff" stroke="#0a0a0a" stroke-width="0.5"/>
      <line x1="0" y1="56" x2="143" y2="56" stroke="#0a0a0a"/>
    </g>
    <text x="310" y="100" font-size="11">重心: <tspan font-weight="500">Zone VI-VIII</tspan></text>
    <text x="310" y="116" font-size="9" fill="#5a5a58">雪景色・女性肖像・植田的</text>

    <text x="10" y="160" font-size="11" font-weight="500">3 つのキーの作り方</text>
    <text x="10" y="180" font-size="11" fill="#5a5a58">"対比アクセント" を必ず仕込む — 全体を一方に振りつつ、対極を 1 点入れる</text>
    <text x="10" y="200" font-size="11" fill="#5a5a58">ハイキー → 暗いアクセント (II-III) を 1-2 箇所、画面が締まる</text>
    <text x="10" y="220" font-size="11" fill="#5a5a58">ローキー → 明るいアクセント (VII-VIII) を 1-2 箇所、視線誘導</text>
    <text x="10" y="240" font-size="9" fill="#5a5a58">※ 縦軸 = 各ゾーンが画面で占める面積比、横軸 = ゾーン 0 → X</text>
  </g>
</svg>
<ul>
  <li><strong>ハイキー</strong> — ゾーン VI-VIII 中心、明るく軽やか、女性ポートレート、雪景色、ハイファッション</li>
  <li><strong>ミドルキー</strong> — ゾーン IV-VI 中心、自然なバランス、ドキュメンタリー、報道</li>
  <li><strong>ローキー</strong> — ゾーン II-IV 中心、重厚で陰鬱、アンセル・アダムスの晩年作、男性ポートレート、夜景</li>
</ul>
<p>キーは <strong>露出 + 現像 + プリント全工程の協調</strong>で作る。撮影だけ、プリントだけでは作れない。</p>
`,
    },
    {
      heading: 'ハイキーの設計',
      body: `
<ol>
  <li><strong>明るい被写体を選ぶ</strong> — 雪、白い建物、ハイライト中心</li>
  <li><strong>シャドウを VI-VII に配置</strong> — 通常の III ではなく上に移動</li>
  <li><strong>露出多め</strong> — EI を 1/2-1 段下げる (シャドウを上げる)</li>
  <li><strong>現像 N または N−1</strong> — ハイライトの硬化を防ぐ</li>
  <li><strong>プリント Grade 1.5-2</strong> — 柔らかく、白を生かす</li>
  <li><strong>少ないアクセント</strong> — ゾーン II-III の小さな黒を 1-2 箇所だけ配置</li>
</ol>
<p>例: 朝霧の白樺林。樹木 (ゾーン VI-VII)、霧 (VIII-IX)、根本の暗部 (III)。アダムスの《Aspens》系。</p>
`,
    },
    {
      heading: 'ローキーの設計',
      body: `
<ol>
  <li><strong>暗い被写体を選ぶ</strong> — 影主体、夜景、暗い室内、深い森</li>
  <li><strong>ハイライトを V-VI に配置</strong> — VIII ではなく抑える</li>
  <li><strong>露出ぎりぎり</strong> — シャドウは II-III に置き、深い黒を作る</li>
  <li><strong>現像 N または N+1</strong> — シャドウのコントラストを保つ</li>
  <li><strong>プリント Grade 3-4</strong> — 強いコントラスト、深い黒</li>
  <li><strong>少ないアクセント</strong> — ゾーン VII-VIII の小さな白を 1-2 箇所</li>
</ol>
<p>例: 夜の街灯下の路地。建物の影 (II-III)、街灯の周辺 (V-VI)、街灯本体 (VIII)。森山大道のストリート系。</p>
`,
    },
    {
      heading: 'よくある失敗',
      body: `
<table class="article-table">
  <tr><th>意図</th><th>失敗</th><th>原因</th></tr>
  <tr><td>ハイキー</td><td>白く飛んだだけ、情報なし</td><td>ハイライトを IX 以上に置いた</td></tr>
  <tr><td>ハイキー</td><td>眠い、印象薄い</td><td>アクセントの黒がない、全体が中間</td></tr>
  <tr><td>ローキー</td><td>真っ黒、被写体が見えない</td><td>シャドウを 0-I に置いた</td></tr>
  <tr><td>ローキー</td><td>暗いだけ、迫力なし</td><td>明部のアクセント (VII-VIII) がない</td></tr>
</table>
<p>キーを作るには、<strong>「2-3 段の対比アクセント」</strong>を必ず仕込む。全体を一方向に振りつつ、対極を 1 点入れることで画面が締まる。</p>
`,
    },
  ],
});

// ============================================================
// 19. シーン別アプローチ
// ============================================================
ARTICLES.push({
  id: 'scene-approach',
  chapter: '19',
  title: 'シーン別アプローチ',
  summary: '風景・人物・建築・夜景・ストリート — シーン別の典型的なゾーン配置と注意点。',
  sections: [
    {
      heading: '風景写真',
      body: `
<figure class="article-figure">
  <img src="/learn-images/19-landscape-shooting.png" alt="朝焼けの風景撮影" loading="lazy" />
  <figcaption>4×5 ビューカメラを構えてピントグラスで構図確認、夜明けの霧と山</figcaption>
</figure>
<table class="article-table">
  <tr><th>要素</th><th>典型ゾーン配置</th></tr>
  <tr><td>影の中の岩</td><td>III</td></tr>
  <tr><td>緑の葉 (順光)</td><td>IV-V</td></tr>
  <tr><td>緑の葉 (赤フィルター使用時)</td><td>II-III</td></tr>
  <tr><td>青空 (フィルターなし)</td><td>V-VI</td></tr>
  <tr><td>青空 (黄 → 赤)</td><td>IV → III → II</td></tr>
  <tr><td>白い雲</td><td>VII-VIII</td></tr>
  <tr><td>雪 (順光)</td><td>VIII</td></tr>
  <tr><td>雪 (影)</td><td>VI-VII</td></tr>
  <tr><td>水面 (反射)</td><td>シーンによる、要測光</td></tr>
</table>
<p><strong>典型 DR は 6-7 段</strong>、N または N−1 で扱える範囲。フィルターで空を抑えるかが構図の決め手。</p>
`,
    },
    {
      heading: '人物 (屋外)',
      body: `
<figure class="article-figure">
  <img src="/learn-images/19-scene-portrait.png" alt="人物撮影シーン" loading="lazy" />
  <figcaption>木陰の柔らかい光 — 肌の質感が美しく出る</figcaption>
</figure>
<ul>
  <li><strong>白人系の肌 (順光)</strong> → ゾーン VI に配置 (= 測光から +1 EV)</li>
  <li><strong>白人系の肌 (日陰)</strong> → ゾーン V (= 測光通り)</li>
  <li><strong>東アジア系の肌 (順光)</strong> → ゾーン V-VI</li>
  <li><strong>濃褐色の肌 (順光)</strong> → ゾーン IV-V</li>
  <li><strong>髪 (黒髪)</strong> → ゾーン II-III</li>
  <li><strong>白いシャツ</strong> → ゾーン VII-VIII</li>
</ul>
<p>人物撮影では、肌の質感が最重要。<strong>ハイライトでなく、頬の中間調を測って配置を決める</strong>のが基本。露出計の TTL や入射光式が機動的。</p>
`,
    },
    {
      heading: '建築写真',
      body: `
<figure class="article-figure">
  <img src="/learn-images/19-scene-architecture.png" alt="建築撮影シーン" loading="lazy" />
  <figcaption>モダニズム建築 — 強い順光で生まれる立体的な影</figcaption>
</figure>
<ul>
  <li><strong>白いコンクリート (順光)</strong> → VII-VIII</li>
  <li><strong>レンガ (順光)</strong> → IV-V</li>
  <li><strong>影の中のレンガ</strong> → II-III</li>
  <li><strong>窓ガラス (反射)</strong> → 場合により II または X</li>
  <li><strong>金属面 (反射)</strong> → 鏡面に注意</li>
</ul>
<p>建築は <strong>面の連続性</strong>が重要。1 つの壁面の中で 2-3 段の差が出るので、面の中央を測って平均的な配置を決める。</p>
<p>逆光・側光時は影と日向で 5-7 段差が出る。<strong>レフ板的な反射体 (隣の建物の壁等) があれば、影をゾーン IV まで持ち上げられる</strong>シーンを選ぶ。</p>
`,
    },
    {
      heading: '夜景・暗所',
      body: `
<figure class="article-figure">
  <img src="/learn-images/19-scene-night.png" alt="夜景撮影シーン" loading="lazy" />
  <figcaption>雨上がりの夜 — 街灯のハロー光と濡れた路面の反射</figcaption>
</figure>
<ul>
  <li><strong>街灯本体</strong> → IX-X (ディテール無し許容)</li>
  <li><strong>街灯の周辺</strong> → V-VII</li>
  <li><strong>建物の壁 (街灯から離れる)</strong> → III-V</li>
  <li><strong>影の中</strong> → I-II</li>
  <li><strong>夜空 (月夜)</strong> → I-III</li>
  <li><strong>満月 (本体)</strong> → VIII (拡大時のディテール)</li>
</ul>
<p>夜景の DR は容易に 10 段超える。<strong>主要シャドウを II に置き、ハイライト IX に落ちる</strong>のは典型 (N−1)。Acros II + 三脚 + 長時間露光が王道。</p>
`,
    },
    {
      heading: 'ストリート (動きのあるシーン)',
      body: `
<figure class="article-figure">
  <img src="/learn-images/19-scene-street.png" alt="ストリート撮影シーン" loading="lazy" />
  <figcaption>森山大道風のアレ・ブレ・ボケ — 強烈なコントラストとグレイン</figcaption>
</figure>
<ul>
  <li>ZS は本来「測光に時間をかける」手法、ストリートと相性は悪い</li>
  <li>解法: <strong>事前に標準露出を決め打ち</strong> (晴天 EV 14-15、薄曇り 12-13)</li>
  <li>F8 + 1/250 で固定し、ピントは 3-5m に置いてゾーンフォーカス (深い被写界深度を活用)</li>
  <li>増感現像 (N+1, N+2) でラチチュード確保</li>
</ul>
<p>森山大道は HP5+ を EI 1600 で撮影、Microphen で N+2 現像という独自スタイル。シャドウは沈むが、強烈なコントラストとグレインを得る。</p>
`,
    },
  ],
});

// ============================================================
// 20. パーソナルフィルムテストの実演
// ============================================================
ARTICLES.push({
  id: 'film-test-practice',
  chapter: '20',
  title: 'パーソナルフィルムテストの実演',
  summary: 'EI と N 時間を測定する具体手順。半日で完了し、その後の全シーンの精度が一気に上がる。',
  sections: [
    {
      heading: 'なぜ自分でテストするのか',
      body: `
<p>メーカー公表の ISO・現像時間は <strong>「平均的な機材・薬品・処理条件」</strong>での値。あなたの:</p>
<ul>
  <li>シャッター速度の実精度 (機械式は ±20% ばらつく)</li>
  <li>絞りの実 F 値</li>
  <li>露出計の校正</li>
  <li>現像液の活性 (希釈や保存状態)</li>
  <li>攪拌の強さ</li>
  <li>水温 (季節差)</li>
  <li>引伸機のヘッドライトの色温度</li>
</ul>
<p>これらの組合せ誤差で、箱書き値から ±2/3 段程度ずれることは普通。テストで自分の組合せに最適化することで、毎回の現像が予測可能になる。</p>
`,
    },
    {
      heading: 'テスト ① 実効感度 (EI)',
      body: `
<svg class="article-illust" viewBox="0 0 460 180" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">ZONE I / VIII TEST FILM COMPARISON</text>
    <text x="60" y="32" text-anchor="middle" font-size="10" font-weight="500">Zone I test</text>
    <g transform="translate(10,40)">
      <rect x="0"  y="0" width="20" height="40" fill="#f4f4f2"/>
      <text x="10" y="55" text-anchor="middle" font-size="8" fill="#5a5a58">EI 200</text>
      <text x="10" y="68" text-anchor="middle" font-size="8" fill="#5a5a58">薄すぎ</text>
      <rect x="22" y="0" width="20" height="40" fill="#ededea"/>
      <text x="32" y="55" text-anchor="middle" font-size="8" fill="#5a5a58">EI 250</text>
      <rect x="44" y="0" width="20" height="40" fill="#cfcfcf"/>
      <text x="54" y="55" text-anchor="middle" font-size="8" fill="#5a5a58">EI 320</text>
      <text x="54" y="68" text-anchor="middle" font-size="8" fill="#0a0a0a" font-weight="500">B+F+0.10</text>
      <rect x="66" y="0" width="20" height="40" fill="#9a9a9a"/>
      <text x="76" y="55" text-anchor="middle" font-size="8" fill="#5a5a58">EI 400</text>
      <rect x="88" y="0" width="20" height="40" fill="#5e5e5e"/>
      <text x="98" y="55" text-anchor="middle" font-size="8" fill="#5a5a58">EI 500</text>
      <text x="98" y="68" text-anchor="middle" font-size="8" fill="#5a5a58">濃すぎ</text>
      <rect x="44" y="-4" width="20" height="48" fill="none" stroke="#0a0a0a" stroke-width="2.5"/>
    </g>

    <text x="320" y="32" text-anchor="middle" font-size="10" font-weight="500">Zone VIII test</text>
    <g transform="translate(260,40)">
      <rect x="0"  y="0" width="22" height="40" fill="#9a9a9a"/>
      <text x="11" y="55" text-anchor="middle" font-size="8" fill="#5a5a58">6 min</text>
      <text x="11" y="68" text-anchor="middle" font-size="8" fill="#5a5a58">薄い</text>
      <rect x="24" y="0" width="22" height="40" fill="#b5b5b5"/>
      <text x="35" y="55" text-anchor="middle" font-size="8" fill="#5a5a58">7 min</text>
      <rect x="48" y="0" width="22" height="40" fill="#cfcfcf"/>
      <text x="59" y="55" text-anchor="middle" font-size="8" fill="#0a0a0a" font-weight="500">8 min</text>
      <text x="59" y="68" text-anchor="middle" font-size="8" fill="#0a0a0a">D≈1.25</text>
      <rect x="72" y="0" width="22" height="40" fill="#e6e6e6"/>
      <text x="83" y="55" text-anchor="middle" font-size="8" fill="#5a5a58">10 min</text>
      <rect x="96" y="0" width="22" height="40" fill="#ffffff" stroke="#0a0a0a" stroke-width="0.5"/>
      <text x="107" y="55" text-anchor="middle" font-size="8" fill="#5a5a58">12 min</text>
      <text x="107" y="68" text-anchor="middle" font-size="8" fill="#5a5a58">過剰</text>
      <rect x="48" y="-4" width="22" height="48" fill="none" stroke="#0a0a0a" stroke-width="2.5"/>
    </g>

    <text x="10" y="130" font-size="11" fill="#5a5a58">Zone I: ベース+カブリ +0.10 になる EI を選ぶ (シャドウディテール下限)</text>
    <text x="10" y="148" font-size="11" fill="#5a5a58">Zone VIII: 目標濃度 (1.25-1.30) に到達する現像時間を選ぶ (ハイライト)</text>
    <text x="10" y="166" font-size="11" fill="#5a5a58">2 つのテストを組合せると、自分の機材・薬品での EI と N 時間が確定する</text>
  </g>
</svg>
<p><strong>目的</strong>: シャドウディテール (ベース+カブリ + 0.10) を出す適正露出を決定。</p>
<ol>
  <li>無地の被写体 (グレーカード or 灰色の壁) を均一光下に置く</li>
  <li>スポット測光して EV 値を得る</li>
  <li><strong>ゾーン I 配置</strong>で撮影 (測光値 +4 EV、つまり 4 段絞る) — 例: 測光 EV 12 なら EV 16 で撮影</li>
  <li>ISO を 1/3 段ずつ変えて 5 枚撮影 (例: ISO 200, 250, 320, 400, 500)</li>
  <li>同じ条件 (一括現像) でメーカー推奨時間で現像</li>
  <li>濃度計で測定、または目視でベース+カブリ +0.10 になるネガを選ぶ</li>
  <li>そのネガに対応する ISO 値があなたの実効 EI</li>
</ol>
<p>濃度計がない場合、未露光部分 (ベース+カブリ) を基準に、ゾーン I 撮影部分が「わずかに濃度差を感じる程度」のものを選ぶ。</p>
`,
    },
    {
      heading: 'テスト ② N 時間',
      body: `
<p><strong>目的</strong>: ハイライト密度 (約 1.25-1.30) を出す現像時間を決定。</p>
<svg class="article-illust" viewBox="0 0 460 240" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">H-D CHARACTERISTIC CURVE (フィルム特性曲線)</text>
    <line x1="50" y1="210" x2="440" y2="210" stroke="#0a0a0a" stroke-width="1.4"/>
    <line x1="50" y1="30"  x2="50" y2="210"  stroke="#0a0a0a" stroke-width="1.4"/>
    <text x="245" y="232" text-anchor="middle" font-size="10" fill="#5a5a58">露光量 log E →</text>
    <text x="20" y="120" text-anchor="middle" font-size="10" fill="#5a5a58" transform="rotate(-90 20 120)">濃度 D →</text>

    <text x="80"  y="225" text-anchor="middle" font-size="9" fill="#5a5a58">I</text>
    <text x="150" y="225" text-anchor="middle" font-size="9" fill="#5a5a58">III</text>
    <text x="220" y="225" text-anchor="middle" font-size="9" fill="#5a5a58">V</text>
    <text x="290" y="225" text-anchor="middle" font-size="9" fill="#5a5a58">VII</text>
    <text x="360" y="225" text-anchor="middle" font-size="9" fill="#5a5a58">VIII</text>
    <text x="420" y="225" text-anchor="middle" font-size="9" fill="#5a5a58">X</text>

    <text x="40" y="60"  text-anchor="end" font-size="9" fill="#5a5a58">3.0</text>
    <text x="40" y="100" text-anchor="end" font-size="9" fill="#5a5a58">2.0</text>
    <text x="40" y="140" text-anchor="end" font-size="9" fill="#5a5a58">1.25</text>
    <text x="40" y="180" text-anchor="end" font-size="9" fill="#5a5a58">0.10</text>

    <line x1="50" y1="140" x2="440" y2="140" stroke="#ededea" stroke-dasharray="2 3"/>
    <line x1="50" y1="180" x2="440" y2="180" stroke="#ededea" stroke-dasharray="2 3"/>

    <path d="M50 200 C 100 195, 130 190, 150 175 C 180 155, 220 120, 280 90 C 330 70, 380 60, 440 55" stroke="#0a0a0a" stroke-width="2" fill="none"/>

    <circle cx="80"  cy="195" r="3" fill="#0a0a0a"/>
    <text x="80"  y="190" text-anchor="middle" font-size="9" fill="#0a0a0a">B+F+0.10</text>
    <circle cx="360" cy="80"  r="3" fill="#0a0a0a"/>
    <text x="360" y="74"  text-anchor="middle" font-size="9" fill="#0a0a0a">VIII target</text>

    <text x="50" y="248" font-size="9" fill="#5a5a58">ゾーン I で B+F+0.10 → EI 確定 / ゾーン VIII で 1.25-1.30 → N 時間確定</text>
  </g>
</svg>
<ol>
  <li>テスト ① で得た EI を使う</li>
  <li>同じ被写体を <strong>ゾーン VIII 配置</strong>で撮影 (測光値 −3 EV)</li>
  <li>5 枚撮影 (全部同じ露出)</li>
  <li>メーカー推奨時間の 0.7 / 0.85 / 1.0 / 1.2 / 1.4 倍で現像</li>
  <li>濃度計または目視で、目標濃度 (シルバープリント時にゾーン VIII) を出す時間を選ぶ</li>
</ol>
<p>目視判定: ストレートプリントを焼いて、ゾーン VIII の部分が「白に近いがわずかに調子がある」状態 (印画紙最大白の手前) になる時間が N。</p>
`,
    },
    {
      heading: 'テスト ③ N+1 と N−1',
      body: `
<p>N が決まったら、N±時間も同様に測定:</p>
<ul>
  <li><strong>N+1</strong>: ゾーン VII 配置 → 通常 N×1.4 で撮影、目標濃度に到達するか確認</li>
  <li><strong>N−1</strong>: ゾーン IX 配置 → 通常 N×0.7 で撮影、目標濃度に抑えられるか確認</li>
</ul>
<p>1.4 / 0.7 倍は経験則。フィルム × 現像液により ±10% 程度ずれる。Zone Notes の DEVELOPMENT 画面の <strong>personal offset</strong>に補正値を記録すれば、以降は自動計算される。</p>
`,
    },
    {
      heading: '所要時間と頻度',
      body: `
<p>テスト ①+② を合わせて約 4-6 時間で完了 (撮影 1 時間、現像 2 時間、評価 1-2 時間)。</p>
<p>再テストが必要なタイミング:</p>
<ul>
  <li>新しいフィルム or 現像液に変更</li>
  <li>カメラの修理・整備後</li>
  <li>露出計を変更</li>
  <li>1 年以上経過 (薬品の経時変化)</li>
</ul>
<p>同じ機材で安定運用しているなら、年 1 回のテストで十分。Zone Notes の ANALYTICS で過去の露出評価傾向を見て、ズレが大きくなったタイミングで再テストの判断ができる。</p>
`,
    },
  ],
});

// ============================================================
// 21. Pyro系・ステイン現像詳説
// ============================================================
ARTICLES.push({
  id: 'pyro-stain',
  chapter: '21',
  title: 'Pyro系・ステイン現像詳説',
  summary: '銀像にステイン (染色) を加える特殊現像液。エッジの鋭さとハイライトの粘りを得る伝統技法。',
  sections: [
    {
      heading: 'Pyro 現像液とは',
      body: `
<p>主成分が <strong>ピロガロール (Pyrogallol)</strong> または <strong>ピロカテキン (Pyrocatechin)</strong> の現像液。通常の現像液は銀粒子のみを生成するが、Pyro は銀像周辺に <strong>黄褐色〜オリーブ色のステイン (染色)</strong>を生成する。</p>
<p>このステインが、印画紙への露光時に <strong>追加的なコントラスト調整</strong>として作用し、独特の階調感が得られる。</p>
`,
    },
    {
      heading: '主要な Pyro 現像液',
      body: `
<table class="article-table">
  <tr><th>名称</th><th>主成分</th><th>特徴</th></tr>
  <tr><td>PMK Pyro</td><td>Pyrogallol</td><td>Gordon Hutchings 開発、ZS 実践者の定番、緑のステイン</td></tr>
  <tr><td>Pyrocat-HD</td><td>Pyrocatechin + Phenidone</td><td>Sandy King 開発、シャープ、長期保存可、茶色のステイン</td></tr>
  <tr><td>ABC Pyro</td><td>Pyrogallol</td><td>古典的処方、エドワード・ウェストン愛用</td></tr>
  <tr><td>WD2D+</td><td>Pyrogallol</td><td>シャドウ感度高、フィルム感度を稼ぎたい時</td></tr>
</table>
`,
    },
    {
      heading: 'Pyro の利点',
      body: `
<ol>
  <li><strong>エッジ効果</strong> — 隣接する密度差の境界で、現像液の局所的疲労によりエッジが強調される (シャープに見える)</li>
  <li><strong>ハイライトの粘り</strong> — ステインが高密度部のコントラストを抑え、ハイライトが飛びにくい</li>
  <li><strong>シャドウディテール</strong> — Pyrocat-HD は特にシャドウ感度が高い</li>
  <li><strong>保存性</strong> — Pyrocat-HD は希釈前で 1 年以上保存可</li>
  <li><strong>VC 印画紙との相性</strong> — ステインが青/黄色を抑え、号数調整がしやすい</li>
</ol>
`,
    },
    {
      heading: '取り扱いの注意',
      body: `
<ul>
  <li><strong>毒性</strong> — Pyrogallol は皮膚吸収する。必ずニトリル手袋着用</li>
  <li><strong>酸化しやすい</strong> — 空気接触で活性低下、使い切り or 真空保存</li>
  <li><strong>染色</strong> — 服・タンク・指が染まる、染色は時間とともに濃くなる</li>
  <li><strong>濃度計の必要性</strong> — ステインの濃度は印画紙のチャンネル感度で見え方が変わる、濃度測定は青フィルター付きで</li>
</ul>
`,
    },
    {
      heading: '使い始めの手順',
      body: `
<ol>
  <li>Pyrocat-HD から始めるのを推奨 (毒性低め、扱いやすい)</li>
  <li>Tri-X / HP5+ / Acros II など慣れたフィルムで開始</li>
  <li>標準現像時間 12-15 分 (20°C、1:1:100 希釈)</li>
  <li>攪拌は軽め (最初の 30 秒連続、以降 1 分に 10 秒)</li>
  <li>強い攪拌は筋ムラを生じる</li>
  <li>定着液は新鮮なものを (古い定着液はステインを薄める)</li>
  <li>水洗を十分に (ステインの均一性に影響)</li>
</ol>
<p>Pyro はゾーンシステムの「上級者向け」薬品だが、独特の階調感は他では得られない。エドワード・ウェストン後期、アンセル・アダムス《Yosemite》シリーズの一部、Michael Kenna の作品で多用されている。</p>
`,
    },
  ],
});

// ============================================================
// 22. ファインプリントの仕上げと調色
// ============================================================
ARTICLES.push({
  id: 'fine-printing',
  chapter: '22',
  title: 'ファインプリントの仕上げと調色',
  summary: 'スポッティング、調色、フラッテニング、マウンティング — プリントを「完成」させる工程。',
  sections: [
    {
      heading: 'スポッティング',
      body: `
<svg class="article-illust" viewBox="0 0 460 180" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">SPOTTING TECHNIQUE</text>
    <g transform="translate(20,30)">
      <rect x="0" y="0" width="120" height="100" fill="#5e5e5e" stroke="#0a0a0a"/>
      <circle cx="40" cy="30" r="2" fill="#ffffff"/>
      <circle cx="70" cy="60" r="2.5" fill="#ffffff"/>
      <circle cx="90" cy="40" r="1.5" fill="#ffffff"/>
      <circle cx="30" cy="80" r="2" fill="#ffffff"/>
      <text x="60" y="118" text-anchor="middle" font-size="9" fill="#0a0a0a">スポッティング前</text>
      <text x="60" y="130" text-anchor="middle" font-size="8" fill="#5a5a58">白い斑点 (ホコリ穴)</text>
    </g>
    <text x="160" y="80" font-size="20" fill="#5a5a58">→</text>
    <g transform="translate(200,30)">
      <rect x="0" y="0" width="120" height="100" fill="#5e5e5e" stroke="#0a0a0a"/>
      <text x="60" y="118" text-anchor="middle" font-size="9" fill="#0a0a0a">スポッティング後</text>
      <text x="60" y="130" text-anchor="middle" font-size="8" fill="#5a5a58">背景に同化</text>
    </g>
    <g transform="translate(340,30)">
      <text x="0" y="14" font-size="10" font-weight="500">道具と手順</text>
      <text x="0" y="32" font-size="9" fill="#5a5a58">Spotone (染料 No.1-3)</text>
      <text x="0" y="46" font-size="9" fill="#5a5a58">000-0000 号筆</text>
      <text x="0" y="60" font-size="9" fill="#5a5a58">パレット + ルーペ</text>
      <text x="0" y="80" font-size="9" fill="#5a5a58">ほぼ乾いた筆で点打ち</text>
      <text x="0" y="94" font-size="9" fill="#5a5a58">薄い色を 5-10 回重ねる</text>
      <text x="0" y="108" font-size="9" fill="#5a5a58">24h 後に定着</text>
    </g>
    <text x="10" y="160" font-size="11" fill="#5a5a58">スポッティングはプリント仕上げの基本技術。10× ルーペで段差を確認しながら作業。</text>
  </g>
</svg>
<p>プリントには必ず <strong>白い斑点</strong>が出る (ネガ上のホコリ穴がプリント上で白点になる)。これを目立たなくする工程がスポッティング。</p>
<table class="article-table">
  <tr><th>道具</th><th>用途</th></tr>
  <tr><td>Spotone (染料)</td><td>セット No.1-3 で印画紙の色味に合わせる</td></tr>
  <tr><td>極細筆 (000-0000 号)</td><td>テンプル筆、Kolinsky など</td></tr>
  <tr><td>パレット</td><td>白い陶器皿、染料を希釈</td></tr>
  <tr><td>ルーペ</td><td>10倍、細部確認</td></tr>
</table>
<ol>
  <li>染料を水で希釈、徐々に濃くしていく</li>
  <li>筆を <strong>ほぼ乾いた状態</strong>にして、点を打つ</li>
  <li>1 点で完成させず、薄い色を <strong>5-10 回重ねる</strong></li>
  <li>強拡大しても周囲との段差が分からないレベルが目標</li>
</ol>
<p>スポッティング後 24 時間置くと染料が定着し、洗っても落ちない状態になる。</p>
`,
    },
    {
      heading: '調色 (Toning)',
      body: `
<svg class="article-illust" viewBox="0 0 460 140" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">TONING COLOR COMPARISON</text>
    <g>
      <rect x="10"  y="30" width="80" height="60" fill="#1a1a1a"/>
      <rect x="10"  y="30" width="80" height="60" fill="none" stroke="#0a0a0a"/>
      <text x="50" y="106" text-anchor="middle" font-size="10">No tone</text>
      <text x="50" y="118" text-anchor="middle" font-size="9" fill="#5a5a58">中性黒</text>
    </g>
    <g>
      <rect x="100" y="30" width="80" height="60" fill="#3a2a2a"/>
      <rect x="100" y="30" width="80" height="60" fill="none" stroke="#0a0a0a"/>
      <text x="140" y="106" text-anchor="middle" font-size="10">Selenium</text>
      <text x="140" y="118" text-anchor="middle" font-size="9" fill="#5a5a58">紫褐色</text>
    </g>
    <g>
      <rect x="190" y="30" width="80" height="60" fill="#4d3a1f"/>
      <rect x="190" y="30" width="80" height="60" fill="none" stroke="#0a0a0a"/>
      <text x="230" y="106" text-anchor="middle" font-size="10">Sepia</text>
      <text x="230" y="118" text-anchor="middle" font-size="9" fill="#5a5a58">褐色</text>
    </g>
    <g>
      <rect x="280" y="30" width="80" height="60" fill="#1a2438"/>
      <rect x="280" y="30" width="80" height="60" fill="none" stroke="#0a0a0a"/>
      <text x="320" y="106" text-anchor="middle" font-size="10">Gold</text>
      <text x="320" y="118" text-anchor="middle" font-size="9" fill="#5a5a58">青紫</text>
    </g>
    <g>
      <rect x="370" y="30" width="80" height="60" fill="#4d2419"/>
      <rect x="370" y="30" width="80" height="60" fill="none" stroke="#0a0a0a"/>
      <text x="410" y="106" text-anchor="middle" font-size="10">Copper</text>
      <text x="410" y="118" text-anchor="middle" font-size="9" fill="#5a5a58">赤褐色</text>
    </g>
  </g>
</svg>
<table class="article-table">
  <tr><th>調色剤</th><th>色調</th><th>保存性</th><th>典型用途</th></tr>
  <tr><td>セレン (KRST)</td><td>紫褐色 → 赤褐色</td><td>大幅向上</td><td>標準、アダムスが多用</td></tr>
  <tr><td>セピア</td><td>褐色</td><td>大幅向上</td><td>クラシック表現</td></tr>
  <tr><td>ゴールド</td><td>青色 / 紫色</td><td>大幅向上</td><td>冷たい印象、Edward Curtis</td></tr>
  <tr><td>銅</td><td>赤褐色</td><td>低下</td><td>暖色系、表現重視</td></tr>
  <tr><td>硫黄</td><td>褐色</td><td>低下</td><td>古典的セピア</td></tr>
</table>
<p><strong>セレン調色</strong>はファイバー印画紙の標準的仕上げ。希釈 (1:9 - 1:20) で色味の変化量を調整可能。1:9 の濃い調色では赤褐色、1:20 の薄い調色ではほぼ無色だが Dmax (最大黒) が向上。</p>
`,
    },
    {
      heading: 'セレン調色の手順',
      body: `
<ol>
  <li>定着・水洗 (ハイポクリア後) 完了したプリントを使用</li>
  <li>調色液を 1:10 程度に希釈、20°C で約 3-8 分浸す</li>
  <li>色味の変化を見ながら適時取り出す (シャドウ部から変化)</li>
  <li>停止: 流水で 30 秒</li>
  <li>本水洗: FB 60 分</li>
  <li>乾燥</li>
</ol>
<p>注意: セレン化合物は <strong>毒性</strong>があるため換気必須。手袋着用、調色液の処理は専門業者へ。</p>
`,
    },
    {
      heading: 'フラッテニング (平面化)',
      body: `
<figure class="article-figure">
  <img src="/learn-images/22-print-drying.png" alt="プリント乾燥網棚" loading="lazy" />
  <figcaption>FB 印画紙の乾燥網棚 — 裏面を上向きに並べて自然乾燥</figcaption>
</figure>
<p>FB 印画紙は乾燥時に丸まる。これを平らにする工程:</p>
<ul>
  <li><strong>網棚乾燥</strong> + 自然な平面化</li>
  <li><strong>ドライマウントプレス</strong> (90-100°C) — プロ用機材、最も確実</li>
  <li><strong>重量プレス</strong> — 厚紙の間に挟み、本などで重しをして数日</li>
  <li><strong>フェロタイプ</strong> — 光沢仕上げ (現代では稀)</li>
</ul>
`,
    },
    {
      heading: 'マウンティング',
      body: `
<p>展示・保存のための装丁:</p>
<svg class="article-illust" viewBox="0 0 460 180" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">HINGE MOUNTING (断面図)</text>
    <rect x="30"  y="60" width="400" height="14" fill="#f4f4f2" stroke="#0a0a0a"/>
    <text x="230" y="71" text-anchor="middle" font-size="9">BACKING (4-ply museum board)</text>
    <rect x="80"  y="46" width="270" height="14" fill="#ffffff" stroke="#0a0a0a"/>
    <text x="215" y="57" text-anchor="middle" font-size="9">PRINT (FB paper)</text>
    <rect x="80"  y="40" width="50" height="8" fill="#0a0a0a"/>
    <text x="105" y="35" text-anchor="middle" font-size="8" fill="#0a0a0a">linen hinge tape</text>
    <rect x="30"  y="32" width="400" height="14" fill="#f4f4f2" stroke="#0a0a0a"/>
    <path d="M180 32 L180 46" stroke="#0a0a0a" stroke-width="2"/>
    <path d="M250 32 L250 46" stroke="#0a0a0a" stroke-width="2"/>
    <text x="215" y="42" text-anchor="middle" font-size="9">OVERMAT (window cut)</text>
    <path d="M180 32 L250 32" stroke="none" fill="#f4f4f2"/>
    <line x1="180" y1="32" x2="180" y2="46" stroke="#0a0a0a" stroke-width="1.4"/>
    <line x1="250" y1="32" x2="250" y2="46" stroke="#0a0a0a" stroke-width="1.4"/>

    <text x="10" y="100" font-size="11" font-weight="500">ヒンジマウントの利点</text>
    <text x="10" y="118" font-size="11" fill="#5a5a58">プリント上端のみリネンテープで留め、紙への接着剤直接接触を回避</text>
    <text x="10" y="136" font-size="11" fill="#5a5a58">4-ply 博物館品質マットボード (acid-free, lignin-free, 100% cotton rag)</text>
    <text x="10" y="154" font-size="11" fill="#5a5a58">オーバーマットの窓抜きでプリント周囲を保護、エッジが空気に触れない</text>
    <text x="10" y="172" font-size="11" fill="#5a5a58">保管温度 18-22°C, 湿度 30-50%, 暗所 → 100 年以上の保存性</text>
  </g>
</svg>
<ul>
  <li><strong>マットボード</strong> — 4ply 博物館品質 (acid-free, lignin-free, 100% cotton rag)</li>
  <li><strong>ヒンジマウント</strong> — リネンテープでプリント上端のみ留める、紙への接着剤直接接触なし</li>
  <li><strong>オーバーマット</strong> — プリント周囲を窓抜きしたボードを重ねる、エッジ保護</li>
  <li><strong>サイン</strong> — マットボード下に鉛筆で署名 (4B 程度)</li>
</ul>
<p>プロのプリンターは <strong>「同じネガから 5 枚連続して同じ仕上げのプリントを焼ける」</strong>ことを基準とする。これがアーカイバルプリント (展示・販売可能) のレベル。</p>
`,
    },
  ],
});

// ============================================================
// 23. アンセル・アダムスの代表作分析
// ============================================================
ARTICLES.push({
  id: 'adams-masterpieces',
  chapter: '23',
  title: 'アンセル・アダムスの代表作分析',
  summary: '《Moonrise》《Clearing Winter Storm》《Monolith》— 名作のゾーン配置と現像方針を読み解く。',
  sections: [
    {
      heading: 'Moonrise, Hernandez, New Mexico (1941)',
      body: `
<figure class="article-figure">
  <img src="/learn-images/24-ansel-adams.png" alt="Ansel Adams の肖像" loading="lazy" />
  <figcaption>Ansel Adams — Yosemite を撮り続けた ZS の体系化者</figcaption>
</figure>
<svg class="article-illust" viewBox="0 0 460 200" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="9" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">MOONRISE — ZONE COMPOSITION</text>
    <rect x="10" y="28" width="440" height="100" fill="#1a1a1a" stroke="#0a0a0a"/>
    <rect x="10" y="28" width="440" height="40" fill="#000000"/>
    <text x="20" y="50" font-size="9" fill="#ffffff">空 (上部): II</text>
    <circle cx="380" cy="50" r="10" fill="#b5b5b5"/>
    <text x="380" y="74" text-anchor="middle" font-size="9" fill="#cfcfcf">月 VII</text>
    <rect x="10"  y="68" width="440" height="20" fill="#444444"/>
    <text x="20" y="82" font-size="9" fill="#ffffff">遠景の山: III-IV</text>
    <rect x="10"  y="88" width="440" height="20" fill="#5e5e5e"/>
    <text x="20" y="102" font-size="9" fill="#ffffff">建物: IV-V</text>
    <g>
      <rect x="60"  y="100" width="6" height="8" fill="#cfcfcf"/>
      <rect x="80"  y="100" width="6" height="8" fill="#cfcfcf"/>
      <rect x="100" y="100" width="6" height="8" fill="#cfcfcf"/>
      <rect x="120" y="100" width="6" height="8" fill="#cfcfcf"/>
      <text x="93" y="92" text-anchor="middle" font-size="9" fill="#ffffff">十字架 VII</text>
    </g>
    <rect x="10" y="108" width="440" height="20" fill="#000000"/>
    <text x="20" y="122" font-size="9" fill="#ffffff">前景: I</text>
    <text x="10" y="148" font-size="11" fill="#5a5a58">月をゾーン VII に配置 → 空がゾーン I-II に落ちる劇的な対比 (N−2 圧縮現像)</text>
    <text x="10" y="166" font-size="11" fill="#5a5a58">同一ネガから 1940年代 vs 1970年代で空の暗さを 1 段以上変えてプリント</text>
    <text x="10" y="184" font-size="11" fill="#5a5a58">Cooke Series XV 285mm, 8×10, F32 1秒, ISO 64</text>
  </g>
</svg>
<p>アダムスの最も有名な作品。1941 年 11 月、Santa Fe からの帰り道で日没直前に偶然出会った村と月の風景。</p>
<p><strong>撮影状況</strong>: 露出計が見つからず、月の輝度から逆算。月 = 250 cd/ft² と暗記していたため、これをゾーン VII に配置と決断。8×10 大判、Cooke Series XV 285mm、F32 1秒、フィルム Kodak Super Pan Press B (ISO 64 相当)。</p>
<p><strong>ゾーン構成</strong>:</p>
<ul>
  <li>月: VII (測光ベース)</li>
  <li>白十字架 (墓地): VII</li>
  <li>遠景の山: III-IV</li>
  <li>地表の建物: IV-V</li>
  <li>空 (上部): II</li>
  <li>暗黒の前景: I</li>
</ul>
<p><strong>現像</strong>: ピロガロール ABC Pyro。フィルムをすぐ現像できなかったため、ドライアイスで冷凍保存して帰宅後処理。</p>
<p><strong>後年のプリント解釈</strong>: 1940年代の初期プリントは比較的柔らかく地表が見える。1970年代の後期プリントは <strong>空をゾーン I まで沈め</strong>、極めて劇的な印象に。同一ネガから生涯解釈を変え続けた典型例。</p>
<figure class="article-figure">
  <img src="/learn-images/23-moonrise-style.png" alt="Moonrise 風の独自構図" loading="lazy" />
  <figcaption>《Moonrise》インスパイアの独自構図 — 月をゾーン VII に配置した夜景</figcaption>
</figure>
`,
    },
    {
      heading: 'Clearing Winter Storm, Yosemite (1937 頃)',
      body: `
<figure class="article-figure">
  <img src="/learn-images/23-clearing-winter-storm.png" alt="Clearing Winter Storm 風" loading="lazy" />
  <figcaption>《Clearing Winter Storm》インスパイア — 嵐が晴れる瞬間の渓谷</figcaption>
</figure>
<p>Yosemite Valley の入口、Inspiration Point 付近からの撮影。冬の嵐が晴れる瞬間、霧が谷を流れ El Capitan、Bridalveil Fall、Half Dome が顔を出す。</p>
<p><strong>撮影状況</strong>: 8×10 Korona view、Zeiss Protar 19" (約 480mm)、F45。フィルム Isopan ISO 64。</p>
<p><strong>ゾーン構成</strong>:</p>
<ul>
  <li>白い霧: VIII (主要ハイライト)</li>
  <li>雲の最も明るい部分: IX</li>
  <li>El Capitan の岩面 (順光): V-VI</li>
  <li>影の中の崖: II-III</li>
  <li>滝の白い水: VII-VIII</li>
  <li>暗い松林: II</li>
</ul>
<p><strong>現像</strong>: N 〜 N−1 の中間。霧と岩の対比を最大化するため、N−0.5 相当の現像時間。</p>
<p><strong>プリント技法</strong>: El Capitan の頂上付近を <strong>強く焼き込み</strong> (Burn) してドラマを強調。霧の部分はストレート、暗部の松林を更に Burn。</p>
<svg class="article-illust" viewBox="0 0 460 180" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="9" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">CLEARING WINTER STORM — ZONE COMPOSITION</text>
    <rect x="10" y="28" width="440" height="100" fill="#5e5e5e" stroke="#0a0a0a"/>
    <rect x="10" y="28" width="440" height="30" fill="#444444"/>
    <text x="30" y="46" font-size="9" fill="#ffffff">雲 (灰色): IV-V</text>
    <rect x="20"  y="55" width="50" height="14" fill="#ffffff" stroke="#0a0a0a" stroke-width="0.5"/>
    <text x="100" y="68" font-size="9" fill="#0a0a0a">最も明るい雲: IX</text>
    <rect x="10" y="38" width="100" height="40" fill="#cfcfcf"/>
    <text x="120" y="54" font-size="9" fill="#0a0a0a">霧: VIII (主要ハイライト)</text>
    <rect x="240" y="58" width="100" height="40" fill="#9a9a9a"/>
    <text x="350" y="84" font-size="9" fill="#0a0a0a">岩 (順光): V-VI</text>
    <rect x="40" y="78" width="60" height="20" fill="#cfcfcf"/>
    <text x="160" y="118" font-size="9" fill="#ffffff">滝: VII-VIII</text>
    <rect x="10" y="98" width="440" height="30" fill="#2d2d2d"/>
    <text x="220" y="122" text-anchor="middle" font-size="9" fill="#ffffff">影の崖: II-III · 暗い松林: II</text>
    <text x="10" y="148" font-size="11" fill="#5a5a58">霧をゾーン VIII に配置 → DR 6 段、N 〜 N−0.5 現像</text>
    <text x="10" y="166" font-size="11" fill="#5a5a58">El Capitan 頂上を強い Burn、霧はストレート、暗部の松林をさらに Burn</text>
  </g>
</svg>
`,
    },
    {
      heading: 'Monolith, the Face of Half Dome (1927)',
      body: `
<figure class="article-figure">
  <img src="/learn-images/23-monolith-style.png" alt="Monolith 風" loading="lazy" />
  <figcaption>《Monolith》インスパイア — 暗赤フィルターで空を真っ黒に</figcaption>
</figure>
<p>アダムスが <strong>「Visualization」を発見した転換点</strong>とされる作品。25 歳のとき、Yosemite で Half Dome の真正面に立ち、レンズフィルター 2 種類で撮影。</p>
<p><strong>撮影状況</strong>: Korona 6.5×8.5 view、Zeiss Tessar 8.25" (210mm)、F22 5秒。Wratten K2 黄フィルターで 1 枚、Wratten 29F 暗赤フィルターで 1 枚。フィルム Wratten Panchromatic Process。</p>
<p><strong>K2 黄での結果</strong>: 通常の自然な調子。空はゾーン V-VI、岩はゾーン IV-V、影はゾーン II-III。</p>
<p><strong>29F 赤での結果 (採用版)</strong>: 空がゾーン I (真っ黒)、岩がほぼ同じだが、空との対比でドラマチックに。月が浮かぶ深淵な雰囲気。</p>
<p>この時アダムスは <strong>「目で見たものではなく、最終プリントで見せたいものを撮る」</strong>ことを発見した。これが Visualization の概念の起源。</p>
<svg class="article-illust" viewBox="0 0 460 180" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="9" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">MONOLITH — ZONE COMPOSITION (red filter 29F)</text>
    <rect x="10" y="28" width="440" height="100" fill="#444444" stroke="#0a0a0a"/>
    <rect x="10" y="28" width="440" height="30" fill="#000000"/>
    <text x="220" y="48" text-anchor="middle" font-size="9" fill="#ffffff">空 (赤フィルター 29F): I (ほぼ真っ黒)</text>
    <rect x="100" y="58" width="260" height="60" fill="#9a9a9a"/>
    <text x="230" y="92" text-anchor="middle" font-size="9" fill="#0a0a0a">Half Dome の花崗岩面: IV-V</text>
    <rect x="220" y="65" width="20" height="30" fill="#cfcfcf"/>
    <text x="250" y="80" font-size="9" fill="#0a0a0a">日が当たる岩: VI</text>
    <rect x="10" y="118" width="100" height="10" fill="#000000"/>
    <rect x="350" y="118" width="100" height="10" fill="#000000"/>
    <text x="230" y="125" text-anchor="middle" font-size="9" fill="#ffffff">前景の松: II</text>
    <text x="10" y="148" font-size="11" fill="#5a5a58">同じ岩面でも K2 黄 vs 29F 赤 で空が IV → I へ 3 段差</text>
    <text x="10" y="166" font-size="11" fill="#5a5a58">フィルター = 「第二の露出操作」、Visualization 発見の原点</text>
  </g>
</svg>
`,
    },
    {
      heading: 'Aspens, Northern New Mexico (1958)',
      body: `
<figure class="article-figure">
  <img src="/learn-images/23-aspens-style.png" alt="Aspens 風" loading="lazy" />
  <figcaption>《Aspens》インスパイア — 暗い森背景で白樺の白を際立たせる</figcaption>
</figure>
<p>アダムスのハイキー作品の代表。秋の白樺林、垂直の幹と暗い背景の対比。</p>
<p><strong>ゾーン構成</strong>:</p>
<ul>
  <li>白樺の幹 (順光面): VII-VIII</li>
  <li>白樺の幹 (影面): V-VI</li>
  <li>葉の最も明るい部分: VI-VII</li>
  <li>葉の影部: III-IV</li>
  <li>背景の暗い森: I-II</li>
</ul>
<p>背景の深い黒 (ゾーン I-II) が、白樺の白を <strong>強烈に浮き上がらせる</strong>。前景の細部までクリアでありながら、全体の印象は明るい。「ハイキーは背景の黒で作る」典型例。</p>
<p><strong>現像</strong>: N+1 寄り (ハイライトを伸ばし、白樺のディテールを強調)。プリントは Grade 2 で焼き、背景部分のみ Burn で深い黒に。</p>
<svg class="article-illust" viewBox="0 0 460 180" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="9" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">ASPENS — ZONE COMPOSITION (high key)</text>
    <rect x="10" y="28" width="440" height="100" fill="#1a1a1a" stroke="#0a0a0a"/>
    <text x="220" y="65" text-anchor="middle" font-size="9" fill="#ffffff">暗い森背景: I-II</text>
    <g>
      <rect x="60"  y="35" width="14" height="80" fill="#e6e6e6"/>
      <rect x="100" y="35" width="14" height="80" fill="#cfcfcf"/>
      <rect x="160" y="35" width="14" height="80" fill="#e6e6e6"/>
      <rect x="220" y="35" width="14" height="80" fill="#b5b5b5"/>
      <rect x="280" y="35" width="14" height="80" fill="#cfcfcf"/>
      <rect x="340" y="35" width="14" height="80" fill="#e6e6e6"/>
      <rect x="390" y="35" width="14" height="80" fill="#b5b5b5"/>
    </g>
    <text x="20" y="85" font-size="9" fill="#ffffff">白樺の幹 (順光): VII-VIII</text>
    <text x="320" y="105" font-size="9" fill="#0a0a0a">幹の影面: V-VI</text>
    <text x="10" y="148" font-size="11" fill="#5a5a58">背景の深い黒 (I-II) が白樺の白 (VII-VIII) を強烈に浮き上がらせる</text>
    <text x="10" y="166" font-size="11" fill="#5a5a58">「ハイキーは背景の黒で作る」典型例、N+1 拡張現像</text>
  </g>
</svg>
`,
    },
  ],
});

// ============================================================
// 24. マスター写真家のアプローチ
// ============================================================
ARTICLES.push({
  id: 'master-approaches',
  chapter: '24',
  title: 'マスター写真家のアプローチ',
  summary: 'Edward Weston、Minor White、Wynn Bullock、森山大道、植田正治 — それぞれの ZS 解釈と独自手法。',
  sections: [
    {
      heading: 'Edward Weston (1886-1958)',
      body: `
<figure class="article-figure">
  <img src="/learn-images/24-edward-weston.png" alt="Edward Weston の肖像" loading="lazy" />
  <figcaption>Edward Weston — 8×10 ビューカメラと共に</figcaption>
</figure>
<p>アダムスの盟友であり、ZS の同時代的実践者。<strong>「Pre-visualization」</strong>の概念をアダムスより先に提唱したとも言われる。</p>
<p><strong>機材</strong>: 8×10 Ansco view、Centruy Universal 19" Petzval、コンタクトプリントのみ (引伸ししない)</p>
<p><strong>露出</strong>: 入射光式と勘の組合せ。ZS の数学的厳密さは追求せず、視覚化された結果を最優先。</p>
<p><strong>現像</strong>: ABC Pyro、コンタクトプリントゆえネガに豊かな密度 (DR 10 段以上を許容)</p>
<p><strong>代表作</strong>: 《Pepper No.30》(1930)、《Nautilus Shell》(1927)、《Dunes, Oceano》(1936)</p>
<p><strong>哲学</strong>: 「物そのもの (Thingness)」を写す、抽象でなく具体物の存在感を捉える。</p>
<figure class="article-figure">
  <img src="/learn-images/24-weston-pepper.png" alt="Weston Pepper 風" loading="lazy" />
  <figcaption>《Pepper No.30》インスパイア — 野菜の彫刻的なクローズアップ</figcaption>
</figure>
<figure class="article-figure">
  <img src="/learn-images/24-weston-nautilus.png" alt="Weston Nautilus 風" loading="lazy" />
  <figcaption>《Nautilus Shell (1927)》インスパイア — オウムガイの対数螺旋</figcaption>
</figure>
`,
    },
    {
      heading: 'Minor White (1908-1976)',
      body: `
<figure class="article-figure">
  <img src="/learn-images/24-minor-white.png" alt="Minor White の肖像" loading="lazy" />
  <figcaption>Minor White — Aperture 誌創刊者、ZS 第二世代</figcaption>
</figure>
<p>ZS を <strong>「精神的修養」</strong>として捉え直した、ZS 第二世代の主要人物。Aperture 誌創刊者、Rochester Institute of Technology 教員。</p>
<p><strong>機材</strong>: 4×5 中心、35mm も併用</p>
<p><strong>独自概念</strong>: <strong>「Equivalents (等価物)」</strong>— 被写体の物理的記録ではなく、撮影者の内面状態を表現する写真。Alfred Stieglitz の Equivalents シリーズの継承。</p>
<p><strong>ZS 拡張</strong>: <strong>Visualization Sequence</strong>、Reading the Image (撮影前の長い瞑想的観察)</p>
<p><strong>代表作</strong>: 《Sequence 17》、《Frosted Window, Rochester》(1958)</p>
<p><strong>影響</strong>: アダムスの技術的 ZS と、White の精神的 ZS は、現代の ZS 教則に統合されている。</p>
<figure class="article-figure">
  <img src="/learn-images/24-white-abstract.png" alt="Minor White 抽象風" loading="lazy" />
  <figcaption>Equivalents インスパイア — 抽象化されたテクスチャ、瞑想的な深さ</figcaption>
</figure>
<figure class="article-figure">
  <img src="/learn-images/24-white-sequence.png" alt="White Sequence 風" loading="lazy" />
  <figcaption>Sequence 形式インスパイア — 自然の連続的な変奏</figcaption>
</figure>
`,
    },
    {
      heading: 'Wynn Bullock (1902-1975)',
      body: `
<figure class="article-figure">
  <img src="/learn-images/24-wynn-bullock.png" alt="Wynn Bullock の肖像" loading="lazy" />
  <figcaption>Wynn Bullock — カリフォルニア派、森林と人体の融合</figcaption>
</figure>
<p>カリフォルニア派のマスター。森林・人体・抽象の融合。長時間露光と二重露光を多用。</p>
<p><strong>機材</strong>: 4×5 中心、Tri-X Pan Professional</p>
<p><strong>露出</strong>: 厳密な ZS 実践、Pentax Spotmeter V を愛用</p>
<p><strong>独自手法</strong>:</p>
<ul>
  <li><strong>多重露光</strong>でハイライトとシャドウを別撮影</li>
  <li><strong>長時間露光</strong>で水・霧の流動を表現 (10秒〜数分)</li>
  <li><strong>強い赤フィルター</strong>で空を真っ黒に</li>
</ul>
<p><strong>代表作</strong>: 《Child in Forest》(1951)、《Tide Pool》(1957)</p>
<p><strong>哲学</strong>: 「時間と空間は一つの連続体」、写真はその一瞬の断面。</p>
<figure class="article-figure">
  <img src="/learn-images/24-bullock-forest.png" alt="Bullock 森風" loading="lazy" />
  <figcaption>《Child in Forest》インスパイア — 神秘的な森と人物の融合</figcaption>
</figure>
<figure class="article-figure">
  <img src="/learn-images/24-bullock-tidepool.png" alt="Bullock Tide Pool 風" loading="lazy" />
  <figcaption>《Tide Pool (1957)》インスパイア — 潮溜まりに映る時間の融合</figcaption>
</figure>
`,
    },
    {
      heading: '森山大道 (1938-)',
      body: `
<figure class="article-figure">
  <img src="/learn-images/24-daido-moriyama.png" alt="森山大道 の肖像" loading="lazy" />
  <figcaption>森山大道 — 新宿のストリート、Ricoh GR と共に</figcaption>
</figure>
<p>日本のストリート・スナップの代表者。ZS とは対照的な <strong>「アレ・ブレ・ボケ」</strong>の美学だが、その背景には独自の露出戦略がある。</p>
<p><strong>機材</strong>: Ricoh GR、Minolta Hi-matic E、35mm</p>
<p><strong>フィルム</strong>: Tri-X 400 を EI 1600 で増感、Microphen で N+2 現像</p>
<p><strong>結果</strong>: 強烈なグレイン、シャドウは沈み、ハイライトは飛ぶ。<strong>シーン全体がゾーン II-VIII の極端な分布</strong>になり、独特の「物理感」が生まれる。</p>
<p>森山自身は ZS の体系的言語を使わないが、<strong>「失敗するシステムを意図的に選んでいる」</strong>と解釈できる。</p>
<figure class="article-figure">
  <img src="/learn-images/24-moriyama-street.png" alt="森山大道 ストリート風" loading="lazy" />
  <figcaption>森山大道インスパイア — 押し焼き Tri-X による強烈なグレインと黒</figcaption>
</figure>
<figure class="article-figure">
  <img src="/learn-images/24-moriyama-interior.png" alt="森山 インテリア風" loading="lazy" />
  <figcaption>森山インスパイア — 古い日本家屋の暗いインテリア</figcaption>
</figure>
`,
    },
    {
      heading: '植田正治 (1913-2000)',
      body: `
<figure class="article-figure">
  <img src="/learn-images/24-shoji-ueda.png" alt="植田正治 の肖像" loading="lazy" />
  <figcaption>植田正治 — 鳥取砂丘の「植田調」</figcaption>
</figure>
<p>鳥取砂丘を舞台にした演出写真で世界的に知られる。<strong>「植田調 (Ueda-cho)」</strong>と呼ばれる独特のミニマルな構図。</p>
<p><strong>機材</strong>: Hasselblad 中判中心、6×6</p>
<p><strong>ZS との関係</strong>: 砂丘という極めて単純な被写体 (砂 + 空) で、ハイキー寄りの繊細な階調を作る。</p>
<p><strong>典型ゾーン構成</strong>:</p>
<ul>
  <li>晴天の白い砂: VII-VIII</li>
  <li>影の砂: V-VI</li>
  <li>人物の服 (黒): II-III</li>
  <li>人物の肌: V</li>
  <li>空: VI-VII</li>
</ul>
<p>派手なコントラストではなく、<strong>「ハイミドル」の繊細な階調</strong>で詩情を作る。植田の作品からは、ZS が決して劇的な表現だけでなく、繊細で控えめな美学にも貢献することが学べる。</p>
<figure class="article-figure">
  <img src="/learn-images/24-ueda-sand-dunes.png" alt="植田正治 砂丘風" loading="lazy" />
  <figcaption>植田調インスパイア — 鳥取砂丘でのミニマルな演出</figcaption>
</figure>
<figure class="article-figure">
  <img src="/learn-images/24-ueda-family.png" alt="植田 家族写真風" loading="lazy" />
  <figcaption>植田調家族写真インスパイア — 均等配置の家族と砂丘</figcaption>
</figure>
`,
    },
  ],
});

// ============================================================
// 25. デジタル RAW でのゾーンシステム応用
// ============================================================
ARTICLES.push({
  id: 'digital-zone-system',
  chapter: '25',
  title: 'デジタル RAW でのゾーンシステム応用',
  summary: 'デジタルセンサーへの ZS 適用。ヒストグラム = ゾーン分布、ETTR、HDR との関係。',
  sections: [
    {
      heading: 'デジタルへの翻訳',
      body: `
<table class="article-table">
  <tr><th>フィルム ZS</th><th>デジタル相当</th></tr>
  <tr><td>11 ゾーン (0-X)</td><td>RAW のダイナミックレンジ (現代センサー 12-14 段)</td></tr>
  <tr><td>ゾーン V = 18% グレー</td><td>ヒストグラム中央 (RGB 値 約 118)</td></tr>
  <tr><td>シャドウのために露光</td><td>ETTR (Expose To The Right)</td></tr>
  <tr><td>N+ 現像 (拡張)</td><td>RAW でシャドウを持ち上げる (Lightroom Shadows +)</td></tr>
  <tr><td>N− 現像 (圧縮)</td><td>RAW でハイライトを抑える (Highlights −)</td></tr>
  <tr><td>覆い焼き / 焼き込み</td><td>レンジマスク + 部分編集</td></tr>
  <tr><td>スプリットグレード</td><td>HDR 合成 / Tone Mapping</td></tr>
</table>
`,
    },
    {
      heading: 'ETTR (Expose To The Right)',
      body: `
<p>デジタル ZS の中核戦略。<strong>「白とびしないギリギリまで露出を多くする」</strong>。</p>
<svg class="article-illust" viewBox="0 0 460 200" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="9" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">NORMAL EXPOSURE vs ETTR</text>

    <text x="115" y="32" text-anchor="middle" font-size="10" font-weight="500">通常露出</text>
    <g transform="translate(20,40)">
      <rect x="0" y="0" width="190" height="60" fill="none" stroke="#0a0a0a"/>
      <rect x="20" y="40" width="20" height="20" fill="#0a0a0a"/>
      <rect x="40" y="20" width="20" height="40" fill="#0a0a0a"/>
      <rect x="60" y="10" width="30" height="50" fill="#0a0a0a"/>
      <rect x="90" y="20" width="30" height="40" fill="#0a0a0a"/>
      <rect x="120" y="40" width="20" height="20" fill="#0a0a0a"/>
      <rect x="140" y="50" width="20" height="10" fill="#0a0a0a"/>
      <text x="20" y="76" font-size="8" fill="#5a5a58">0</text>
      <text x="180" y="76" font-size="8" fill="#5a5a58">255</text>
    </g>
    <text x="20" y="120" font-size="10" fill="#5a5a58">シャドウ部にノイズ載りやすい</text>
    <text x="20" y="135" font-size="10" fill="#5a5a58">右に余白あり (情報を捨てている)</text>

    <text x="345" y="32" text-anchor="middle" font-size="10" font-weight="500">ETTR (右寄せ)</text>
    <g transform="translate(250,40)">
      <rect x="0" y="0" width="190" height="60" fill="none" stroke="#0a0a0a"/>
      <rect x="60" y="40" width="20" height="20" fill="#0a0a0a"/>
      <rect x="80" y="20" width="20" height="40" fill="#0a0a0a"/>
      <rect x="100" y="10" width="30" height="50" fill="#0a0a0a"/>
      <rect x="130" y="20" width="30" height="40" fill="#0a0a0a"/>
      <rect x="160" y="40" width="20" height="20" fill="#0a0a0a"/>
      <rect x="180" y="50" width="10" height="10" fill="#0a0a0a"/>
      <line x1="188" y1="0" x2="188" y2="60" stroke="#5a5a58" stroke-width="1" stroke-dasharray="2 2"/>
      <text x="20" y="76" font-size="8" fill="#5a5a58">0</text>
      <text x="180" y="76" font-size="8" fill="#5a5a58">255</text>
    </g>
    <text x="250" y="120" font-size="10" fill="#5a5a58">SN 比向上 → シャドウ救済可</text>
    <text x="250" y="135" font-size="10" fill="#5a5a58">右端ギリギリ (白とび直前)</text>

    <text x="10" y="170" font-size="11" fill="#5a5a58">→ RAW 現像でハイライトを下げる (= フィルムの N− 圧縮現像に相当)</text>
    <text x="10" y="188" font-size="11" fill="#5a5a58">「ハイライトのために露光、シャドウのために RAW 現像」がデジタル時代の対応原則</text>
  </g>
</svg>
<ul>
  <li>デジタルセンサーは <strong>シャドウのノイズが多い</strong> (フィルムと逆)</li>
  <li>ハイライト寄りに撮ることで、シャドウの SN 比が向上</li>
  <li>RAW 現像でハイライトを下げる = フィルムの N− に相当</li>
</ul>
<p>つまり <strong>「ハイライトのために露光、シャドウのために RAW 現像」</strong>がデジタル時代の対応原則。フィルムと正反対。</p>
`,
    },
    {
      heading: 'ヒストグラム = ゾーン分布',
      body: `
<p>ヒストグラムの横軸を <strong>11 ゾーンに分割</strong>して読む:</p>
<svg class="article-illust" viewBox="0 0 460 200" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="9" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">RGB HISTOGRAM (0-255) → ZONE MAP</text>
    <g transform="translate(10,28)">
      <rect x="0"   y="0" width="38" height="80" fill="#000000"/>
      <rect x="38"  y="0" width="38" height="80" fill="#1a1a1a"/>
      <rect x="76"  y="0" width="38" height="80" fill="#2d2d2d"/>
      <rect x="114" y="0" width="38" height="80" fill="#444444"/>
      <rect x="152" y="0" width="38" height="80" fill="#5e5e5e"/>
      <rect x="190" y="0" width="38" height="80" fill="#808080"/>
      <rect x="228" y="0" width="38" height="80" fill="#9a9a9a"/>
      <rect x="266" y="0" width="38" height="80" fill="#b5b5b5"/>
      <rect x="304" y="0" width="38" height="80" fill="#cfcfcf"/>
      <rect x="342" y="0" width="38" height="80" fill="#e6e6e6"/>
      <rect x="380" y="0" width="38" height="80" fill="#ffffff" stroke="#0a0a0a"/>
      <rect x="0" y="0" width="418" height="80" fill="none" stroke="#0a0a0a"/>
      <text x="19"  y="98"  text-anchor="middle" font-size="9" fill="#5a5a58">0</text>
      <text x="57"  y="98"  text-anchor="middle" font-size="9" fill="#5a5a58">I</text>
      <text x="95"  y="98"  text-anchor="middle" font-size="9" fill="#5a5a58">II</text>
      <text x="133" y="98"  text-anchor="middle" font-size="9" fill="#5a5a58">III</text>
      <text x="171" y="98"  text-anchor="middle" font-size="9" fill="#5a5a58">IV</text>
      <text x="209" y="98"  text-anchor="middle" font-size="9" fill="#5a5a58">V</text>
      <text x="247" y="98"  text-anchor="middle" font-size="9" fill="#5a5a58">VI</text>
      <text x="285" y="98"  text-anchor="middle" font-size="9" fill="#5a5a58">VII</text>
      <text x="323" y="98"  text-anchor="middle" font-size="9" fill="#5a5a58">VIII</text>
      <text x="361" y="98"  text-anchor="middle" font-size="9" fill="#5a5a58">IX</text>
      <text x="399" y="98"  text-anchor="middle" font-size="9" fill="#5a5a58">X</text>
      <text x="0"   y="116" font-size="9" fill="#5a5a58">0</text>
      <text x="200" y="116" font-size="9" fill="#5a5a58">128</text>
      <text x="408" y="116" font-size="9" fill="#5a5a58">255</text>
    </g>
    <text x="10" y="160" font-size="11" fill="#5a5a58">左端貼り付き → ゾーン 0 黒つぶれ (回復不可)</text>
    <text x="10" y="180" font-size="11" fill="#5a5a58">右端貼り付き → ゾーン X 白とび (回復不可、ETTR から 1/3 段戻す)</text>
  </g>
</svg>
<ul>
  <li>0-23: ゾーン 0 (純黒)</li>
  <li>24-46: ゾーン I</li>
  <li>47-69: ゾーン II</li>
  <li>70-92: ゾーン III (重要シャドウ)</li>
  <li>93-115: ゾーン IV</li>
  <li>116-138: ゾーン V (中間)</li>
  <li>139-161: ゾーン VI</li>
  <li>162-184: ゾーン VII</li>
  <li>185-207: ゾーン VIII (重要ハイライト)</li>
  <li>208-230: ゾーン IX</li>
  <li>231-255: ゾーン X (純白)</li>
</ul>
<p>ヒストグラム左端 (シャドウ) が崩れていなければ、後の RAW 現像で持ち上げ可能。右端 (ハイライト) が貼り付いていたら情報損失、回復不可。</p>
`,
    },
    {
      heading: 'Lightroom / Capture One での実践',
      body: `
<p><strong>段階的な調整手順</strong>:</p>
<ol>
  <li>Exposure: 全体の中間明度を調整 (ゾーン V を狙う)</li>
  <li>Highlights: ゾーン VIII-IX のディテール救済</li>
  <li>Shadows: ゾーン II-III のディテール救済</li>
  <li>Whites: ゾーン IX-X の最大白を設定</li>
  <li>Blacks: ゾーン 0-I の最大黒を設定</li>
  <li>Contrast: 全体のミッドトーン強度</li>
  <li>Tone Curve: ゾーン別の精密調整</li>
  <li>Range Mask + Brush: 部分的な Dodge/Burn</li>
</ol>
<p><strong>レンジマスク</strong> (luminosity mask) は、Photoshop の Channel based selection と同等。「ゾーン VII-IX のみに適用」のような精密制御が可能。</p>
`,
    },
    {
      heading: 'HDR とトーンマッピング',
      body: `
<p>1 枚の RAW で扱えない高 DR シーンに対し、<strong>露出を変えた複数枚を合成</strong>する技法。</p>
<ul>
  <li>3-7 段ブラケット撮影 → HDR Merge</li>
  <li>32bit RAW として保存 → Tone Mapping で 8/16bit に圧縮</li>
  <li>不自然な「HDR ルック」を避けるには、トーンカーブで控えめに圧縮</li>
</ul>
<p>これは <strong>フィルム ZS の N−2 / N−3 圧縮現像のデジタル相当</strong>。ZS の思想 (シーンのゾーン分布を意図したゾーン分布に翻訳する) は同一。</p>
`,
    },
    {
      heading: 'デジタル化の利点と限界',
      body: `
<table class="article-table">
  <tr><th></th><th>フィルム ZS</th><th>デジタル ZS</th></tr>
  <tr><td>ダイナミックレンジ</td><td>10-12 段 (シャドウ余裕)</td><td>12-14 段 (ハイライト余裕)</td></tr>
  <tr><td>後工程の自由度</td><td>限定的 (現像方針は決まる)</td><td>極めて高い (RAW なら無限調整)</td></tr>
  <tr><td>意思決定のタイミング</td><td>撮影時に大半を決める</td><td>後工程で多くを決められる</td></tr>
  <tr><td>習熟の意義</td><td>体系的訓練が必須</td><td>ある程度ソフトに任せられる</td></tr>
</table>
<p><strong>逆説的</strong>: デジタル時代こそ ZS 的訓練の価値が高い。なぜなら、後工程で「何でもできる」がゆえに、撮影時に <strong>「自分が何を見せたいか」</strong>を明確にしておかないと、無限の選択肢に溺れてしまう。</p>
`,
    },
  ],
});

// ============================================================
// 26. 90日練習プログラム
// ============================================================
ARTICLES.push({
  id: 'practice-program',
  chapter: '26',
  title: '90 日練習プログラム',
  summary: '初心者から実用レベルまで、3 ヶ月で ZS を体に染み込ませる段階的トレーニング。',
  sections: [
    {
      heading: '前提と準備',
      body: `
<p><strong>必要な機材</strong>:</p>
<ul>
  <li>カメラ (中判または 35mm)</li>
  <li>スポット測光器 (Pentax Spotmeter V 等)</li>
  <li>三脚</li>
  <li>フィルム (Tri-X 400 を 10-15 本)</li>
  <li>現像セット (D-76 1:1、定着液、温度計、タンク)</li>
  <li>引伸機 + 印画紙 (Ilford MGFB Classic 8×10 を 100 枚)</li>
</ul>
<p><strong>所要時間</strong>: 週 5-10 時間 × 13 週</p>
<p><strong>目標</strong>: 任意のシーンを見て、即座にゾーン配置を予測できるようになる。</p>
`,
    },
    {
      heading: '第 1 週 - 第 2 週: 観察と測光の基礎',
      body: `
<svg class="article-illust" viewBox="0 0 460 200" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="9" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">90-DAY PROGRAM TIMELINE (13 weeks)</text>
    <line x1="20" y1="50" x2="450" y2="50" stroke="#0a0a0a" stroke-width="1.4"/>
    <g>
      <line x1="20" y1="46" x2="20" y2="54" stroke="#0a0a0a"/>
      <text x="20" y="38" text-anchor="middle" font-size="8">W1</text>
      <text x="20" y="68" text-anchor="middle" font-size="8" fill="#5a5a58">観察・測光</text>
      <line x1="55" y1="46" x2="55" y2="54" stroke="#0a0a0a"/>
      <text x="55" y="38" text-anchor="middle" font-size="8">W3</text>
      <text x="55" y="68" text-anchor="middle" font-size="8" fill="#5a5a58">撮影開始</text>
      <line x1="120" y1="46" x2="120" y2="54" stroke="#0a0a0a"/>
      <text x="120" y="38" text-anchor="middle" font-size="8">W5</text>
      <text x="120" y="68" text-anchor="middle" font-size="8" fill="#5a5a58">EI/N時間テスト</text>
      <line x1="190" y1="46" x2="190" y2="54" stroke="#0a0a0a"/>
      <text x="190" y="38" text-anchor="middle" font-size="8">W7</text>
      <text x="190" y="68" text-anchor="middle" font-size="8" fill="#5a5a58">プリント開始</text>
      <line x1="260" y1="46" x2="260" y2="54" stroke="#0a0a0a"/>
      <text x="260" y="38" text-anchor="middle" font-size="8">W9</text>
      <text x="260" y="68" text-anchor="middle" font-size="8" fill="#5a5a58">N±試行</text>
      <line x1="340" y1="46" x2="340" y2="54" stroke="#0a0a0a"/>
      <text x="340" y="38" text-anchor="middle" font-size="8">W11</text>
      <text x="340" y="68" text-anchor="middle" font-size="8" fill="#5a5a58">フィルター活用</text>
      <line x1="430" y1="46" x2="430" y2="54" stroke="#0a0a0a"/>
      <text x="430" y="38" text-anchor="middle" font-size="8">W13</text>
      <text x="430" y="68" text-anchor="middle" font-size="8" fill="#5a5a58">統合・発表</text>
    </g>

    <text x="10" y="100" font-size="11" font-weight="500">能力の発達曲線</text>
    <g transform="translate(20, 110)">
      <rect x="0"  y="40" width="60" height="20" fill="#444444"/>
      <text x="30" y="78" text-anchor="middle" font-size="8" fill="#5a5a58">測光精度</text>
      <rect x="70" y="20" width="60" height="40" fill="#5e5e5e"/>
      <text x="100" y="78" text-anchor="middle" font-size="8" fill="#5a5a58">露出判断</text>
      <rect x="140" y="10" width="60" height="50" fill="#9a9a9a"/>
      <text x="170" y="78" text-anchor="middle" font-size="8" fill="#5a5a58">N±判断</text>
      <rect x="210" y="0"  width="60" height="60" fill="#cfcfcf"/>
      <text x="240" y="78" text-anchor="middle" font-size="8" fill="#5a5a58">プリント</text>
      <rect x="280" y="0"  width="60" height="60" fill="#e6e6e6" stroke="#0a0a0a"/>
      <text x="310" y="78" text-anchor="middle" font-size="8" fill="#5a5a58">統合表現</text>
    </g>
  </g>
</svg>
<ol>
  <li>毎日 10 シーン、スポット測光のみ実施 (撮影しない)</li>
  <li>シャドウ・ハイライトの EV 値を記録</li>
  <li>「目で見た予想」と「実測値」の差を Zone Notes に記録</li>
  <li>2 週間で予測誤差が ±1 EV 以内に収まるのが目標</li>
  <li>並行: 教材 01-04 章を読む</li>
</ol>
`,
    },
    {
      heading: '第 3 週 - 第 4 週: 撮影開始',
      body: `
<ol>
  <li>Tri-X 400 (EI 320) を 4 本撮る</li>
  <li>すべて N 現像 (D-76 1:1, 9'45")</li>
  <li>シャドウ III 配置を厳守、ハイライトは結果観察</li>
  <li>各撮影で Zone Notes に完全記録</li>
  <li>現像後、ネガをルーペで観察 → ハイライト密度の感覚を掴む</li>
  <li>並行: 教材 05-06 章を読む</li>
</ol>
`,
    },
    {
      heading: '第 5 週 - 第 6 週: パーソナルテスト',
      body: `
<ol>
  <li>EI テスト (1 本)</li>
  <li>N 時間テスト (1 本)</li>
  <li>結果を Zone Notes プリセットの「personal offset」に登録</li>
  <li>並行: 教材 13-14, 20 章を読む</li>
</ol>
`,
    },
    {
      heading: '第 7 週 - 第 8 週: プリント開始',
      body: `
<ol>
  <li>これまでの全ネガから 5 枚を選択</li>
  <li>テストストリップ → ストレートプリント</li>
  <li>各プリントの「気に入らない部分」を REFLECTION (printing) に記録</li>
  <li>10 枚のプリントを完成させる</li>
  <li>並行: 教材 07, 22 章を読む</li>
</ol>
`,
    },
    {
      heading: '第 9 週 - 第 10 週: N+ / N− を試す',
      body: `
<ol>
  <li>低コントラスト (曇天、霧) シーンで N+1 撮影 (1 本)</li>
  <li>高コントラスト (晴天、雪 + 影) シーンで N−1 撮影 (1 本)</li>
  <li>各シーンを Zone Notes で完全予測 → 結果照合</li>
  <li>プリントで N±現像の効果を視覚的に確認</li>
  <li>並行: 教材 17, 18 章を読む</li>
</ol>
`,
    },
    {
      heading: '第 11 週 - 第 12 週: フィルター活用',
      body: `
<ol>
  <li>Yellow #8、Orange #16、Red #25 を試す (各 1 本)</li>
  <li>同一シーンをフィルターなし → Y → O → R で連続撮影</li>
  <li>空のゾーンが V → IV → III → II と変化することを実測確認</li>
  <li>並行: 教材 15 章を読む</li>
</ol>
`,
    },
    {
      heading: '第 13 週: 統合と発表',
      body: `
<ol>
  <li>これまでの最良プリント 5 枚を選択 → スポッティング → セレン調色 → マウント</li>
  <li>友人・家族に見せて感想を聞く</li>
  <li>Zone Notes ANALYTICS で 90 日の傾向を分析</li>
  <li>次の 90 日の課題を 3 つ決める</li>
</ol>
<p>このプログラム完了時点で、ZS の <strong>「言語」</strong>が体に染み込んでいる。以降は意識せずに配置・現像・プリントが回るようになる。</p>
`,
    },
  ],
});

// ============================================================
// 27. ゾーンシステム用語集
// ============================================================
ARTICLES.push({
  id: 'glossary',
  chapter: '27',
  title: 'ゾーンシステム用語集',
  summary: 'ZS およびモノクロ写真の専門用語を 50+ 項目で網羅。アルファベット順。',
  sections: [
    {
      heading: 'A - C',
      body: `
<table class="article-table">
  <tr><th>用語</th><th>意味</th></tr>
  <tr><td><strong>Adams, Ansel</strong></td><td>1902-1984。ZS の体系化者。Yosemite を撮り続けた</td></tr>
  <tr><td><strong>Agitation (攪拌)</strong></td><td>現像中に薬液を循環させる動作。強さでコントラストが変わる</td></tr>
  <tr><td><strong>Anchor Point</strong></td><td>「アンカー」、配置のために選ぶ重要な部分 (主要シャドウ・ハイライト)</td></tr>
  <tr><td><strong>Archer, Fred</strong></td><td>ZS の共同開発者。Art Center School で同僚</td></tr>
  <tr><td><strong>Archival Processing</strong></td><td>長期保存可能な処理。2浴定着、十分な水洗、調色等</td></tr>
  <tr><td><strong>Base + Fog</strong></td><td>未露光フィルムの最低濃度。ベース + カブリ</td></tr>
  <tr><td><strong>Bracketing</strong></td><td>同シーンを露出を変えて複数撮ること</td></tr>
  <tr><td><strong>Burning</strong></td><td>プリント時に追加露光して暗くする (焼き込み)</td></tr>
  <tr><td><strong>Characteristic Curve</strong></td><td>フィルム/印画紙の入射光 vs 濃度の関係を示す曲線 (H-D 曲線)</td></tr>
  <tr><td><strong>Compensating Developer</strong></td><td>ハイライトのコントラストを抑えながらシャドウを保つ現像液 (HC-110, Diafine)</td></tr>
  <tr><td><strong>Compression</strong></td><td>圧縮現像 (N−)、高 DR シーンを現像で抑える</td></tr>
  <tr><td><strong>Contact Print</strong></td><td>ネガをそのままプリント紙に密着させて焼く (大判のみ実用)</td></tr>
  <tr><td><strong>Contrast</strong></td><td>明部と暗部の濃度差。シーンと現像の両方を含む概念</td></tr>
</table>
`,
    },
    {
      heading: 'D - I',
      body: `
<table class="article-table">
  <tr><th>用語</th><th>意味</th></tr>
  <tr><td><strong>Densitometer</strong></td><td>濃度計。ネガ・プリントの透過率/反射率を数値化</td></tr>
  <tr><td><strong>Density (D)</strong></td><td>濃度。-log10(透過率)。0 = 透明、3.0 = 1/1000</td></tr>
  <tr><td><strong>Developer</strong></td><td>現像液 (D-76, HC-110, XTOL, Pyro 系等)</td></tr>
  <tr><td><strong>Dodging</strong></td><td>プリント時に部分的に光を遮って明るく残す (覆い焼き)</td></tr>
  <tr><td><strong>Dry Down</strong></td><td>プリントが乾燥するとわずかに暗くなる現象 (5-10%)</td></tr>
  <tr><td><strong>EI (Exposure Index)</strong></td><td>実効感度。ISO とは別に、自分のテストで決めた感度</td></tr>
  <tr><td><strong>Equivalents</strong></td><td>Stieglitz が提唱、White が継承。被写体ではなく内面を表現する写真</td></tr>
  <tr><td><strong>ETTR</strong></td><td>Expose To The Right。デジタルでハイライトギリギリまで露光</td></tr>
  <tr><td><strong>EV (Exposure Value)</strong></td><td>露出値。EV = log2(N²/t) - log2(ISO/100)</td></tr>
  <tr><td><strong>Expansion</strong></td><td>拡張現像 (N+)、低 DR シーンを現像でコントラスト増加</td></tr>
  <tr><td><strong>FB (Fiber Based)</strong></td><td>ファイバー印画紙、最高品質、長期保存可</td></tr>
  <tr><td><strong>Fall on</strong></td><td>「落ちる」、配置を決めた結果他の部分が落ち着くゾーン</td></tr>
  <tr><td><strong>Fixer</strong></td><td>定着液 (チオ硫酸ナトリウム、Rapid Fixer 等)</td></tr>
  <tr><td><strong>Hypoclear</strong></td><td>ハイポクリアリングエージェント、水洗時間短縮</td></tr>
</table>
`,
    },
    {
      heading: 'L - R',
      body: `
<table class="article-table">
  <tr><th>用語</th><th>意味</th></tr>
  <tr><td><strong>Latitude</strong></td><td>ラチチュード、フィルムが許容する露出のずれ範囲</td></tr>
  <tr><td><strong>Local Contrast</strong></td><td>画面内の特定領域だけのコントラスト</td></tr>
  <tr><td><strong>Massive Dev Chart</strong></td><td>世界最大のフィルム × 現像液 × 時間データベース (digitaltruth.com)</td></tr>
  <tr><td><strong>N (Normal)</strong></td><td>標準現像、ハイライト VIII を出す時間</td></tr>
  <tr><td><strong>N+</strong></td><td>拡張現像、ハイライトを上げる (N+1 ≒ ×1.4)</td></tr>
  <tr><td><strong>N−</strong></td><td>圧縮現像、ハイライトを抑える (N−1 ≒ ×0.7)</td></tr>
  <tr><td><strong>Panchromatic</strong></td><td>パンクロ、可視光全域に感度を持つフィルム</td></tr>
  <tr><td><strong>Place on</strong></td><td>「配置する」、シーンの一部を意図したゾーンに置く</td></tr>
  <tr><td><strong>Pre-visualization</strong></td><td>撮影前に最終プリントを思い描くこと (= Visualization)</td></tr>
  <tr><td><strong>Pyro</strong></td><td>ピロガロール系現像液、ステイン (染色) を生成</td></tr>
  <tr><td><strong>RC (Resin Coated)</strong></td><td>レジンコート印画紙、即乾、保存性は FB に劣る</td></tr>
  <tr><td><strong>Reciprocity Failure</strong></td><td>相反則不軌、長時間露光時の感度低下</td></tr>
  <tr><td><strong>Reducer</strong></td><td>減力液、ネガ濃度を化学的に下げる</td></tr>
</table>
`,
    },
    {
      heading: 'S - Z',
      body: `
<table class="article-table">
  <tr><th>用語</th><th>意味</th></tr>
  <tr><td><strong>Selenium Toning</strong></td><td>セレン調色、保存性向上 + 色味変化</td></tr>
  <tr><td><strong>Sheet Film</strong></td><td>シートフィルム、4×5 / 5×7 / 8×10 等の 1 枚撮り</td></tr>
  <tr><td><strong>Silver Halide</strong></td><td>銀ハロゲン化物、フィルムの感光主成分</td></tr>
  <tr><td><strong>Spot Meter</strong></td><td>1°視野の反射光式露出計、ZS 必須</td></tr>
  <tr><td><strong>Split Grade Printing</strong></td><td>VC 印画紙で Grade 0 と 5 を組合せて焼く現代的技法</td></tr>
  <tr><td><strong>Stain</strong></td><td>ステイン、Pyro 系現像液が生成する染色</td></tr>
  <tr><td><strong>Stop Bath</strong></td><td>停止液、現像反応を止める酸性浴</td></tr>
  <tr><td><strong>Subject Brightness Range</strong></td><td>被写体輝度範囲、シーンの DR</td></tr>
  <tr><td><strong>Tanning Developer</strong></td><td>タンニング現像液、ゼラチンを硬化させる (Pyro 系)</td></tr>
  <tr><td><strong>T-Grain</strong></td><td>T 字型結晶のフィルム (T-Max, Delta)、微粒子・高解像</td></tr>
  <tr><td><strong>Test Strip</strong></td><td>テストストリップ、段階露光の試焼</td></tr>
  <tr><td><strong>Tonality</strong></td><td>調子、プリントの明度の連続性</td></tr>
  <tr><td><strong>Tone Curve</strong></td><td>トーンカーブ、入力 vs 出力の濃度関係</td></tr>
  <tr><td><strong>VC (Variable Contrast)</strong></td><td>マルチグレード印画紙、フィルターで 0-5 号を可変</td></tr>
  <tr><td><strong>Visualization</strong></td><td>視覚化、撮影前に最終プリントを完成させる思考</td></tr>
  <tr><td><strong>Wash Aid</strong></td><td>= Hypoclear、水洗助剤</td></tr>
  <tr><td><strong>Water Bath</strong></td><td>水浴現像、現像中に純水浴に移してハイライトを抑える伝統技法</td></tr>
  <tr><td><strong>Weston, Edward</strong></td><td>1886-1958。アダムスの盟友、Pre-visualization 共同開発者</td></tr>
  <tr><td><strong>White, Minor</strong></td><td>1908-1976。ZS 第二世代、Aperture 誌創刊</td></tr>
  <tr><td><strong>Zone</strong></td><td>ゾーン。ZS の基本単位、各 1 段差の 11 段階</td></tr>
  <tr><td><strong>Zone Dial</strong></td><td>Pentax Spotmeter V 用のゾーンスケールシール</td></tr>
</table>
`,
    },
  ],
});

// ============================================================
// 28. トラブルシューティング辞典
// ============================================================
ARTICLES.push({
  id: 'troubleshooting',
  chapter: '28',
  title: 'トラブルシューティング辞典',
  summary: '撮影・現像・プリントで起きる典型的な失敗と、その原因 / 対処を症状別に整理。',
  sections: [
    {
      heading: '撮影の失敗',
      body: `
<div style="display:grid; grid-template-columns: repeat(2, 1fr); gap:8px; margin-bottom:14px;">
  <figure class="article-figure" style="margin:0;">
    <img src="/learn-images/28-trouble-shadow-loss.png" alt="黒つぶれ" loading="lazy" />
    <figcaption>黒つぶれ — 露光不足、シャドウディテール消失</figcaption>
  </figure>
  <figure class="article-figure" style="margin:0;">
    <img src="/learn-images/28-trouble-highlight-blown.png" alt="白とび" loading="lazy" />
    <figcaption>白とび — 露光過多、ハイライト消失</figcaption>
  </figure>
  <figure class="article-figure" style="margin:0;">
    <img src="/learn-images/28-trouble-blurry.png" alt="ブレ" loading="lazy" />
    <figcaption>手ブレ・被写体ブレ — SS 不足</figcaption>
  </figure>
  <figure class="article-figure" style="margin:0;">
    <img src="/learn-images/28-trouble-high-contrast.png" alt="コントラスト過多" loading="lazy" />
    <figcaption>コントラスト過多 — 現像過剰</figcaption>
  </figure>
</div>
<table class="article-table">
  <tr><th>症状</th><th>原因</th><th>対処</th></tr>
  <tr><td>ピンボケ</td><td>ピント合わせ甘い、被写体ブレ、手ブレ</td><td>三脚、ケーブルレリーズ、ミラーアップ</td></tr>
  <tr><td>露光不足</td><td>EI 過大、フィルター補正忘れ</td><td>EI を 1/3-1 段下げる、フィルター係数を Zone Notes に記録</td></tr>
  <tr><td>露光過剰</td><td>EI 過小、シャッター速度誤読</td><td>EI を 1/3 段上げる、機械式 SS は精度確認</td></tr>
  <tr><td>シャドウに調子なし</td><td>シャドウをゾーン I-II に置いた</td><td>シャドウは III に配置を厳守</td></tr>
  <tr><td>ハイライトが飛ぶ</td><td>ゾーン X 超え、現像方針誤り</td><td>N− 圧縮現像、または事前に Burn 計画</td></tr>
  <tr><td>画面端が暗い (周辺光量低下)</td><td>レンズ後玉ケラレ、フィルター枠の干渉</td><td>レンズフード適切化、フィルターサイズ確認</td></tr>
  <tr><td>フィルムが入らない</td><td>シートホルダー差し込み不全</td><td>ロック確認、ホルダーの清掃</td></tr>
</table>
`,
    },
    {
      heading: '現像の失敗',
      body: `
<table class="article-table">
  <tr><th>症状</th><th>原因</th><th>対処</th></tr>
  <tr><td>ネガ全体が薄い</td><td>露光不足 or 現像不足 or 古い現像液</td><td>EI 確認、N+1 で再現像はできない (露光のやり直し必須)</td></tr>
  <tr><td>ネガ全体が濃い</td><td>露光過多 or 現像過多 or 古い定着液</td><td>露出計校正、現像時間短縮、定着液交換</td></tr>
  <tr><td>ハイライトのみ硬い</td><td>現像過剰</td><td>N−1 (時間 ×0.7) を試す</td></tr>
  <tr><td>シャドウのみ薄い</td><td>露光不足、シャドウ感度低い現像液</td><td>EI 下げる、HC-110 や DD-X など補償系に変更</td></tr>
  <tr><td>筋ムラ (ストリーキング)</td><td>攪拌不足、攪拌過剰、現像液量不足</td><td>攪拌プロトコル統一、最低 250ml/シート</td></tr>
  <tr><td>ピンホール (黒点)</td><td>停止液濃すぎ、温度差大</td><td>停止液 1.5% に薄める、薬液温度を 20°C で揃える</td></tr>
  <tr><td>気泡跡 (透明な丸い斑点)</td><td>初期攪拌不足</td><td>最初の 30 秒は連続攪拌</td></tr>
  <tr><td>カブリ (ベース濃度上昇)</td><td>セーフライト漏れ、現像液汚染、フィルム古い</td><td>暗室遮光確認、現像液廃棄、フィルム期限確認</td></tr>
  <tr><td>定着不足 (黄色い汚れ)</td><td>定着時間短い、古い定着液</td><td>2 浴定着、5 分 + 5 分</td></tr>
</table>
`,
    },
    {
      heading: 'プリントの失敗',
      body: `
<table class="article-table">
  <tr><th>症状</th><th>原因</th><th>対処</th></tr>
  <tr><td>プリント全体が暗い</td><td>露光時間長い or ネガ薄い</td><td>テストストリップで時間再決定、ネガが薄ければ Grade 上げる</td></tr>
  <tr><td>プリント全体が灰色</td><td>露光不足、印画紙古い、現像液弱い</td><td>露光時間延長、印画紙交換、Dektol 1:2 に新調</td></tr>
  <tr><td>コントラスト不足</td><td>Grade 低すぎ、ネガが N+ 必要</td><td>Grade 3-4 に上げる</td></tr>
  <tr><td>コントラスト過多</td><td>Grade 高すぎ、ネガが N− だった</td><td>Grade 1-2 に下げる、スプリット推奨</td></tr>
  <tr><td>白い斑点</td><td>ネガに付いたホコリ、引伸機内ホコリ</td><td>ネガクリーニング、エアブロー、スポッティング</td></tr>
  <tr><td>黒い斑点</td><td>印画紙にホコリ付着、暗室の埃</td><td>暗室の清潔維持、印画紙保管見直し</td></tr>
  <tr><td>四隅が暗い</td><td>引伸機ヘッドのケラレ、アライメント不良</td><td>レンズ・ネガホルダー・印画紙面の平行確認</td></tr>
  <tr><td>プリントが時間と共に変色 (黄ばみ)</td><td>定着・水洗不足</td><td>2 浴定着、ハイポクリア、本水洗 60 分</td></tr>
  <tr><td>湿った状態で見ると違うプリント</td><td>Dry Down 効果 (5-10% 暗くなる)</td><td>テストプリントを完全乾燥させてから判定</td></tr>
</table>
`,
    },
    {
      heading: 'システム的な失敗',
      body: `
<table class="article-table">
  <tr><th>症状</th><th>原因</th><th>対処</th></tr>
  <tr><td>毎回露出が安定しない</td><td>EI が定まっていない、機材複数を併用</td><td>パーソナルテスト実施、機材を 1 つに固定</td></tr>
  <tr><td>シーンの DR を読み違える</td><td>スポット測光不慣れ、目視予測がズレる</td><td>第 1-2 週の観察訓練 (90 日プログラム)</td></tr>
  <tr><td>N+ / N− を使うべきか毎回迷う</td><td>標準値の判断基準が曖昧</td><td>「シャドウ III, ハイライト VIII」を絶対基準にする</td></tr>
  <tr><td>プリントの「調子」がイメージと違う</td><td>Visualization の精度不足</td><td>撮影前にプリントを言語化して Zone Notes に記録</td></tr>
  <tr><td>同じネガから 2 度同じプリントが焼けない</td><td>露光時間・Dodge/Burn を記録していない</td><td>Zone Notes printing フェーズに完全記録</td></tr>
  <tr><td>フィルムを変えると感覚が崩れる</td><td>フィルムごとの特性把握不足</td><td>1 種類で 100 カット撮ってから次を試す</td></tr>
</table>
`,
    },
    {
      heading: '機材の経年劣化',
      body: `
<table class="article-table">
  <tr><th>症状</th><th>原因</th><th>対処</th></tr>
  <tr><td>機械式シャッターの精度低下</td><td>注油切れ、摩耗</td><td>5-10 年に 1 回のオーバーホール</td></tr>
  <tr><td>露出計のばらつき</td><td>受光素子劣化、電池接点</td><td>校正サービス、グレーカード基準で自己校正</td></tr>
  <tr><td>レンズの内部曇り (ハーズ)</td><td>湿気、保管不良</td><td>除湿剤と乾燥剤、修理は専門業者</td></tr>
  <tr><td>引伸レンズのコーティング劣化</td><td>長年の使用</td><td>Apo-Componon-S 等の現代品に更新</td></tr>
</table>
`,
    },
  ],
});

// ============================================================
// 29. 写真家の系譜
// ============================================================
ARTICLES.push({
  id: 'photographer-lineage',
  chapter: '29',
  title: '写真家の系譜',
  summary: 'アンセル・アダムスを中心に、影響を与えた人物・影響を受けた人物の関係性を可視化。19世紀末からモダニズム、現代までの流れ。',
  sections: [
    {
      heading: 'Adams を中心とした影響関係',
      body: `
<svg class="article-illust" viewBox="0 0 460 360" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">PHOTOGRAPHER LINEAGE (with Ansel Adams as anchor)</text>

    <g transform="translate(60,40)">
      <rect x="0" y="0" width="120" height="30" fill="#cfcfcf" stroke="#0a0a0a"/>
      <text x="60" y="14" text-anchor="middle" font-size="9" font-weight="500">Alfred Stieglitz</text>
      <text x="60" y="26" text-anchor="middle" font-size="8" fill="#5a5a58">1864-1946 / Equivalents</text>
    </g>

    <g transform="translate(280,40)">
      <rect x="0" y="0" width="120" height="30" fill="#cfcfcf" stroke="#0a0a0a"/>
      <text x="60" y="14" text-anchor="middle" font-size="9" font-weight="500">Paul Strand</text>
      <text x="60" y="26" text-anchor="middle" font-size="8" fill="#5a5a58">1890-1976 / Straight</text>
    </g>

    <path d="M120 70 L160 130" stroke="#0a0a0a" stroke-width="1" stroke-dasharray="2 2"/>
    <path d="M340 70 L200 130" stroke="#0a0a0a" stroke-width="1" stroke-dasharray="2 2"/>

    <g transform="translate(160,130)">
      <rect x="0" y="0" width="140" height="36" fill="#0a0a0a" stroke="#0a0a0a"/>
      <text x="70" y="16" text-anchor="middle" font-size="11" font-weight="500" fill="#ffffff">Ansel Adams</text>
      <text x="70" y="30" text-anchor="middle" font-size="9" fill="#cfcfcf">1902-1984 / Zone System</text>
    </g>

    <g transform="translate(20,130)">
      <rect x="0" y="0" width="130" height="30" fill="#9a9a9a" stroke="#0a0a0a"/>
      <text x="65" y="14" text-anchor="middle" font-size="9" font-weight="500" fill="#ffffff">Edward Weston</text>
      <text x="65" y="26" text-anchor="middle" font-size="8" fill="#cfcfcf">1886-1958 / f/64</text>
    </g>

    <g transform="translate(310,130)">
      <rect x="0" y="0" width="130" height="30" fill="#9a9a9a" stroke="#0a0a0a"/>
      <text x="65" y="14" text-anchor="middle" font-size="9" font-weight="500" fill="#ffffff">Fred Archer</text>
      <text x="65" y="26" text-anchor="middle" font-size="8" fill="#cfcfcf">co-developer of ZS</text>
    </g>

    <path d="M150 150 L160 150" stroke="#0a0a0a" stroke-width="2"/>
    <path d="M300 150 L310 150" stroke="#0a0a0a" stroke-width="2"/>

    <path d="M230 165 L230 200" stroke="#0a0a0a" stroke-width="1"/>

    <g transform="translate(20,210)">
      <rect x="0" y="0" width="130" height="30" fill="#9a9a9a" stroke="#0a0a0a"/>
      <text x="65" y="14" text-anchor="middle" font-size="9" font-weight="500" fill="#ffffff">Minor White</text>
      <text x="65" y="26" text-anchor="middle" font-size="8" fill="#cfcfcf">1908-1976 / Aperture</text>
    </g>

    <g transform="translate(160,210)">
      <rect x="0" y="0" width="140" height="30" fill="#9a9a9a" stroke="#0a0a0a"/>
      <text x="70" y="14" text-anchor="middle" font-size="9" font-weight="500" fill="#ffffff">Wynn Bullock</text>
      <text x="70" y="26" text-anchor="middle" font-size="8" fill="#cfcfcf">1902-1975 / Time-Space</text>
    </g>

    <g transform="translate(310,210)">
      <rect x="0" y="0" width="130" height="30" fill="#9a9a9a" stroke="#0a0a0a"/>
      <text x="65" y="14" text-anchor="middle" font-size="9" font-weight="500" fill="#ffffff">Brett Weston</text>
      <text x="65" y="26" text-anchor="middle" font-size="8" fill="#cfcfcf">1911-1993</text>
    </g>

    <path d="M85 240 L85 280" stroke="#0a0a0a" stroke-width="1" stroke-dasharray="2 2"/>
    <path d="M230 240 L230 280" stroke="#0a0a0a" stroke-width="1" stroke-dasharray="2 2"/>
    <path d="M375 240 L375 280" stroke="#0a0a0a" stroke-width="1" stroke-dasharray="2 2"/>

    <g transform="translate(20,285)">
      <rect x="0" y="0" width="130" height="30" fill="#444444" stroke="#0a0a0a"/>
      <text x="65" y="14" text-anchor="middle" font-size="9" font-weight="500" fill="#ffffff">Michael Kenna</text>
      <text x="65" y="26" text-anchor="middle" font-size="8" fill="#cfcfcf">1953- / long exposure</text>
    </g>

    <g transform="translate(160,285)">
      <rect x="0" y="0" width="140" height="30" fill="#444444" stroke="#0a0a0a"/>
      <text x="70" y="14" text-anchor="middle" font-size="9" font-weight="500" fill="#ffffff">Sebastião Salgado</text>
      <text x="70" y="26" text-anchor="middle" font-size="8" fill="#cfcfcf">1944- / Genesis</text>
    </g>

    <g transform="translate(310,285)">
      <rect x="0" y="0" width="130" height="30" fill="#444444" stroke="#0a0a0a"/>
      <text x="65" y="14" text-anchor="middle" font-size="9" font-weight="500" fill="#ffffff">Hiroshi Sugimoto</text>
      <text x="65" y="26" text-anchor="middle" font-size="8" fill="#cfcfcf">1948- / Seascapes</text>
    </g>

    <text x="10" y="345" font-size="9" fill="#5a5a58">━ 直接の協働 / 師弟 ┄ 影響</text>
  </g>
</svg>
<p>アンセル・アダムスは <strong>Stieglitz/Strand のストレートフォトグラフィー</strong>を受け継ぎ、<strong>Edward Weston との Group f/64 (1932)</strong> でモダニズム写真を確立した。Fred Archer と共に Zone System を体系化し、その後 <strong>Minor White、Wynn Bullock、Brett Weston</strong> といった第二世代に技術と哲学を継承。</p>
<p>現代では <strong>Michael Kenna</strong> が長時間露光と精密プリント、<strong>Sebastião Salgado</strong> が報道とアート融合、<strong>杉本博司</strong> が概念写真として ZS の遺産を異なる形で発展させている。</p>
`,
    },
    {
      heading: 'Group f/64 と西海岸モダニズム',
      body: `
<p>1932 年に Adams と Weston を中心に結成された Group f/64 は、当時の絵画的写真 (Pictorialism) に対抗するため、<strong>「鋭く焦点を合わせ、深い被写界深度で、現実をそのまま記録する」</strong>を掲げた。グループ名はレンズの絞り値 f/64 に由来。</p>
<ul>
  <li><strong>Ansel Adams</strong> — Yosemite、ZS</li>
  <li><strong>Edward Weston</strong> — Pepper、Nautilus、Dunes</li>
  <li><strong>Imogen Cunningham</strong> — 花のクローズアップ、ヌード</li>
  <li><strong>Willard Van Dyke</strong> — ドキュメンタリー要素</li>
  <li><strong>Sonya Noskowiak</strong> — Weston の弟子、植物クローズアップ</li>
  <li><strong>Brett Weston</strong> — Edward Weston の息子</li>
</ul>
<p>これらの写真家の共通哲学が、後の Zone System の理論的土台となった。「写真は現実を変形するのではなく、見えるはずのものを精密に翻訳する」という思想。</p>
`,
    },
    {
      heading: '日本の系譜',
      body: `
<p>アメリカ西海岸の伝統とは別に、日本にも独自の写真伝統がある:</p>
<ul>
  <li><strong>木村伊兵衛</strong> (1901-1974) — スナップショット、ライカ判の日常記録</li>
  <li><strong>土門拳</strong> (1909-1990) — リアリズム、社会写真、寺院写真</li>
  <li><strong>植田正治</strong> (1913-2000) — 演出写真、植田調</li>
  <li><strong>東松照明</strong> (1930-2012) — 戦後写真、原爆ドーム</li>
  <li><strong>森山大道</strong> (1938-) — アレ・ブレ・ボケ、新宿</li>
  <li><strong>中平卓馬</strong> (1938-2015) — Provoke、写真論</li>
  <li><strong>荒木経惟</strong> (1940-) — 私写真、Sentimental Journey</li>
  <li><strong>杉本博司</strong> (1948-) — 概念写真、Theaters、Seascapes</li>
</ul>
<p>日本派は ZS の体系性とは異なる路線 (情緒、即興、私性) で発展したが、特に植田と杉本は <strong>緻密な階調表現</strong>で ZS と通底する。</p>
`,
    },
  ],
});

// ============================================================
// 30. フィルム/カメラ進化年表
// ============================================================
ARTICLES.push({
  id: 'film-camera-history',
  chapter: '30',
  title: 'フィルム/カメラ進化年表',
  summary: '1839 ダゲレオタイプから現代まで、写真技術と機材の主要な進化を概観。Zone System がいつ、どの技術的状況下で生まれたかが見える。',
  sections: [
    {
      heading: '主要な技術革新の流れ',
      body: `
<svg class="article-illust" viewBox="0 0 460 380" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="9" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">PHOTOGRAPHY TIMELINE</text>
    <line x1="80" y1="40" x2="80" y2="360" stroke="#0a0a0a" stroke-width="1.4"/>

    <g transform="translate(0,45)"><circle cx="80" cy="0" r="4" fill="#0a0a0a"/><text x="40" y="4" text-anchor="end" font-size="9">1839</text><text x="90" y="-2" font-size="10" font-weight="500">Daguerreotype</text><text x="90" y="10" font-size="8" fill="#5a5a58">最初の実用化、銀板写真</text></g>
    <g transform="translate(0,75)"><circle cx="80" cy="0" r="4" fill="#0a0a0a"/><text x="40" y="4" text-anchor="end" font-size="9">1851</text><text x="90" y="-2" font-size="10" font-weight="500">Collodion (Wet Plate)</text><text x="90" y="10" font-size="8" fill="#5a5a58">湿板、即時露光、現像</text></g>
    <g transform="translate(0,105)"><circle cx="80" cy="0" r="4" fill="#0a0a0a"/><text x="40" y="4" text-anchor="end" font-size="9">1871</text><text x="90" y="-2" font-size="10" font-weight="500">Gelatin Dry Plate</text><text x="90" y="10" font-size="8" fill="#5a5a58">乾板、利便性向上</text></g>
    <g transform="translate(0,135)"><circle cx="80" cy="0" r="4" fill="#0a0a0a"/><text x="40" y="4" text-anchor="end" font-size="9">1888</text><text x="90" y="-2" font-size="10" font-weight="500">Kodak No.1</text><text x="90" y="10" font-size="8" fill="#5a5a58">「You press the button」</text></g>
    <g transform="translate(0,165)"><circle cx="80" cy="0" r="4" fill="#0a0a0a"/><text x="40" y="4" text-anchor="end" font-size="9">1925</text><text x="90" y="-2" font-size="10" font-weight="500">Leica I (35mm)</text><text x="90" y="10" font-size="8" fill="#5a5a58">小型化、報道写真の幕開け</text></g>
    <g transform="translate(0,195)"><circle cx="80" cy="0" r="4" fill="#0a0a0a"/><text x="40" y="4" text-anchor="end" font-size="9">1932</text><text x="90" y="-2" font-size="10" font-weight="500" fill="#0a0a0a">Group f/64 結成</text><text x="90" y="10" font-size="8" fill="#5a5a58">Adams + Weston</text></g>
    <g transform="translate(0,225)"><circle cx="80" cy="0" r="4" fill="#0a0a0a" stroke="#0a0a0a" stroke-width="2"/><rect x="76" y="-4" width="8" height="8" fill="#0a0a0a"/><text x="40" y="4" text-anchor="end" font-size="9" font-weight="500">1939-40</text><text x="90" y="-2" font-size="10" font-weight="500" fill="#0a0a0a">Zone System 体系化</text><text x="90" y="10" font-size="8" fill="#5a5a58">Adams + Archer, Art Center School</text></g>
    <g transform="translate(0,255)"><circle cx="80" cy="0" r="4" fill="#0a0a0a"/><text x="40" y="4" text-anchor="end" font-size="9">1948</text><text x="90" y="-2" font-size="10" font-weight="500">Polaroid Land</text><text x="90" y="10" font-size="8" fill="#5a5a58">インスタント写真</text></g>
    <g transform="translate(0,285)"><circle cx="80" cy="0" r="4" fill="#0a0a0a"/><text x="40" y="4" text-anchor="end" font-size="9">1959</text><text x="90" y="-2" font-size="10" font-weight="500">Nikon F</text><text x="90" y="10" font-size="8" fill="#5a5a58">本格 35mm 一眼レフ</text></g>
    <g transform="translate(0,315)"><circle cx="80" cy="0" r="4" fill="#0a0a0a"/><text x="40" y="4" text-anchor="end" font-size="9">1969</text><text x="90" y="-2" font-size="10" font-weight="500">Pentax 67</text><text x="90" y="10" font-size="8" fill="#5a5a58">中判 6×7 普及機</text></g>
    <g transform="translate(0,345)"><circle cx="80" cy="0" r="4" fill="#0a0a0a"/><text x="40" y="4" text-anchor="end" font-size="9">1981-83</text><text x="90" y="-2" font-size="10" font-weight="500">Adams 三部作刊行</text><text x="90" y="10" font-size="8" fill="#5a5a58">Camera/Negative/Print</text></g>
  </g>
</svg>
<p>Zone System が体系化された 1939-40 年は、技術的に <strong>「乾板から35mm まで多様なフォーマットが共存、シートフィルム文化が成熟」</strong>した時代だった。アダムスの三部作 (1981-83) は、彼の生涯の集大成として公刊された。</p>
`,
    },
    {
      heading: 'フィルム感度の進化',
      body: `
<table class="article-table">
  <tr><th>年</th><th>製品</th><th>ISO</th><th>意義</th></tr>
  <tr><td>1925</td><td>Eastman Super Speed Pan</td><td>20</td><td>初期パンクロ、暗所撮影可能に</td></tr>
  <tr><td>1934</td><td>Kodak Super-X</td><td>32-40</td><td>感度向上、報道で頻用</td></tr>
  <tr><td>1954</td><td>Tri-X</td><td>200 → 400 (1960)</td><td>戦後の標準、ZS 実践で広く使われる</td></tr>
  <tr><td>1962</td><td>Plus-X</td><td>125</td><td>標準感度、微粒子</td></tr>
  <tr><td>1986</td><td>T-Max 100/400</td><td>100/400</td><td>T-grain 結晶、新世代</td></tr>
  <tr><td>1992</td><td>T-Max P3200</td><td>3200</td><td>超高感度</td></tr>
  <tr><td>2000</td><td>Acros (初代)</td><td>100</td><td>長時間露光に強い</td></tr>
  <tr><td>2019</td><td>Acros II</td><td>100</td><td>復活、デジタル時代の銘品</td></tr>
</table>
`,
    },
    {
      heading: 'デジタルとの並走',
      body: `
<p>1990 年代後半からデジタル写真が急速に普及した:</p>
<ul>
  <li><strong>1991</strong> Kodak DCS 100 — 最初のデジタル一眼 (130 万画素)</li>
  <li><strong>1999</strong> Nikon D1 — プロ向けデジタル一眼の標準確立</li>
  <li><strong>2002</strong> Canon EOS-1Ds — 35mm 相当 1110 万画素</li>
  <li><strong>2009</strong> RED ONE / Phase One IQ 80 MP — 中判デジタル普及</li>
  <li><strong>2012</strong> Sony A7 シリーズ — フルサイズミラーレス革命</li>
  <li><strong>現在</strong> Fujifilm GFX 100s, Hasselblad X2D — 1 億画素中判デジタル</li>
</ul>
<p>フィルムは 2010 年前後に底を打ち、その後 <strong>「アート/教育用途として復活」</strong>。Zone System は今も学校教育、コマーシャル、ファインアートで現役。</p>
`,
    },
  ],
});

// ============================================================
// 31. ZS と関連理論
// ============================================================
ARTICLES.push({
  id: 'related-theories',
  chapter: '31',
  title: 'ZS と関連理論',
  summary: 'Sensitometry (感光度測定)、Hurter-Driffield 曲線、Latitude theory、Densitometry — ZS の理論的基礎となった学問領域を概観。',
  sections: [
    {
      heading: '体系の地図',
      body: `
<svg class="article-illust" viewBox="0 0 460 320" xmlns="http://www.w3.org/2000/svg">
  <g font-family="Inter Tight, sans-serif" font-size="10" fill="#0a0a0a">
    <text x="10" y="14" font-size="9" letter-spacing="0.18em" fill="#5a5a58">RELATED THEORIES AROUND ZS</text>

    <g transform="translate(170,30)">
      <rect x="0" y="0" width="120" height="40" fill="#0a0a0a" stroke="#0a0a0a"/>
      <text x="60" y="18" text-anchor="middle" font-size="11" font-weight="500" fill="#ffffff">Zone System</text>
      <text x="60" y="32" text-anchor="middle" font-size="9" fill="#cfcfcf">Adams &amp; Archer 1940</text>
    </g>

    <g transform="translate(20,110)">
      <rect x="0" y="0" width="140" height="36" fill="#cfcfcf" stroke="#0a0a0a"/>
      <text x="70" y="14" text-anchor="middle" font-size="10" font-weight="500">Sensitometry</text>
      <text x="70" y="28" text-anchor="middle" font-size="8" fill="#5a5a58">感光度測定 / Driffield 1890</text>
    </g>

    <g transform="translate(170,110)">
      <rect x="0" y="0" width="120" height="36" fill="#cfcfcf" stroke="#0a0a0a"/>
      <text x="60" y="14" text-anchor="middle" font-size="10" font-weight="500">H-D Curve</text>
      <text x="60" y="28" text-anchor="middle" font-size="8" fill="#5a5a58">Hurter-Driffield 1890</text>
    </g>

    <g transform="translate(300,110)">
      <rect x="0" y="0" width="140" height="36" fill="#cfcfcf" stroke="#0a0a0a"/>
      <text x="70" y="14" text-anchor="middle" font-size="10" font-weight="500">Densitometry</text>
      <text x="70" y="28" text-anchor="middle" font-size="8" fill="#5a5a58">濃度測定 / Goldberg 1920s</text>
    </g>

    <g transform="translate(20,180)">
      <rect x="0" y="0" width="140" height="36" fill="#9a9a9a" stroke="#0a0a0a"/>
      <text x="70" y="14" text-anchor="middle" font-size="10" font-weight="500" fill="#ffffff">Latitude Theory</text>
      <text x="70" y="28" text-anchor="middle" font-size="8" fill="#cfcfcf">露出ラチチュード概念</text>
    </g>

    <g transform="translate(170,180)">
      <rect x="0" y="0" width="120" height="36" fill="#9a9a9a" stroke="#0a0a0a"/>
      <text x="60" y="14" text-anchor="middle" font-size="10" font-weight="500" fill="#ffffff">Pre-visualization</text>
      <text x="60" y="28" text-anchor="middle" font-size="8" fill="#cfcfcf">Stieglitz, Weston</text>
    </g>

    <g transform="translate(300,180)">
      <rect x="0" y="0" width="140" height="36" fill="#9a9a9a" stroke="#0a0a0a"/>
      <text x="70" y="14" text-anchor="middle" font-size="10" font-weight="500" fill="#ffffff">Colorimetry</text>
      <text x="70" y="28" text-anchor="middle" font-size="8" fill="#cfcfcf">色彩計測、Munsell 系</text>
    </g>

    <g transform="translate(20,250)">
      <rect x="0" y="0" width="140" height="36" fill="#444444" stroke="#0a0a0a"/>
      <text x="70" y="14" text-anchor="middle" font-size="10" font-weight="500" fill="#ffffff">BTZS</text>
      <text x="70" y="28" text-anchor="middle" font-size="8" fill="#cfcfcf">Davis 1980s, ZS の精緻化</text>
    </g>

    <g transform="translate(170,250)">
      <rect x="0" y="0" width="120" height="36" fill="#444444" stroke="#0a0a0a"/>
      <text x="60" y="14" text-anchor="middle" font-size="10" font-weight="500" fill="#ffffff">Digital ETTR</text>
      <text x="60" y="28" text-anchor="middle" font-size="8" fill="#cfcfcf">RAW 現像の現代版 ZS</text>
    </g>

    <g transform="translate(300,250)">
      <rect x="0" y="0" width="140" height="36" fill="#444444" stroke="#0a0a0a"/>
      <text x="70" y="14" text-anchor="middle" font-size="10" font-weight="500" fill="#ffffff">HDR Theory</text>
      <text x="70" y="28" text-anchor="middle" font-size="8" fill="#cfcfcf">Debevec 1997, トーンマッピング</text>
    </g>

    <path d="M170 70 L90 110" stroke="#0a0a0a" stroke-width="1" stroke-dasharray="2 2"/>
    <path d="M230 70 L230 110" stroke="#0a0a0a" stroke-width="1" stroke-dasharray="2 2"/>
    <path d="M290 70 L370 110" stroke="#0a0a0a" stroke-width="1" stroke-dasharray="2 2"/>
    <path d="M230 146 L230 180" stroke="#0a0a0a" stroke-width="1" stroke-dasharray="2 2"/>
    <path d="M230 216 L230 250" stroke="#0a0a0a" stroke-width="1" stroke-dasharray="2 2"/>

    <text x="10" y="305" font-size="11" fill="#5a5a58">ZS は感光度測定 + 濃度測定の応用、Pre-visualization 哲学と組合さって完成した</text>
  </g>
</svg>
`,
    },
    {
      heading: 'Hurter-Driffield 曲線',
      body: `
<p>1890 年、Ferdinand Hurter と Vero Driffield は、写真フィルムの <strong>「露光量と現像後の濃度の関係」</strong>を初めて科学的に測定した。これが Sensitometry (感光度測定) の起源。</p>
<p>H-D 曲線は以下の構造を持つ:</p>
<ul>
  <li><strong>裾 (Toe)</strong> — 露光量小、濃度の立ち上がり緩 → ZS のゾーン 0-II 領域</li>
  <li><strong>直線部 (Straight Line)</strong> — 露光量と濃度が比例 → ZS のゾーン III-VIII 領域</li>
  <li><strong>肩 (Shoulder)</strong> — 露光量大、濃度の伸び止まる → ZS のゾーン IX-X 領域</li>
</ul>
<p>ZS が <strong>「シャドウは露出で決まり、ハイライトは現像で決まる」</strong>と教える根拠は、この H-D 曲線の物理的構造にある。アダムスは「写真の数学的基盤」として H-D 曲線を学び、それを実用的な操作言語に翻訳した。</p>
`,
    },
    {
      heading: 'BTZS (Beyond the Zone System)',
      body: `
<p>1981 年、Phil Davis が著書《Beyond the Zone System》で発表した、ZS の <strong>精緻化バージョン</strong>。</p>
<p>BTZS の特徴:</p>
<ul>
  <li><strong>濃度計による精密測定</strong> — 全てを数値化、感覚に依存しない</li>
  <li><strong>SBR (Subject Brightness Range)</strong> — シーン輝度範囲を絞り段数で精密表現</li>
  <li><strong>EFS (Effective Film Speed)</strong> — フィルムの実効感度を個別測定</li>
  <li><strong>BTZS チューブ現像</strong> — 1 枚ずつ最適現像できる物理的方法</li>
  <li><strong>Plotter プログラム</strong> — H-D 曲線を作成・解析するソフトウェア</li>
</ul>
<p>ZS が「感覚的言語 (シャドウ/ハイライト/N±)」だとすれば、BTZS は「測定言語 (SBR/EFS/DR)」。実践的な精度は BTZS の方が高いが、習得コストも高い。</p>
<p>多くの実践者は <strong>ZS で日常運用、BTZS で精密測定</strong>のハイブリッドで使う。</p>
`,
    },
    {
      heading: 'デジタル時代の翻訳',
      body: `
<table class="article-table">
  <tr><th>フィルム ZS</th><th>デジタル相当</th></tr>
  <tr><td>パーソナル EI テスト</td><td>センサー基準 ISO + RAW ヒストグラム解析</td></tr>
  <tr><td>N+ / N− 現像</td><td>RAW のトーンカーブ調整、Shadows/Highlights スライダー</td></tr>
  <tr><td>Pre-visualization</td><td>同上、加えてリアルタイムヒストグラム確認</td></tr>
  <tr><td>覆い焼き / 焼き込み</td><td>レンジマスク + Brush ツール</td></tr>
  <tr><td>スプリットグレード</td><td>HDR Merge + Tone Mapping</td></tr>
  <tr><td>ファインプリント</td><td>Soft Proofing + Inkjet 印刷</td></tr>
</table>
<p>ZS の <strong>思想 (シーンのゾーン分布を意図したゾーン分布に翻訳する)</strong> は、技術が変わっても通底する。デジタル時代でも、ZS 的訓練を受けた写真家は、後工程で迷わない判断ができる。</p>
`,
    },
  ],
});

export function getArticle(id) {
  return ARTICLES.find((a) => a.id === id) || null;
}
